import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Info, Edit, ArrowForwardIos, ChevronLeft, CheckCircle as CheckCircleIcon, Delete as DeleteIcon, Add as AddIcon, Warning as WarningIcon, Close as CloseIcon, InsertDriveFile as InsertDriveFileIcon } from "@material-ui/icons";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { fetchDSBatchDetails, requestQAApproval } from "../../../containers/actions/common/saslAction";
import moment from "moment";
import {DatePicker} from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import IconButton from "@material-ui/core/IconButton";
import Event from "@material-ui/icons/Event";
import { PDFViewer } from "../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import { appUrl } from "../../../../../utils/config";
import ProductDetailsLoader from "../sasl/batch/ProductDetailsLoader";
import { getFeaturesAccess } from "../../../containers/actions/common/accessControl";

// Styling
const useStyles = makeStyles((theme) => ({
  body : {
    overflow: "hidden",
  },
  // Shipment tabs (Post Shipment ASN multi-shipment view): MUI's <Tabs> defaults
  // textColor to "secondary", which applies theme.palette.secondary.main to the
  // selected tab via a class with higher specificity than a plain inline style in
  // some builds — !important here guarantees both tabs stay plain bold black,
  // regardless of theme color, with only the underline distinguishing the active one.
  shipmentTab: {
    color: "#000 !important",
    fontWeight: 700,
    textTransform: "none",
  },
  formLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: "150px",
    paddingRight: theme.spacing(2),
    textAlign: "right",
  },
  fixedLabel: {
    width: 180,
    textAlign: "left",
    paddingRight: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  requiredStar: {
    color: "red",
    marginLeft: 4,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  // BCM-902: the globally imported `.Mui-disabled svg { color: #b4b4b4 !important }` rule
  // (CMOConfigDetails.css / ConfigProductDetails.css) greys out every disabled icon and beats
  // inline styles, so the delete icon needs its own !important to stay red when disabled.
  deleteIconButton: {
    color: "red",
    "& svg": {
      color: "red !important",
    },
    // Disabled stays red so it reads as the same action, just faded back.
    "&.Mui-disabled": {
      color: "rgba(255, 0, 0, 0.4)",
    },
    "&.Mui-disabled svg": {
      color: "rgba(255, 0, 0, 0.4) !important",
    },
  },
  testTable: {
    '& .MuiTableCell-head': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
    },
  },
  FeildHistoryHorizantalBorder: {
    borderBottom: "1px solid grey"
  },
  closeText: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: 600,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    cursor: "pointer",
    padding: "0.3rem",
  },
}));

// Function to convert snake_case to Title Case, if cmo or bms word is present, then return the word in uppercase
const toTitleCase = (str) => {
  // Check the full string first for special cases
  const lowerStr = str.toLowerCase();
  if (lowerStr === "total_weight") {
    return "Produced Quantity";
  } else if (lowerStr === "quantity_produced") {
    return "Quantity";
  }
  
  // Then process word by word
  return str
    .split("_")
    .map((word) => {
      const lowerWord = word.toLowerCase();
      if (lowerWord === "cmo") {
        return "CMO";
      } else if (lowerWord === "bms") {
        return "BMS";
      } else if (lowerWord === "sponsor") {
        return "BMS";
      } else if (lowerWord === "uom") {
        return "UoM";
      } else if (lowerWord === "of") {
        return "of";
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    })
    .join(" ");
};

const convertToISOUTC = (dateInput) => {
  let date;

  if (
    dateInput === null ||
    dateInput === "N/A" ||
    dateInput === "n/a" ||
    dateInput === "NA"
  ) {
    return "";
  }

  try {
    // Handle different input types
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === "number") {
      // Unix timestamp (seconds or milliseconds)
      date = new Date(dateInput < 10000000000 ? dateInput * 1000 : dateInput);
    } else if (typeof dateInput === "string") {
      // Handle various string formats
      date = parseStringDate(dateInput);
    } else {
      return null; // Invalid input type
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }

    // For date-only fields, preserve the date without timezone conversion
    // Format as YYYY-MM-DD to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return null; // Any parsing error
  }
};

const parseStringDate = (dateString) => {
  // Clean the input
  const cleanInput = dateString.trim();

  // Check for obviously invalid patterns that should be rejected
  const invalidPatterns = [
    /[{}[\]]/, // Contains braces or brackets
    /[!@#$%^&*()+=<>?~`]/, // Contains special characters (excluding common date separators)
    /\d{5,}/, // Contains numbers with 5+ consecutive digits (except for years in valid positions)
  ];

  // Reject strings with invalid characters
  const hasInvalidPattern = invalidPatterns.some((pattern) =>
    pattern.test(cleanInput)
  );
  if (hasInvalidPattern) {
    // Exception: allow valid year formats even if they contain 4+ digits
    const validYearPattern =
      /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4}$|^\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2}$|^\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}$/i;
    if (!validYearPattern.test(cleanInput)) {
      throw new Error(`Invalid characters in date format: ${dateString}`);
    }
  }

  // Try native Date parsing first
  let date = new Date(cleanInput);
  if (!isNaN(date.getTime())) {
    return date;
  }

  // Handle DD/MM/YYYY vs MM/DD/YYYY ambiguity
  // Assume MM/DD/YYYY for US-style dates unless day > 12
  const slashMatch = cleanInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slashMatch) {
    const [, first, second, year] = slashMatch;
    const firstNum = parseInt(first);
    const secondNum = parseInt(second);

    // If first number > 12, assume DD/MM/YYYY
    if (firstNum > 12) {
      const reformatted = cleanInput.replace(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})/,
        `$2/$1/$3`
      );
      date = new Date(reformatted);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // Handle dot notation (European style DD.MM.YYYY)
  const dotMatch = cleanInput.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dotMatch) {
    const [, day, month, year] = dotMatch;
    const reformatted = cleanInput.replace(
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})/,
      `$2/$1/$3`
    );
    date = new Date(reformatted);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // If all else fails, throw an error (will be caught by try-catch in main function)
  throw new Error(`Unable to parse date format: ${dateString}`);
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Small clickable preview card for a sibling document. Used as the preview-pane
// fallback when the currently open document has no directly viewable file of its
// own (e.g. an IDoc-sourced XML like Production ASN / Post Shipment ASN) — instead
// of "No PDF available", the pane shows thumbnails of the other real PDFs filed
// under the same documentCategoryType, matching the spec's screenshots.
const DocumentThumbnail = ({ schrockenCustomFileID, customFileName, onClick }) => {
  const [thumbURL, setThumbURL] = useState(null);
  const [isPdf, setIsPdf] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    if (schrockenCustomFileID) {
      axios
        .post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/downloadCustomFile`,
          { schrockenCustomFileID }
        )
        .then((res) => {
          if (cancelled || !res.data?.status) return;
          const byteArray = new Uint8Array(res.data.fileData.data);
          const fname = res.data.fileName || customFileName || "";
          const pdf = fname.toLowerCase().endsWith(".pdf");
          setIsPdf(pdf);
          const blob = new Blob([byteArray], {
            type: pdf ? "application/pdf" : "application/octet-stream",
          });
          objectUrl = URL.createObjectURL(blob);
          setThumbURL(objectUrl);
        })
        .catch((err) => console.error("Error loading thumbnail preview:", err));
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [schrockenCustomFileID]);

  return (
    <Box
      onClick={onClick}
      style={{ cursor: "pointer", width: 140, display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Box
        style={{
          width: 120,
          height: 150,
          border: "1px solid #ddd",
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {thumbURL && isPdf ? (
          // Render the PDF viewer natively AT the thumbnail's own small size, instead of
          // rendering it full-size and shrinking with a CSS transform — most browsers'
          // built-in PDF viewer renders blank when its hosting iframe is scaled down via
          // CSS after the fact, but sizing the iframe itself small (and hiding the
          // toolbar/nav chrome via the viewer's own URL params) reliably shows a
          // miniature of the first page.
          <iframe
            src={`${thumbURL}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={customFileName}
            width="120"
            height="150"
            style={{ border: "none", pointerEvents: "none" }}
          />
        ) : thumbURL ? (
          <InsertDriveFileIcon style={{ fontSize: 48, color: "#bbb" }} />
        ) : (
          <Typography variant="body2" style={{ color: "#999" }}>Loading...</Typography>
        )}
      </Box>
      <Typography
        variant="body2"
        style={{ color: "#1890FF", textDecoration: "underline", marginTop: 6, textAlign: "center", wordBreak: "break-word" }}
      >
        {customFileName}
      </Typography>
    </Box>
  );
};

const CommonDigitizedDocument = (props) => {
  // State to track file type (pdf or excel)
  const [fileType, setFileType] = useState(null);
  // Which sibling document (by schrockenCustomFileID) is currently expanded to full
  // view in the preview pane's associated-documents thumbnail grid, if any.
  const [expandedSiblingFileID, setExpandedSiblingFileID] = useState(null);

  // Guards setState after unmount for the post-success delay in handleReviewAction
  // (Mark Review as Complete / Update & Re-Run) below.
  const isMountedRef = useRef(true);
  // Cancel tokens for the two re-triggerable reads below (validateFieldsForDigitizationUI
  // fires on mount and can be re-run; fetchFieldHistory fires per field-history-icon
  // click and can be re-clicked before the previous one resolves).
  const validateFieldsCancelRef = useRef(null);
  const fieldHistoryCancelRef = useRef(null);
  // Shared across the three explicit user-submit writes to handleDataExtractionEditedData
  // (Save field edit / Mark Review Complete / Update & Re-Run) — these are single-click
  // actions, not re-triggerable, so this only needs to cover cancel-on-unmount.
  const saveCancelRef = useRef(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (validateFieldsCancelRef.current) {
        validateFieldsCancelRef.current.cancel("leaving digitized document view");
      }
      if (fieldHistoryCancelRef.current) {
        fieldHistoryCancelRef.current.cancel("leaving digitized document view");
      }
      if (saveCancelRef.current) {
        saveCancelRef.current.cancel("leaving digitized document view");
      }
    };
  }, []);

  // Handler for file type changes from PDFViewer component
  const handleFileTypeChange = (type) => {
    setFileType(type);
  };
  const {
    initialData,
    onClose,
    handleCloseRef,
    schrockenCustomFileID: schrockenCustomFileIDProp,
    digitizedData,
    batchDetails,
    poNumber,
    batchNumber,
    cmoBatchNumber,
    onDataRefresh,
  } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const docType = digitizedData?.documentType || "Unknown";

  // Multi-shipment support: a batch can carry more than one concurrent record of the same
  // document_type — one per delivery/shipment (e.g. two "Post Shipment ASN" entries, each
  // tagged with its own deliveryNumber). When that's the case, render them as tabs. A
  // document type with zero or one matching record resolves to [] here, so every existing
  // single-shipment document renders exactly as before — schrockenCustomFileID below simply
  // falls back to the original prop unchanged.
  const documentShipments = useMemo(() => {
    const all = batchDetails?.digitizedExtractedData;
    if (!Array.isArray(all) || !docType || docType === "Unknown") return [];
    const matches = all.filter((d) => {
      if (!d?.deliveryNumber) return false; // only records tagged with a shipment identity
      const kvp = (d.kvpJsonEdited && Object.keys(d.kvpJsonEdited).length > 0)
        ? d.kvpJsonEdited
        : d.kvpJsonOriginal;
      return kvp?.document_type === docType;
    });
    // A document with a deliveryNumber is shown as its own (single) shipment tab even
    // when it's the only one — only truly hide the tab bar when there's no shipment
    // identity to show at all.
    if (matches.length === 0) return [];
    return matches.slice().sort((a, b) => (a.schrockenCustomFileID || 0) - (b.schrockenCustomFileID || 0));
  }, [batchDetails?.digitizedExtractedData, docType]);

  // Which shipment tab is active. Defaults to whichever shipment matches the originally
  // passed schrockenCustomFileID (so opening shipment 2 directly starts on its own tab),
  // falling back to the first shipment otherwise.
  const [activeShipmentIdx, setActiveShipmentIdx] = useState(() => {
    if (documentShipments.length === 0) return 0;
    const idx = documentShipments.findIndex((d) => d.schrockenCustomFileID === schrockenCustomFileIDProp);
    return idx >= 0 ? idx : 0;
  });

  // Every existing lookup below (category resolution, KVP field sync, PDF preview, download,
  // mark-review submission) reads the bare schrockenCustomFileID — shadowing it here with the
  // active shipment's own ID means all of that keeps working unchanged, but now reacts to the
  // tab selection whenever there's a shipment identity for this document type (one tab is
  // shown even for a single shipment). For any document with none at all, this is byte-for-byte
  // the original prop value.
  const schrockenCustomFileID = documentShipments.length > 0
    ? (documentShipments[activeShipmentIdx]?.schrockenCustomFileID ?? schrockenCustomFileIDProp)
    : schrockenCustomFileIDProp;

  // Non-null whenever there's at least one shipment for this document type — used to scope
  // the associated-documents preview and the mark-review submission to the specific shipment
  // currently active.
  const activeShipmentDeliveryNumber = documentShipments.length > 0
    ? documentShipments[activeShipmentIdx]?.deliveryNumber
    : null;

  // Shared lookup for the currently open document's own file entry and its
  // documentCategoryType (e.g. "Post-Manufacturing" / "Post-Shipment Documents") — reused
  // by the preview panel header, the preview panel content, and the batch-status note below
  // so they always agree on which category this document belongs to.
  const currentCustomFile = batchDetails?.customFilesList?.find(
    (f) => f.schrockenCustomFileID === schrockenCustomFileID?.toString()
  );
  const currentFileCategory = currentCustomFile?.documentCategoryType;
  // IDoc-sourced documents (e.g. Production ASN / Post Shipment ASN) have no PDF/XLSX file of
  // their own — same extension check used for the preview-panel fallback below. These must
  // always offer Mark Review Complete, so this flag lets that button bypass the field-count,
  // category-editability, and already-reviewed gates that still apply to PDF/Excel documents.
  const isIdocDocument = !!currentCustomFile?.customFileName &&
    !/\.(pdf|xlsx|xls)$/i.test(currentCustomFile.customFileName);

  // Business rule (Post-Shipment ASN only): Mark Review Complete additionally requires
  // every shipment's own Bill of Lading to actually be on file, and the batch to be
  // effectively fully dispatched — Remaining Qty within 1% of Produced Qty (threshold
  // hardcoded for now). Both flags default to true — i.e. never block — for every other
  // document type, so this can only ever ADD a gate here, never touch existing behavior
  // for Post-Manufacturing/Pre-Shipment/other Post-Shipment documents.
  const isPostShipmentAsnDoc = isIdocDocument && /post-shipment/i.test(currentFileCategory || "");
  const shipmentDeliveryNumbers = documentShipments.length > 0
    ? documentShipments.map((d) => d.deliveryNumber).filter(Boolean)
    : (activeShipmentDeliveryNumber ? [activeShipmentDeliveryNumber] : []);
  const allBillOfLadingReceived =
    !isPostShipmentAsnDoc ||
    shipmentDeliveryNumbers.length === 0 ||
    shipmentDeliveryNumbers.every((deliveryNum) => {
      // Cross-reference this delivery's own schrockenCustomFileID (via digitizedExtractedData,
      // the only place deliveryNumber lives) into customFilesList to check for a real,
      // non-deleted Bill of Lading filed under it.
      const shipmentFileID = (batchDetails?.digitizedExtractedData || [])
        .find((d) => d?.deliveryNumber === deliveryNum)?.schrockenCustomFileID;
      return (batchDetails?.customFilesList || []).some(
        (f) =>
          f.schrockenCustomFileID === String(shipmentFileID) &&
          f.customFileType === "Bill of Lading" &&
          !f.markedForDelete
      );
    });
  const producedQty = Number(batchDetails?.producedQuantity);
  const remainingQty = Number(batchDetails?.remainingQuantityToBeDispatched);
  const isEffectivelyFullyDispatched =
    !isPostShipmentAsnDoc ||
    !Number.isFinite(producedQty) || producedQty <= 0 ||
    !Number.isFinite(remainingQty) ||
    Math.abs(remainingQty) < producedQty * 0.01;

  // Normalizes a raw documentCategoryType into a label ending in "Documents" exactly once —
  // raw category values are inconsistent ("Post-Manufacturing" has no suffix, while
  // "Post-Shipment Documents" already has one), which previously produced "...documents documents".
  const formatCategoryLabel = (category) => {
    if (!category) return "";
    return /documents\s*$/i.test(category.trim()) ? category : `${category} Documents`;
  };

  // Reset the associated-documents thumbnail expansion whenever the open document changes.
  useEffect(() => {
    setExpandedSiblingFileID(null);
  }, [schrockenCustomFileID]);

  // Get screen access for fgBatch
  const screenAccess = useSelector((state) => {
    return state.pcmp.common.accessControl.screensAccess.data.fgBatch;
  });

  // Get features access for fgBatch
  const featuresAccess = useSelector((state) => {
    return state.pcmp.common.accessControl.featuresAccess.fgBatch;
  });

   // Call getFeaturesAccess when component mounts
   useEffect(() => {
    if (screenAccess?.screenAccess && !featuresAccess) {
      dispatch(getFeaturesAccess(screenAccess?.screenId, "fgBatch"));
    }
    // Mark as loaded when featuresAccess is available or when it's CMO (doesn't need it)
    if (featuresAccess || localStorage.collabType === "CMO") {
      setIsFeaturesAccessLoaded(true);
    }
  }, [dispatch, screenAccess, featuresAccess]);

  //console.log("here is the featuresAccess",featuresAccess);
  //console.log("here is the screenAccess",screenAccess);

  // const deliverySchedulesFeatures = useSelector(
  //    (state) =>
  //      state.pcmp.common.accessControl.featuresAccess?.deliverySchedulesFeatures
  // );
  // const canReviewAndEdit = !deliverySchedulesFeatures?.ReviewandEditFields;
  const canReviewAndEdit=()=>
  {
    if(localStorage.collabType !== "CMO")
    {
      //console.log("here is the featuresAccess",featuresAccess?.ReviewandEditFields);
      return featuresAccess?.ReviewandEditFields;
    }
    return false;
  }

  // BCM-564: Helper to call /validateFieldsForDigitizationUI and update apiData if Corda
  // returns an updated kvpJsonEdited or kvpJson (trigger 1: link click; trigger 3: Ok in success dialog).
  // Also handles auto-population: when material check passes and CMO config has
  // "Consumed Qty = On-hand Qty", Corda auto-fills actual_material_quantity_consumed
  // and returns the updated data in kvpJson (kvpJsonEdited may be empty in this case).
  // Only fires for consumption documents (documents that have bowls).
  const runValidateFieldsForDigitizationUI = async () => {
    if (!batchDetails?.schrockenDeliveryScheduleBatchID) return;
    // Skip page-level validate once the document has been marked as reviewed:
    // re-running it can overwrite the reviewed flag or reset user edits.
    if (apiData?.markAsReviewed === true) return;
    try {
      // BCM-654 (updated directive): always call the page-level validation API for
      // documents with a bowls structure — regardless of row confidence levels.
      // The full document (all top-level fields + bowls) is sent for validation.
      // (The previous all-rows-low-confidence skip was removed intentionally.)

      // BCM-654: Use apiData directly — it is always current (updated after every save).
      // Reading from batchDetails.digitizedExtractedData is stale when called from Trigger 3
      // (OK after success dialog) because Redux state is not refreshed before OK is clicked.
      // apiData holds the latest edited state, so send it as kvpJsonEdited when edits exist,
      // or kvpJson when viewing original unedited data.
      if (!apiData || Object.keys(apiData).length === 0) return;
      // BCM-654: OR the in-session edit flag so that right after a field save (before
      // batchDetails has refreshed) the edited apiData is still sent under kvpJsonEdited.
      const hasEdits = hasEditedData() || hasLocalEdits;

      const payload = {
        schrockenDeliveryScheduleBatchID: batchDetails.schrockenDeliveryScheduleBatchID,
        schrockenSLID: batchDetails?.schrockenSLID || null,
        documentType: docType,
        kvpJson:       hasEdits ? null : JSON.stringify(apiData),
        kvpJsonEdited: hasEdits ? JSON.stringify(apiData) : null,
        vendorMosymphonyID: batchDetails?.vendorMosymphonyID || null,
        batchNumber: batchDetails?.vendorBatchNumber || batchNumber || null,
        poNumber: batchDetails?.scheduleAgreementNumber || poNumber || null,
      };
      if (validateFieldsCancelRef.current) {
        validateFieldsCancelRef.current.cancel("superseded validateFieldsForDigitizationUI call");
      }
      const cancelSource = axios.CancelToken.source();
      validateFieldsCancelRef.current = cancelSource;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/validateFieldsForDigitizationUI`,
        payload,
        { timeout: 120000, cancelToken: cancelSource.token }
      );
      // Apply returned data to apiData so the UI shows auto-populated values (e.g. qty, material).
      // Priority: kvpJsonEdited (explicit save) > kvpJson (auto-population fallback).
      if (response.data) {
        // BCM-654: Corda can return errors in two shapes:
        //   1. response.data.validationErrors — array of objects with errorMessage/message
        //   2. response.data.errorMessage     — a plain string OR a JSON-stringified array string
        //      e.g. "[\"Multiple materials are associated...\"]"
        const parseErrorMessage = (raw) => {
          if (!raw) return [];
          if (typeof raw !== 'string') return [String(raw)];
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.filter(Boolean);
            return [raw];
          } catch {
            return [raw];
          }
        };

        let validationErrorMessages = [];
        if (response.data.validationErrors && response.data.validationErrors.length > 0) {
          validationErrorMessages = response.data.validationErrors
            .map(e => e.errorMessage || e.message || JSON.stringify(e))
            .filter(Boolean);
        } else if (response.data.errorMessage) {
          validationErrorMessages = parseErrorMessage(response.data.errorMessage);
        }
        setCordaValidationErrors(validationErrorMessages);

        // BCM-564: Update UI data when Corda auto-populated a field (autoPopulated=true).
        // BCM-654: Also update when Corda returned validation errors — Corda may have updated
        //   needValidation flags on fields (e.g. set needValidation=true for batch+material
        //   when multiple inventory records found), and the UI must reflect those changes.
        let updated = null;
        if (response.data.data?.kvpJsonEdited) {
          updated = response.data.data.kvpJsonEdited;
        } else if (response.data.kvpJsonEdited && response.data.kvpJsonEdited !== '') {
          updated = typeof response.data.kvpJsonEdited === 'string'
            ? JSON.parse(response.data.kvpJsonEdited)
            : response.data.kvpJsonEdited;
        } else if (response.data.kvpJson && response.data.kvpJson !== '') {
          updated = typeof response.data.kvpJson === 'string'
            ? JSON.parse(response.data.kvpJson)
            : response.data.kvpJson;
        }

        const hasAutoPopulated = (data) => {
          if (!data) return false;
          // Check top-level fields (e.g. date_of_manufacture) — these are NOT inside bowls
          for (const key of Object.keys(data)) {
            if (key === 'bowls' || key === 'pallets') continue;
            const field = data[key];
            if (field && typeof field === 'object' && !Array.isArray(field) && field.autoPopulated === true) return true;
          }
          // Check bowl row fields (e.g. actual_material_quantity_consumed)
          for (const bowl of (data.bowls || [])) {
            for (const row of (bowl.rows || [])) {
              for (const field of Object.values(row)) {
                if (field && typeof field === 'object' && field.autoPopulated === true) return true;
              }
            }
          }
          return false;
        };

        // BCM-654: also update UI when Corda sets errorMessage on a top-level field
        // (e.g. date_of_manufacture.errorMessage = "could not be determined").
        // Without this, shouldUpdateUI stays false and the error banner never shows on first visit.
        const hasFieldErrorMessage = (data) => {
          if (!data) return false;
          for (const key of Object.keys(data)) {
            if (key === 'bowls' || key === 'pallets') continue;
            const field = data[key];
            if (field && typeof field === 'object' && !Array.isArray(field) && field.errorMessage) return true;
          }
          return false;
        };

        const shouldUpdateUI = updated &&
          typeof updated === 'object' &&
          Object.keys(updated).length > 0 &&
          (hasAutoPopulated(updated) || hasFieldErrorMessage(updated) || validationErrorMessages.length > 0);

        if (shouldUpdateUI) {
          setApiData(updated);
        }
      }
    } catch (err) {
      if (axios.isCancel(err)) return; // superseded by a newer call — swallow silently
      // Non-blocking — log and continue; do not surface UI error for background validation
      console.error('[validateFieldsForDigitizationUI]', err?.response?.data?.errorMessage || err.message);
    }
  };

  // BCM-564 Trigger 1: Call validateFieldsForDigitizationUI on component mount.
  // This fires when the "Digitalized Consumption Details" link is clicked, which
  // mounts this component. Only runs for consumption documents (those with bowls).
  useEffect(() => {
    if (initialData?.bowls && initialData.bowls.length > 0) {
      runValidateFieldsForDigitizationUI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to get original data from extractedData
  const getDocumentFromExtractedData = () => {
    if (batchDetails?.digitizedExtractedData) {
      if (Array.isArray(batchDetails.digitizedExtractedData)) {
        const document = batchDetails.digitizedExtractedData.find(
          (doc) => doc.kvpJsonOriginal?.document_type === docType
        );
        return document?.kvpJsonOriginal || null;
      } else if (
        batchDetails.digitizedExtractedData.kvpJsonOriginal?.document_type ===
        docType
      ) {
        return batchDetails.digitizedExtractedData.kvpJsonOriginal || null;
      }
    }
    return null;
  };

  // Helper to check if edited data exists and has content
  const hasEditedData = () => {
    if (batchDetails?.digitizedExtractedData) {
      if (Array.isArray(batchDetails.digitizedExtractedData)) {
        const document = batchDetails.digitizedExtractedData.find(
          (doc) => doc.kvpJsonOriginal?.document_type === docType
        );
        return (
          document?.kvpJsonEdited &&
          Object.keys(document.kvpJsonEdited).length > 0
        );
      } else if (
        batchDetails.digitizedExtractedData.kvpJsonOriginal?.document_type ===
        docType
      ) {
        return (
          batchDetails.digitizedExtractedData.kvpJsonEdited &&
          Object.keys(batchDetails.digitizedExtractedData.kvpJsonEdited)
            .length > 0
        );
      }
    }
    return false;
  };

  const [apiData, setApiData] = useState(initialData || {});
  const [originalData, setOriginalData] = useState(initialData || {});
  const [editedData, setEditedData] = useState(initialData || {});
  // BCM-654: synchronous "an edit was saved this session" flag. Set true on a
  // successful field save so the page-level validate (validateFieldsForDigitizationUI)
  // sends the edited apiData under kvpJsonEdited even before batchDetails is refreshed.
  // hasEditedData() (which reads the async-refreshed batchDetails) is OR-ed with this,
  // so behavior is unchanged on first mount and only adds the in-session signal.
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editModalValidationError, setEditModalValidationError] = useState("");
  const [markReviewValidationErrors, setMarkReviewValidationErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // BCM-564: Compute batch low-confidence page-level warnings directly from apiData.
  // Re-evaluates whenever apiData changes (e.g. after user reviews/edits a field).
  const batchConfidencePageErrors = useMemo(() => {
    const errors = [];
    if (!apiData?.bowls) return errors;
    const lowConfLimit = batchDetails?.confidenceLevels?.lowConfidenceLimit ?? 80;
    apiData.bowls.forEach((bowl) => {
      if (!bowl?.rows) return;
      // Build a human-readable section label, e.g. "Consumption Details A"
      const bowlLabel = `Consumption Details ${bowl?.bowl?.value || bowl?.ID || ''}`.trim();
      bowl.rows.forEach((row, rowIndex) => {
        const batchSponsor = row?.consumption_batch_number_sponsor;
        if (!batchSponsor || !batchSponsor.value) return;

        const batchConfidence = parseInt(batchSponsor.confidence ?? 100);
        const needsValidation =
          batchSponsor.needValidation === true ||
          batchSponsor.needValidation === 'true';
        const isLowConfidence = batchConfidence < lowConfLimit;

        // Row label matches what is shown in the UI, e.g. "Consumption Batch Number BMS 1"
        const rowLabel = `Consumption Batch Number BMS ${rowIndex + 1}`;

        console.log('[BCM-564] batchSponsor:', batchSponsor.value,
          '| confidence:', batchConfidence,
          '| needValidation:', batchSponsor.needValidation,
          '| isLowConfidence:', isLowConfidence,
          '| needsValidation:', needsValidation);

        // BCM-564 (spec rows R15/R16): show the page-level "low confidence" error ONLY
        // when the batch is BOTH low-confidence AND needValidation=true. The spec uses AND,
        // not OR. A high-confidence batch with needValidation=true (e.g. backend flagged an
        // inventory mismatch) should NOT trigger this page-level error — it should be
        // resolved via the popup save flow (validateEditedDigitizedData) instead.
        // After the user reviews, needValidation flips to false → AND fails → error clears.
        if (needsValidation && isLowConfidence) {
          const materialValue = (
            row?.consumed_material_number_sponsor?.value || ''
          ).toString().trim();
          if (!materialValue) {
            errors.push(
              `${bowlLabel} - ${rowLabel} has low confidence. Please review the field to allow auto population of BMS Consumed Material Number field.`
            );
          } else {
            errors.push(
              `${bowlLabel} - ${rowLabel} has low confidence. Please review the field to allow validation of BMS Consumed Material Number field.`
            );
          }

          // BCM-654 (spec p.15): also flag DoM when it needs auto-population
          const domField = apiData?.date_of_manufacture;
          if (domField && typeof domField === 'object' &&
              domField.isVisibleOnUI !== false && !domField.value) {
            errors.push(
              `${bowlLabel} - ${rowLabel} has low confidence. Please review the field to allow auto population of Date of Manufacture field.`
            );
          }
        }
      });
    });
    return errors;
  }, [apiData, batchDetails]);

  // BCM-654: true when any bowl row has a batch field with needValidation=true.
  // Used to disable DoM editing and show its NA badge in the top-level fields.
  const hasAnyBatchLowConfidence = useMemo(() => {
    if (!apiData?.bowls) return false;
    return apiData.bowls.some(bowl =>
      (bowl?.rows || []).some(row => {
        const batchField = row?.consumption_batch_number_sponsor;
        return batchField?.needValidation === true;
      })
    );
  }, [apiData]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [enableAllFieldsEdit, setEnableAllFieldsEdit] = useState(false);
  const [modalData, setModalData] = useState({
    field: "",
    historyData: [],
    hasHistory: false,
  });
  const [editModalData, setEditModalData] = useState({
    fieldKey: "",
    fieldLabel: "",
    currentValue: "",
    saveValue: "",
    originalValue: "",
    confidenceLevel: 0,
    dataType: "",
    digitalizationCode: "",
    comment: "",
  });
  const [apiDigitalizationCodes, setApiDigitalizationCodes] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successDialogTitle, setSuccessDialogTitle] = useState("");
  // FLAG-GATED (Goods Receipt only): when the caller passes onFieldSaveResult, field
  // save / delete confirmations go out as a toast via that callback instead of the
  // OK-button dialog. The dialog's OK used to trigger the page-level re-validate, so
  // that now runs from the effect below (fresh apiData after the save's re-render).
  // Batch Execution passes no such prop and keeps the dialog.
  const [validateAfterSave, setValidateAfterSave] = useState(false);
  useEffect(() => {
    if (!validateAfterSave) return;
    setValidateAfterSave(false);
    if (apiData?.bowls && apiData.bowls.length > 0) {
      runValidateFieldsForDigitizationUI();
    }
  }, [validateAfterSave]); // eslint-disable-line react-hooks/exhaustive-deps
  const [markingReviewComplete, setMarkingReviewComplete] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [showUpdateRerunReminderDialog, setShowUpdateRerunReminderDialog] = useState(false);
  const [updateRerunLoading, setUpdateRerunLoading] = useState(false);
  const [productCode, setProductCode] = useState(null);
  // BCM-564: Validation errors returned by validateFieldsForDigitizationUI (triggers 1 & 3)
  const [cordaValidationErrors, setCordaValidationErrors] = useState([]);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState({
    type: "", // "batch" or "consumptionSection"
    bowlIndex: null,
    rowIndex: null,
    batchNumber: null,
    consumptionDetailsLabel: "",
    materialNumber: "",
    batchNumberValue: "",
    quantity: "",
  });
  const [deleteError, setDeleteError] = useState("");
  const [isFeaturesAccessLoaded, setIsFeaturesAccessLoaded] = useState(false);

  // Nested value helpers
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((current, key) => {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (key.includes("[")) {
        const [arr, idx] = key.split("[");
        const arrayIndex = parseInt(idx.replace("]", ""));
        if (current[arr] && Array.isArray(current[arr]) && current[arr][arrayIndex] !== undefined) {
          return current[arr][arrayIndex];
        }
        return undefined;
      }
      return current[key];
    }, obj);
  };

  const setNestedValue = (obj, path, value) => {
    if (!obj || !path) return;
    const parts = path.split(".");
    const lastKey = parts.pop();
    const target = parts.reduce((current, key) => {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (key.includes("[")) {
        const [arr, idx] = key.split("[");
        const arrayIndex = parseInt(idx.replace("]", ""));
        if (current[arr] && Array.isArray(current[arr]) && current[arr][arrayIndex] !== undefined) {
          return current[arr][arrayIndex];
        }
        return undefined;
      }
      return current[key];
    }, obj);
    if (target !== null && target !== undefined) {
      target[lastKey] = value;
    }
  };

  const parseFieldPath = (path, data) => {

    
    const bowlsMatch = path.match(/bowls\[(\d+)\]\.rows\[(\d+)\]\.(.+)/);
    if (bowlsMatch) {
      const [, bowlIndex, rowIndex, fieldKey] = bowlsMatch;
      const bowl = data.bowls?.[parseInt(bowlIndex)];
      return {
        dataFieldName: fieldKey,
        ID: bowl?.ID ?? null, // Use null if ID is undefined
        rowID: bowl?.rows?.[parseInt(rowIndex)]?.row_id ?? null, // Use null if row_id is undefined
      };
    }

    // Handle pallets structure: pallets[index].fieldKey
    const palletsMatch = path.match(/pallets\[(\d+)\]\.(.+)/);
    if (palletsMatch) {
      const [, palletIndex, fieldKey] = palletsMatch;
      const pallet = data.pallets?.[parseInt(palletIndex)];
      return {
        dataFieldName: fieldKey,
        ID: null,
        rowID: pallet?.row_id ?? null,
      };
    }
    
   
    
    return { dataFieldName: path, ID: null, rowID: null };
  };

  // Helper function to merge incoming data while preserving reasonCode and comment from existing edits
  // This prevents info icons from flipping back to confidence levels when data refreshes
  const mergePreservingEdits = (currentData, incomingData) => {
    if (!currentData || !incomingData) return incomingData;
    
    const merged = JSON.parse(JSON.stringify(incomingData)); // Deep clone incoming data

    
    // Preserve counts from current data if they are higher than incoming data
    // This prevents counts from resetting when backend hasn't updated yet
    const currentToBeReviewed = currentData.noOfFieldsToBeReviewed || 0;
    const currentReviewed = currentData.noOfFieldsReviewed || 0;
    const incomingToBeReviewed = merged.noOfFieldsToBeReviewed || 0;
    const incomingReviewed = merged.noOfFieldsReviewed || 0;
    
    // If current reviewed count is higher, preserve it (means local edit not yet reflected in backend)
    if (currentReviewed > incomingReviewed) {
      merged.noOfFieldsReviewed = currentReviewed;
      merged.noOfFieldsToBeReviewed = currentToBeReviewed;
    }
    
    // Merge regular fields
    Object.keys(merged).forEach((key) => {
      if (key !== "bowls" && typeof merged[key] === "object" && merged[key] !== null && "value" in merged[key]) {
        const currentField = currentData[key];
        if (currentField && typeof currentField === "object") {
          // Preserve reasonCode and comment from current data if they exist
          if (currentField.reasonCode && !merged[key].reasonCode) {
            merged[key].reasonCode = currentField.reasonCode;
          }
          if (currentField.comment && !merged[key].comment) {
            merged[key].comment = currentField.comment;
          }
          // If field was marked as not needing validation (edited), preserve that
          if (currentField.needValidation === false && merged[key].needValidation === true) {
            merged[key].needValidation = false;
          }
        }
      }
    });
    
    // Merge bowls fields
    if (merged.bowls && Array.isArray(merged.bowls) && currentData.bowls && Array.isArray(currentData.bowls)) {
      merged.bowls.forEach((bowl, bowlIndex) => {
        const currentBowl = currentData.bowls[bowlIndex];
        if (bowl.rows && Array.isArray(bowl.rows) && currentBowl?.rows && Array.isArray(currentBowl.rows)) {
          bowl.rows.forEach((row, rowIndex) => {
            const currentRow = currentBowl.rows[rowIndex];
            if (currentRow) {
              Object.keys(row).forEach((fieldKey) => {
                if (fieldKey !== "row_id" && typeof row[fieldKey] === "object" && row[fieldKey] !== null && "value" in row[fieldKey]) {
                  const currentField = currentRow[fieldKey];
                  if (currentField && typeof currentField === "object") {
                    // Preserve reasonCode and comment from current data if they exist
                    if (currentField.reasonCode && !row[fieldKey].reasonCode) {
                      row[fieldKey].reasonCode = currentField.reasonCode;
                    }
                    if (currentField.comment && !row[fieldKey].comment) {
                      row[fieldKey].comment = currentField.comment;
                    }
                    // If field was marked as not needing validation (edited), preserve that
                    if (currentField.needValidation === false && row[fieldKey].needValidation === true) {
                      row[fieldKey].needValidation = false;
                    }
                  }
                }
              });
            }
          });
        }
      });
    }
    
    return merged;
  };

  // Dynamic data initialization
  useEffect(() => {
    let dataToUse = initialData || {};
    let originalDataToUse = initialData || {};

    if (batchDetails?.digitizedExtractedData) {
      let document;
      const currentFileID = schrockenCustomFileID;
      
      if (Array.isArray(batchDetails.digitizedExtractedData)) {
        if (currentFileID) {
          document = batchDetails.digitizedExtractedData.find(
            (doc) => doc.schrockenCustomFileID === currentFileID
          );
        }
        
        if (!document) {
          document = batchDetails.digitizedExtractedData.find(
            (doc) => doc.kvpJsonOriginal?.document_type === docType ||
                     doc.kvpJsonEdited?.document_type === docType
          );
        }
      } else if (
        batchDetails.digitizedExtractedData.kvpJsonOriginal?.document_type === docType ||
        batchDetails.digitizedExtractedData.kvpJsonEdited?.document_type === docType
      ) {
        document = batchDetails.digitizedExtractedData;
      }

      if (document) {
        if (
          document.kvpJsonEdited &&
          Object.keys(document.kvpJsonEdited).length > 0
        ) {
          dataToUse = document.kvpJsonEdited;
          originalDataToUse = document.kvpJsonOriginal || document.kvpJsonEdited;
          
          // Preserve markAsReviewed from kvpJsonOriginal if not in kvpJsonEdited
          if (document.kvpJsonOriginal?.markAsReviewed !== undefined && dataToUse.markAsReviewed === undefined) {
            dataToUse.markAsReviewed = document.kvpJsonOriginal.markAsReviewed;
          }
        } else if (document.kvpJsonOriginal) {
          dataToUse = document.kvpJsonOriginal;
          originalDataToUse = document.kvpJsonOriginal;
        }
      }
    }

    // Merge with existing apiData to preserve reasonCode and comment from local edits
    // This prevents info icons from disappearing when data refreshes after 2-3 minutes
    const mergedData = mergePreservingEdits(apiData, dataToUse);
    setApiData(mergedData);
    setOriginalData(originalDataToUse);
    setEditedData(mergedData);
  }, [initialData, batchDetails, docType, schrockenCustomFileID]);

  useEffect(() => {
    const isDocumentMarkedAsReviewed = apiData.markAsReviewed === true;
    const hasNoPartialShipping = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;
    
    // Get document category for additional checks
    const customFile = batchDetails?.customFilesList?.find(
      file => file.schrockenCustomFileID === schrockenCustomFileID?.toString()
    );
    const category = customFile?.documentCategoryType;
    
    // Enable editing if document is marked as reviewed and no partial shipping
    if (isDocumentMarkedAsReviewed && hasNoPartialShipping) {
      setEnableAllFieldsEdit(true)
    }
    // Enable editing for Post-Shipment Documents docs when reviewPostShipmentFieldsReqd is true and partialShippingDetails exists
    else if (batchDetails?.reviewPostShipmentFieldsReqd === true && 
             batchDetails?.partialShippingDetails && 
             batchDetails.partialShippingDetails.length > 0 && 
             category === "Post-Shipment Documents") {
      setEnableAllFieldsEdit(true)
    }
  }, []);

  // Handle refresh when batchDetails changes
  useEffect(() => {
    if (
      batchDetails &&
      !showingOriginal &&
      batchDetails.digitizedExtractedData
    ) {
      let document;
      const currentFileID = schrockenCustomFileID;
      
      if (Array.isArray(batchDetails.digitizedExtractedData)) {
        if (currentFileID) {
          document = batchDetails.digitizedExtractedData.find(
            (doc) => doc.schrockenCustomFileID === currentFileID
          );
        }
        
        if (!document) {
          document = batchDetails.digitizedExtractedData.find(
            (doc) => doc.kvpJsonOriginal?.document_type === docType ||
                     doc.kvpJsonEdited?.document_type === docType
          );
        }
      } else if (
        batchDetails.digitizedExtractedData.kvpJsonOriginal?.document_type === docType ||
        batchDetails.digitizedExtractedData.kvpJsonEdited?.document_type === docType
      ) {
        document = batchDetails.digitizedExtractedData;
      }

      if (
        document &&
        document.kvpJsonEdited &&
        Object.keys(document.kvpJsonEdited).length > 0
      ) {
        // Merge with existing apiData to preserve reasonCode and comment from local edits
        const mergedData = mergePreservingEdits(apiData, document.kvpJsonEdited);
        setApiData(mergedData);
        setEditedData(mergedData);
      } else if (document && document.kvpJsonOriginal) {
        setApiData(document.kvpJsonOriginal);
        setEditedData(document.kvpJsonOriginal);
      }
    }
  }, [batchDetails, showingOriginal, docType, schrockenCustomFileID]);

  // Total Dispatch Qty / Remaining Qty must reflect ALL shipments combined — each
  // shipment's own KVP only carries its own "Dispatch Quantity" (dispatch_quantity, i.e.
  // "Dispatch Qty in this shipment"), so summing just the active shipment's document gave
  // the wrong (and sometimes negative) Remaining Qty on every tab. Recompute both fields
  // here — Total Dispatch Qty as the sum of dispatch_quantity across every shipment, and
  // Remaining Qty as Produced Qty (batchDetails.producedQuantity, from outside the KVP
  // document) minus that sum. Both are always computed/derived values, never raw digitized
  // data, so they're SYNTHESIZED into apiData when the document's own KVP doesn't already
  // carry them (test/dummy documents so far don't) rather than only patching a pre-existing
  // field. Declared AFTER the "Handle refresh when batchDetails changes" effect above
  // (which does a full setApiData(document.kvpJsonOriginal) REPLACEMENT whenever
  // kvpJsonEdited is empty) so this one always runs last and its synthesized fields
  // survive — otherwise that effect's replacement wipes them out on every render.
  useEffect(() => {
    if (!isPostShipmentAsnDoc) return;

    const shipmentDocs = documentShipments.length > 0
      ? documentShipments
      : (batchDetails?.digitizedExtractedData || []).filter(
          (d) => d?.schrockenCustomFileID === schrockenCustomFileID
        );

    const totalDispatchQty = shipmentDocs.reduce((sum, doc) => {
      // The active shipment's own Dispatch Qty may have just been edited (saved into
      // apiData) but not yet reflected in batchDetails' backend-sourced copy — use the
      // live apiData value for it so editing shipment 1 updates Total/Remaining (and the
      // negative-value warning) immediately on both tabs, without waiting on a refresh.
      const isActiveShipment = doc.schrockenCustomFileID === schrockenCustomFileID;
      if (isActiveShipment && apiData?.dispatch_quantity?.value !== undefined) {
        return sum + (Number(apiData.dispatch_quantity.value) || 0);
      }
      const kvp = (doc.kvpJsonEdited && Object.keys(doc.kvpJsonEdited).length > 0)
        ? doc.kvpJsonEdited
        : doc.kvpJsonOriginal;
      return sum + (Number(kvp?.dispatch_quantity?.value) || 0);
    }, 0);
    const producedQtyOutside = Number(batchDetails?.producedQuantity) || 0;
    // Round to 3 decimals to avoid floating-point noise (e.g. 2.8499999999999996)
    // from the plain subtraction below.
    const remainingQty = Math.round((producedQtyOutside - totalDispatchQty) * 1000) / 1000;

    setApiData((prev) => {
      const alreadyCorrect =
        prev?.total_dispatch_quantity?.value === totalDispatchQty &&
        prev?.total_dispatch_quantity?.confidence === 100 &&
        prev?.remaining_quantity?.value === remainingQty &&
        prev?.remaining_quantity?.confidence === 100;
      if (alreadyCorrect) return prev;
      return {
        ...prev,
        // Preserve any other pre-existing attributes (isMandatory, etc.) on these two
        // fields, but always force confidence to 100 and dataType to Numeric — the raw
        // backend placeholder for both is {confidence: null, value: null} on documents
        // that carry them, and a computed value is never anything but fully confident.
        total_dispatch_quantity: { ...prev?.total_dispatch_quantity, confidence: 100, dataType: "Numeric", value: totalDispatchQty },
        remaining_quantity: { ...prev?.remaining_quantity, confidence: 100, dataType: "Numeric", value: remainingQty },
      };
    });
  }, [isPostShipmentAsnDoc, documentShipments, batchDetails, schrockenCustomFileID, apiData?.dispatch_quantity?.value]);

  // Fetch digitalization codes
  useEffect(() => {
    const cancelSource = axios.CancelToken.source();
    const fetchDigitalizationCodes = async () => {
      try {
        const response = await axios.get(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/Admin/fetchDigitalizationCodes`,
          { cancelToken: cancelSource.token }
        );
        if (response.data.status && response.data.digitalizationCodesList) {
          const activatedCodes = response.data.digitalizationCodesList
            .filter((code) => code.activated && !code.markForDelete)
            .map((code) => ({
              code: code.digitalizationCode,
              description: code.digitalizationCodeDescription,
            }));
          setApiDigitalizationCodes(activatedCodes);
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("Error fetching digitalization codes:", error);
      }
    };
    fetchDigitalizationCodes();
    return () => {
      cancelSource.cancel("leaving digitized document view");
    };
  }, []);

  // Validate field
  const validateField = (fieldKey, value) => {
    if (!value || value.trim() === "") {
      return `${toTitleCase(fieldKey)} is required`;
    }
    return null;
  };

  // Format date for display - normalize all date formats to DD-MMM-YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString || dateString === "N/A" || dateString === "n/a" || dateString === "NA") {
      return dateString || "";
    }

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    try {
      let parsedDate = null;

      // Pattern 1: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss format (ISO)
      const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${parseInt(day)}-${monthNames[parseInt(month) - 1]}-${year}`;
      }

      // Pattern 2: DD-MMM-YYYY (already in target format)
      if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(dateString)) {
        return dateString;
      }

      // Pattern 3: DD.MMM.YYYY (e.g., "31.Dec.2025")
      const dotMonthMatch = dateString.match(/^(\d{1,2})\.([A-Za-z]{3})\.(\d{4})$/);
      if (dotMonthMatch) {
        const [, day, month, year] = dotMonthMatch;
        return `${parseInt(day)}-${month}-${year}`;
      }

      // Pattern 4: MM/DD/YYYY or DD/MM/YYYY
      const slashMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        const [, first, second, year] = slashMatch;
        // Try to parse using Date object
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const day = date.getDate();
          const month = date.getMonth();
          return `${day}-${monthNames[month]}-${year}`;
        }
      }

      // Fallback: Try to parse with Date object for any other format
      const date = parseStringDate(dateString);
      if (date && !isNaN(date.getTime())) {
        const day = date.getDate();
        const month = date.getMonth();
        const year = date.getFullYear();
        return `${day}-${monthNames[month]}-${year}`;
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }

    // If all parsing fails, return original
    return dateString;
  };

  // Get field value
  const getValueForField = (path) => {
    const data = showingOriginal ? originalData : apiData;
    const fieldData = getNestedValue(data, path);
    const value = fieldData &&
      typeof fieldData === "object" &&
      fieldData.value !== undefined
      ? fieldData.value || ""
      : fieldData || "";

    // Format date values for display — but not while the field still needs
    // validation. formatDateForDisplay's slash-date pattern (MM/DD vs DD/MM) is
    // inherently ambiguous; guessing it before the user has reviewed the raw
    // extracted value risks silently locking in a misparsed date. Show the raw
    // value as-is until needValidation flips to false (auto-confirmed or
    // user-edited), then format normally.
    if (fieldData && typeof fieldData === "object" && fieldData.dataType === "Date" && value) {
      if (fieldData.needValidation === true) {
        return value;
      }
      return formatDateForDisplay(value);
    }

    return value;
  };

  // Get confidence for field
  const getConfidenceForField = (path) => {
    const data = showingOriginal ? originalData : apiData;
    const fieldData = getNestedValue(data, path);
    if (
      fieldData &&
      typeof fieldData === "object" &&
      fieldData.confidence != null
    ) {
      return parseFloat(fieldData.confidence);
    }
    return 0;
  };

  // Get color for confidence level
  const getLevelColor = (level) => {
    if (level === null || level === undefined) return "#cccccc";
    const confidenceLevels = batchDetails?.confidenceLevels || {
      lowConfidenceLimit: 80,
      highConfidenceLimit: 90,
    };
    const lowConfidenceLimit = confidenceLevels.lowConfidenceLimit;
    const highConfidenceLimit = confidenceLevels.highConfidenceLimit;
    if (level < lowConfidenceLimit) return "red";
    if (level <= highConfidenceLimit) return "orange";
    return "green";
  };

  // Get confidence display
  const getConfidenceDisplay = (path) => {
    const confidence = getConfidenceForField(path);
    return confidence !== null ? Math.round(confidence) : "0";
  };

  // Get dataType for field
  const getDataTypeForField = (path) => {
    const data = showingOriginal ? originalData : apiData;
    const fieldData = getNestedValue(data, path);
    if (fieldData && typeof fieldData === "object" && fieldData.dataType) {
      return fieldData.dataType;
    }
    return null;
  };

  // Check if field is mandatory
  const isFieldMandatory = (path) => {
    try {
      const data = showingOriginal ? originalData : apiData;
      
      // For fields in bowls (consumption details), check if the row or bowl is new
      const bowlsMatch = path.match(/bowls\[(\d+)\]\.rows\[(\d+)\]\.(.+)/);
      if (bowlsMatch) {
        const [, bowlIndex, rowIndex, fieldKey] = bowlsMatch;
        const bowl = data.bowls?.[parseInt(bowlIndex)];
        const row = bowl?.rows?.[parseInt(rowIndex)];
        const isBowlNew = bowl?.isNew === true;
        const isRowNew = row?.isNew === true;

        // BCM-654: BMS Consumed Material Number is ALWAYS mandatory — always show the
        // red star, regardless of backend isMandatory flag or new/existing bowl state.
        if (fieldKey === 'consumed_material_number_sponsor') return true;
        
        // Fields that should show mandatory indicator even in new bowls (new consumed material)
        // BCM-654: include consumed_material_number_sponsor so the BMS material number always
        // shows the red mandatory star (alongside batch and qty), including in new bowls.
        const mandatoryFieldsForNewBowls = [
          'consumed_material_number_sponsor',
          'consumption_batch_number_sponsor',
          'consumption_batch_number',
          'actual_material_quantity_consumed'
        ];
        
        // If bowl is new (new consumed material), only show mandatory for specific fields
        if (isBowlNew && !mandatoryFieldsForNewBowls.includes(fieldKey)) {
          return false;
        }
        
        // If row is new but bowl is NOT new (new consumed batch in existing bowl), 
        // show mandatory for ALL fields that have isMandatory set to true
        if (isRowNew && !isBowlNew) {
          const fieldData = getNestedValue(data, path);
          return !!(fieldData && typeof fieldData === "object" && fieldData.isMandatory === true);
        }
      }
      
      const fieldData = getNestedValue(data, path);
      return !!(fieldData && typeof fieldData === "object" && fieldData.isMandatory === true);
    } catch (e) {
      // If path traversal fails or data is missing, treat as not mandatory rather than crashing render
      return false;
    }
  };

  // Check if field should show info icon
  const shouldShowInfoIcon = (path) => {
    if (showingOriginal) return false;
    // BCM-654: only show info icon when the document has saved edits (non-empty kvpJsonEdited).
    // Without this guard, mergePreservingEdits can carry in-memory reasonCode/comment even
    // when nothing has been saved to the server, causing the icon to appear and the history
    // popup to open only to show "No history found".
    if (!hasEditedData()) return false;
    const fieldData = getNestedValue(apiData, path);
    // BCM-565: require needValidation === false so the info icon only appears when Corda has
    // actually accepted the edit. Without this, a failed save (e.g., wrong material value)
    // still left reasonCode/comment in apiData via batchDetails refresh + mergePreservingEdits,
    // flipping the field's confidence badge to an info icon even though the save was rejected.
    return (
      fieldData &&
      typeof fieldData === "object" &&
      fieldData.needValidation === false &&
      (fieldData.reasonCode || fieldData.comment)
    );
  };

  // Check if field needs validation
  const shouldShowEditForValidation = (path) => {
    if (showingOriginal) return false;
    const fieldData = getNestedValue(apiData, path);
    return (
      fieldData &&
      typeof fieldData === "object" &&
      fieldData.needValidation === true
    );
  };

  // Check if editing is allowed based on document category
  const isDocumentCategoryEditable = () => {
    // return true
    if (!batchDetails || !schrockenCustomFileID) return false;
    
    // Get document category first
    const customFile = batchDetails.customFilesList?.find(
      file => file.schrockenCustomFileID === schrockenCustomFileID.toString()
    );
    
    if (!customFile) return false;

    const category = customFile.documentCategoryType;

    // After a Batch Reset + document resubmission, the batch status moves ahead again
    // but the reviewPostMFGFieldsReqd / reviewPostShipmentFieldsReqd flags below aren't
    // reliably re-flipped by the backend to reflect that — which was hiding Mark Review
    // as Complete even though the batch has genuinely moved forward with real resubmitted
    // documents to review. Allow it explicitly once the batch has reached these statuses,
    // checked first (and returned early) so none of the flag-based branches below can
    // override it:
    //  - Post-Manufacturing: CMO QC Released (5) or back to Production Complete (3)
    //  - Post-Shipment Documents: Post-Shipment Documents Uploaded (50), CMO QC Released (5),
    //    or back to Production Complete (3)
    if (category === "Post-Manufacturing" &&
        (batchDetails.SASLBatchStatusID === 5 || batchDetails.SASLBatchStatusID === 3)) {
      return true;
    }
    if (category === "Post-Shipment Documents" &&
        (batchDetails.SASLBatchStatusID === 50 || batchDetails.SASLBatchStatusID === 5 || batchDetails.SASLBatchStatusID === 3)) {
      return true;
    }

    // BCM-898: the two flag checks below used to treat "flag is false" and "flag was never
    // configured" identically (`!(x)` is true for both false and null/undefined) — so a CMO
    // that explicitly configured review as NOT mandatory (reviewPostMFGFieldsReqd: false, like
    // PCI IRL/PHL) still got defaulted to editable here, same as a CMO that simply never had
    // the flag configured at all. Only treat it as "default to editable" when the flag is
    // genuinely unset (`== null` matches both null and undefined) — an explicit `false` means
    // the business deliberately turned review off for this CMO and must stay non-editable.
    // This only narrows behavior for CMOs with an explicit false; any CMO relying on the
    // flag being absent keeps working exactly as before.
    // NOTE: this function governs general field-EDITING capability (the "Apply edit feature
    // to all fields" checkbox, individual field edit icons, add/delete consumption rows,
    // etc.) and deliberately treats reviewPostMFGFieldsReqd/reviewPreShipmentFieldsReqd being
    // explicitly false the SAME as them being unconfigured — editing/correcting a field's
    // value must stay available regardless of whether formal review completion is mandatory
    // for this CMO (e.g. to fix a Specs Not Matching variance and trigger Update and re-Run,
    // even when the CMO doesn't require Mark Review as Complete). BCM-898's "review isn't
    // mandatory for this CMO" distinction is intentionally NOT applied here — it's scoped
    // separately, only to the Mark Review as Complete button's own visibility check further
    // below, so it can't affect editability through this shared function.

    // If all review flags are false/unconfigured, allow editing only for Post-Manufacturing and Pre-Shipment Documents
    if ((!(batchDetails.reviewPostMFGFieldsReqd) || (batchDetails.reviewPostMFGFieldsReqd === false)) &&
        (!(batchDetails?.partialShippingDetails) || (batchDetails?.partialShippingDetails.length === 0)) &&
        (!(batchDetails.reviewPreShipmentFieldsReqd) || (batchDetails.reviewPreShipmentFieldsReqd === false))) {
      if (category === "Post-Manufacturing" || category === "Pre-Shipment Documents") {
        return true;
      }
      return false;
    }


    // Special case: if reviewPostMFGFieldsReqd is null/false and reviewPreShipmentFieldsReqd is true,
    // allow editing for both Post-Manufacturing and Pre-Shipment Documents
    if ((!(batchDetails.reviewPostMFGFieldsReqd) || (batchDetails.reviewPostMFGFieldsReqd === false)) &&
        (batchDetails.reviewPreShipmentFieldsReqd === true)) {
      if (category === "Post-Manufacturing" || category === "Pre-Shipment Documents") {
        return true;
      }
    }
    
    // Special case: when reviewPostShipmentFieldsReqd is true and partialShippingDetails array has items,
    // allow editing for Post-Shipment Documents documents
    if (batchDetails.reviewPostShipmentFieldsReqd === true && 
        batchDetails?.partialShippingDetails && 
        batchDetails.partialShippingDetails.length > 0 && 
        category === "Post-Shipment Documents") {
      return true;
    }
    
    // Map review flags to their corresponding categories
    if (category === "Post-Manufacturing" && batchDetails.reviewPostMFGFieldsReqd === true) {
      return true;
    }
    if (category === "Post-Shipment Documents" && batchDetails.reviewPostShipmentFieldsReqd === true) {
      return true;
    }
    if (category === "Pre-Shipment Documents" && batchDetails.reviewPreShipmentFieldsReqd === true) {
      return true;
    }
    
    return false;
  };

  // // Input validation based on dataType
  // const validateInput = (value, dataType) => {
  //   if (!dataType) return true; // If no dataType specified, allow any input

  //   switch (dataType) {
  //     case "Number":
  //       return /^-?\d*\.?\d*$/.test(value) && value !== "";
  //     case "String":
  //       return /^[a-zA-Z0-9\s]*$/.test(value); // Only alphanumeric and spaces
  //     case "Date":
  //       return true; // Date picker will handle validation
  //     default:
  //       return true;
  //   }
  // };

  // const formatInputValue = (value, dataType) => {
  //   if (!dataType) return value; // If no dataType specified, return as is

  //   if (dataType === "Number") {
  //     // Remove any non-numeric characters except decimal point and minus
  //     return value.replace(/[^-0-9.]/g, "");
  //   }
  //   if (dataType === "String") {
  //     // Remove special characters except spaces
  //     return value.replace(/[^a-zA-Z0-9\s]/g, "");
  //   }
  //   return value;
  // };

  // Handle input restriction based on dataType
  const handleInputRestriction = (e, dataType) => {
    const value = e.target.value;

    if (dataType === "Numeric") {
      // Allow only numbers, decimal point.
      e.target.value = value.replace(/[^0-9.]/g, "");

      // Prevent multiple decimal points
      const parts = e.target.value.split(".");
      if (parts.length > 2) {
        e.target.value = parts[0] + "." + parts.slice(1).join("");
      }
    } else if (dataType === "Numeric/Whole Number") {
      // Allow only numbers(no decimal point)
      e.target.value = value.replace(/[^0-9]/g, "");
    } else if (dataType === "String") {
      // Allow only alphanumeric and spaces
      e.target.value = value.replace(/[^a-zA-Z0-9\s\.\,\-\(\)]/g, "");
    }

    // Always update the state with the value (restricted or unrestricted)
    // Note: Trimming is done before saving, not while typing, to allow users to type freely
    setEditModalData((prev) => ({
      ...prev,
      currentValue: e.target.value,
    }));
    
    // Clear error message when user starts typing
    if (editModalValidationError) {
      setEditModalValidationError("");
    }
  };

  // Check if editing is disabled
  const isEditingDisabled = () => {
    // Post-Manufacturing Documents not yet received (batch status 3) — nothing to
    // review/edit on this document yet, matching the "We can proceed to next steps
    // once the Post-Manufacturing Documents are received" note shown in the preview pane.
    // Post-Shipment documents are exempt — same as that note, this restriction doesn't
    // apply to them.
    if (batchDetails?.SASLBatchStatusID === 3 && !/post-shipment/i.test(currentFileCategory || "")) {
      return true;
    }

    // MOSYMCOM-2829: IDoc-sourced documents (e.g. Production ASN) carry no OCR
    // confidence data, so the field-count gate below never trips for them even
    // while the backend blocks the batch pending review — bypass it here so
    // Mark Review as Complete stays reachable, matching isIdocDocument's intent.
    if (isIdocDocument) {
      return false;
    }

    const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
    const reviewed = apiData.noOfFieldsReviewed || 0;
    const isMarkedAsReviewed = apiData.markAsReviewed === true;

    // Enable editing when Mark as Complete button is visible
    // Button is visible when: !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed
    if (!showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed) {
      return false; // Enable editing (but still check category for actual editability)
    }

    // Check if document is marked as reviewed (document-specific)
    const isDocumentMarkedAsReviewed = apiData.markAsReviewed === true;
    const hasNoPartialShipping = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;
    
    // Special case: If document is marked as reviewed and no partial shipping, allow editing
    if (isDocumentMarkedAsReviewed && hasNoPartialShipping) {
      return false; // Enable editing irrespective of validation status
    }
    
    // Special case: For Post-Shipment Documents docs when reviewPostShipmentFieldsReqd is true and partialShippingDetails exists
    const customFile = batchDetails?.customFilesList?.find(
      file => file.schrockenCustomFileID === schrockenCustomFileID?.toString()
    );
    const category = customFile?.documentCategoryType;
    
    if (batchDetails?.reviewPostShipmentFieldsReqd === true && 
        batchDetails?.partialShippingDetails && 
        batchDetails.partialShippingDetails.length > 0 && 
        category === "Post-Shipment Documents") {
      return false; // Enable editing for Post-Shipment Documents docs in this scenario
    }
    
    
    
    // Check if there are fields needing validation
    let needsValidation = false;
    
    Object.keys(apiData).forEach((key) => {
      if (
        key !== "bowls" &&
        key !== "pallets" &&
        typeof apiData[key] === "object" &&
        apiData[key].needValidation === true
      ) {
        needsValidation = true;
      }
    });
    if (apiData.bowls) {
      apiData.bowls.forEach((bowl) => {
        bowl.rows.forEach((row) => {
          Object.keys(row).forEach((key) => {
            if (
              key !== "row_id" &&
              typeof row[key] === "object" &&
              row[key].needValidation === true
            ) {
              needsValidation = true;
            }
          });
        });
      });
    }

    if (apiData.pallets) {
      apiData.pallets.forEach((pallet) => {
        Object.keys(pallet).forEach((key) => {
          if (
            key !== "row_id" &&
            typeof pallet[key] === "object" &&
            pallet[key].needValidation === true
          ) {
            needsValidation = true;
          }
        });
      });
    }
    
    const result = !needsValidation;
    return result;
  };

  // Fetch field history
  const fetchFieldHistory = async (path, fieldLabel) => {
    try {
      setOpenModal(false);
      setIsFetchingHistory(true);
      const { dataFieldName, ID, rowID } = parseFieldPath(path, apiData);
      const requestPayload = {
        schrockenDeliveryScheduleBatchID:
          batchDetails?.schrockenDeliveryScheduleBatchID,
        documentType: docType,
        dataFieldName,
        ...(ID !== null && { ID: Number(ID) }), // Ensure ID is a number
        ...(rowID !== null && { rowID: Number(rowID) }), // Ensure rowID is a number
      };
      if (fieldHistoryCancelRef.current) {
        fieldHistoryCancelRef.current.cancel("superseded field-history fetch");
      }
      const cancelSource = axios.CancelToken.source();
      fieldHistoryCancelRef.current = cancelSource;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/fetchExtractedDataHistory`,
        requestPayload,
        { headers: { "Content-Type": "application/json" }, cancelToken: cancelSource.token }
      );
      if (
        response.data?.status &&
        response.data?.fieldDataHistory?.length > 0
      ) {
        const historyRecords = response.data.fieldDataHistory.map((record) => ({
          before: record.beforeValue || "N/A",
          after: record.afterValue || "N/A",
          updatedBy: record.lastUpdatedBy || "System",
          timestamp: record.lastUpdatedDate
            ? new Date(record.lastUpdatedDate).toLocaleString()
            : new Date().toLocaleString(),
        }));
        setModalData({
          field: fieldLabel,
          historyData: historyRecords,
          hasHistory: true,
        });
      } else {
        setModalData({
          field: fieldLabel,
          historyData: [],
          hasHistory: false,
        });
      }
      setOpenModal(true);
    } catch (error) {
      if (axios.isCancel(error)) return; // superseded by a newer field-history click
      console.error(
        "Error fetching field history:",
        error.response?.data || error.message
      );
      setModalData({
        field: fieldLabel,
        historyData: [],
        hasHistory: false,
      });
      setOpenModal(true);
    } finally {
      setIsFetchingHistory(false);
    }
  };


  // Open edit modal
  const handleOpenEditModal = (path, fieldLabel) => {
    const currentValue = getValueForField(path);
    const confidence = getConfidenceForField(path);
    const dataType = getDataTypeForField(path);
    setEditModalData({
      fieldKey: path,
      fieldLabel,
      currentValue,
      saveValue: currentValue,
      originalValue: currentValue,
      confidenceLevel: confidence,
      dataType,
      digitalizationCode: "",
      comment: "",
    });
    setEditModalValidationError("");
    setOpenEditModal(true);
  };

  /*
   * BCM-1077: the same batch, or the same material, may not be entered twice.
   *
   * A bowl IS a material and each of its rows is one batch of that material
   * (handleAddNewConsumedMaterial adds a bowl, handleAddNewConsumedBatch adds a
   * row to one), which is why the two scopes differ:
   *   • batch    → must be unique across EVERY row of every bowl. Entering a
   *                batch that already exists under a different material is a
   *                clash too, not just one repeated under the same material.
   *   • material → compared only with the OTHER bowls. Rows inside one bowl
   *                sharing a material is the normal structure (one material,
   *                many batches), not a duplicate.
   * Both Add New buttons create the row/bowl with empty values and the value is
   * then typed in this same edit popup, so adding is covered as well as editing.
   *
   * Compared trimmed and case-insensitively. A blank value is never a
   * duplicate — the mandatory check owns that case.
   */
  const BATCH_FIELD_KEYS = [
    'consumption_batch_number_sponsor',
    'consumption_batch_number',
  ];
  const MATERIAL_FIELD_KEYS = [
    'consumed_material_number_sponsor',
    'consumed_material_number',
  ];

  const findDuplicateFieldEntry = (path, rawValue) => {
    const match = String(path || '').match(
      /bowls\[(\d+)\]\.rows\[(\d+)\]\.(.+)$/
    );
    if (!match) return null;

    const editingBowl = Number(match[1]);
    const editingRow = Number(match[2]);
    const fieldKey = match[3];

    const isBatch = BATCH_FIELD_KEYS.includes(fieldKey);
    const isMaterial = MATERIAL_FIELD_KEYS.includes(fieldKey);
    if (!isBatch && !isMaterial) return null;

    const normalize = (v) => String(v ?? '').trim().toLowerCase();
    const target = normalize(rawValue);
    if (!target) return null;

    const bowls = Array.isArray(apiData?.bowls) ? apiData.bowls : [];
    for (let b = 0; b < bowls.length; b += 1) {
      // A material repeated inside its own bowl is the normal shape, not a clash.
      if (isMaterial && b === editingBowl) continue;

      const rows = Array.isArray(bowls[b]?.rows) ? bowls[b].rows : [];
      for (let r = 0; r < rows.length; r += 1) {
        if (b === editingBowl && r === editingRow) continue;
        if (normalize(rows[r]?.[fieldKey]?.value) === target) {
          const bowl = bowls[b];
          return {
            isBatch,
            // Same label the section heading uses, so the message points at
            // something the user can actually see on screen.
            bowlLabel: `Consumption Details ${
              bowl?.bowl?.value || bowl?.ID || b + 1
            }`,
          };
        }
      }
    }
    return null;
  };

  // Save field edit
  const handleSaveFieldEdit = async () => {
    // if (
    //   editModalData.dataType &&
    //   !validateInput(editModalData.currentValue, editModalData.dataType)
    // ) {
    //   setEditModalValidationError(
    //     `Invalid ${editModalData.dataType.toLowerCase()} format`
    //   );
    //   return;
    // }
    // Check if mandatory field is empty
    if (isFieldMandatory(editModalData.fieldKey)) {
      const valueToCheck = editModalData.dataType === "Date" 
        ? editModalData.saveValue || editModalData.currentValue 
        : editModalData.currentValue;
      
      if (!valueToCheck || (typeof valueToCheck === 'string' && valueToCheck.trim() === "")) {
        setEditModalValidationError(
          `${editModalData.fieldLabel} is mandatory and cannot be empty`
        );
        return;
      }
    }

    /*
     * BCM-1077: block a batch or material that is already entered elsewhere.
     * Checked before the digitalization-code rule so the user is told what is
     * actually wrong with the value, rather than being sent to fill in a
     * comment for an edit that cannot be saved anyway.
     */
    const duplicateEntry = findDuplicateFieldEntry(
      editModalData.fieldKey,
      editModalData.currentValue
    );
    if (duplicateEntry) {
      setEditModalValidationError(
        duplicateEntry.isBatch
          ? `This Consumed Material Batch is already entered in ${duplicateEntry.bowlLabel}. The same batch cannot be entered twice.`
          : `This Consumed Material Number is already entered in ${duplicateEntry.bowlLabel}. The same material cannot be entered twice.`
      );
      return;
    }

    if (!editModalData.digitalizationCode && !editModalData.comment.trim()) {
      setEditModalValidationError(
        "If digitalization code is not entered, comment field is mandatory"
      );
      return;
    }
    setEditModalValidationError("");
    try {
      setSaving(true);
      const fieldData = getNestedValue(apiData, editModalData.fieldKey);
      let valueToSave = editModalData.currentValue;
      if (editModalData.dataType === "Date") {
       if (editModalData.saveValue) {
         valueToSave = editModalData.saveValue;
       } else {
         valueToSave = editModalData.currentValue;
       }
      } else {
        // Trim leading and trailing spaces for better optimization (except for Date fields)
        valueToSave = typeof valueToSave === 'string' ? valueToSave.trim() : valueToSave;
      }
      // Trim comment field as well
      const trimmedComment = typeof editModalData.comment === 'string' ? editModalData.comment.trim() : editModalData.comment;
      
      // Check if this is a bowl field (consumption details)
      const isBowlField = editModalData.fieldKey.includes('bowls[');
      
      // Extract field name from path (e.g., "bowls[0].rows[0].consumed_material_number" -> "consumed_material_number")
      let fieldName = '';
      let currentBowlIndex = -1;
      let currentRowIndex = -1;
      if (isBowlField) {
        const match = editModalData.fieldKey.match(/bowls\[(\d+)\]\.rows\[(\d+)\]\.(.+)$/);
        if (match) {
          currentBowlIndex = parseInt(match[1]);
          currentRowIndex = parseInt(match[2]);
          fieldName = match[3];
        }
      }
      
      // Validation for consumption details fields only
      if (isBowlField && apiData.bowls && Array.isArray(apiData.bowls)) {
        // Check if editing consumed_material_number or consumed_material_number_sponsor
        const isMaterialField = fieldName === 'consumed_material_number' || fieldName === 'consumed_material_number_sponsor';
        // Check if editing consumption_batch_number or consumption_batch_number_sponsor
        const isBatchField = fieldName === 'consumption_batch_number' || fieldName === 'consumption_batch_number_sponsor';
        
        if (isMaterialField || isBatchField) {
          // Get current bowl's material number (check both fields)
          const currentBowl = apiData.bowls[currentBowlIndex];
          const currentRow = currentBowl?.rows?.[currentRowIndex];
          
          // Material number is always stored in the first row of the bowl
          const firstRowOfCurrentBowl = currentBowl?.rows?.[0];
          
          // Determine the new material number
          let newMaterialNumber = '';
          if (isMaterialField) {
            // User is editing the material field
            newMaterialNumber = valueToSave;
          } else {
            // User is editing batch field. Use THIS row's own BMS material if it has one
            // (a bowl can have rows with DIFFERENT materials); otherwise fall back to the
            // first row's material (same-material bowls store it once in row 0).
            // CMO field is no longer visible on UI, so dedup uses Sponsor field only.
            newMaterialNumber = currentRow?.consumed_material_number_sponsor?.value
              || firstRowOfCurrentBowl?.consumed_material_number_sponsor?.value || '';
          }

          // Determine the new batch number (Sponsor field only — CMO batch is not visible on UI)
          let newBatchNumber = '';
          if (isBatchField) {
            // User is editing the batch field
            newBatchNumber = valueToSave;
          } else {
            // User is editing material field, get existing BMS batch number
            newBatchNumber = currentRow?.consumption_batch_number_sponsor?.value || '';
          }

          // Check for duplicates across all bowls and rows (BMS values only)
          for (let bowlIndex = 0; bowlIndex < apiData.bowls.length; bowlIndex++) {
            const bowl = apiData.bowls[bowlIndex];
            if (!bowl.rows || !Array.isArray(bowl.rows)) continue;

            // Material is normally stored in the first row of a bowl, but a bowl can also
            // have rows with DIFFERENT materials — so compare against each row's OWN material
            // when it has one, falling back to the first row's for same-material bowls.
            const firstRowOfBowl = bowl.rows[0];

            for (let rowIndex = 0; rowIndex < bowl.rows.length; rowIndex++) {
              // Skip the current row being edited
              if (bowlIndex === currentBowlIndex && rowIndex === currentRowIndex) continue;

              const row = bowl.rows[rowIndex];
              const existingMaterialNumber = row?.consumed_material_number_sponsor?.value
                || firstRowOfBowl?.consumed_material_number_sponsor?.value || '';
              const existingBatchNumber = row?.consumption_batch_number_sponsor?.value || '';
              
              // Check if material numbers match (case-insensitive, trimmed)
              const materialsMatch = existingMaterialNumber && newMaterialNumber && 
                                    existingMaterialNumber.toString().trim().toLowerCase() === 
                                    newMaterialNumber.toString().trim().toLowerCase();
              
              // Check if batch numbers match (case-insensitive, trimmed)
              // This compares the actual batch values users enter (e.g., "123" === "123")
              const batchesMatch = existingBatchNumber && newBatchNumber && 
                                  existingBatchNumber.toString().trim().toLowerCase() === 
                                  newBatchNumber.toString().trim().toLowerCase();
              
              // Validation 1: Check if editing material field and material already exists
              if (materialsMatch && isMaterialField) {
                setSaving(false);
                setEditModalValidationError(
                  "This material already exists. Please add a new batch for the existing material instead."
                );
                return;
              }
              
              // Validation 2: Check if editing batch field and same material + same batch value exists
              // Example: Material "MAT001" already has batch value "123" in "Consumption Batch Number BMS 1"
              //          User tries to enter "123" in "Consumption Batch Number BMS 2" for same material "MAT001"
              //          → Error: Batch already exists for this material
              if (materialsMatch && batchesMatch && isBatchField) {
                setSaving(false);
                setEditModalValidationError(
                  "The Consumption Batch Number already exists for this material. Please enter a new batch number."
                );
                return;
              }
            }
          }
        }
      }
      
      // BCM-564: Validation is now handled inside handleDataExtractionEditedData (MW calls
      // Corda /validateEditedDigitizedData before saving). FE checks errorMessage in response.

      // For edited fields: needValidation = false
      // Preserve isMandatory value (true or false) from original field data, falling back to
      // kvpJsonOriginal when apiData's copy is missing it (e.g. an earlier save dropped it).
      const originalFieldForEdit = getNestedValue(originalData, editModalData.fieldKey);
      const resolvedIsMandatoryForEdit =
        fieldData?.isMandatory !== undefined ? fieldData.isMandatory : originalFieldForEdit?.isMandatory;
      const newFieldData = {
        ...fieldData,
        value: valueToSave,
        needValidation: false,
        reasonCode: editModalData.digitalizationCode,
        comment: trimmedComment,
        ...(fieldData?.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI }),
        // Preserve isMandatory value (true or false) if it exists in original field
        ...(resolvedIsMandatoryForEdit !== undefined && { isMandatory: resolvedIsMandatoryForEdit }),
        // Clear errorMessage when DoM is manually edited — backend set it when DoM could not
        // be determined; user editing the field resolves it, so we send null to clear it.
        ...(editModalData.fieldKey === 'date_of_manufacture' && { errorMessage: null }),
      };
      const newApiData = JSON.parse(JSON.stringify(apiData));
      setNestedValue(newApiData, editModalData.fieldKey, newFieldData);

      // BCM-564: When material number is edited/confirmed in a bowl row, immediately clear
      // consumption_batch_number_sponsor.needValidation for that row so the page-level
      // warning disappears right after save — without waiting for a batchDetails refresh.
      // BCM-654: ONLY do this when the batch ALREADY has a value (the existing
      // low-confidence batch case). For a newly added bowl/row the batch is still
      // empty — clearing its needValidation here would wrongly hide the pencil and
      // let the user skip filling in a mandatory batch. Empty batch must keep
      // needValidation: true.
      if (isBowlField &&
          (fieldName === 'consumed_material_number' || fieldName === 'consumed_material_number_sponsor') &&
          currentBowlIndex >= 0 && currentRowIndex >= 0) {
        const batchSponsorPath = `bowls[${currentBowlIndex}].rows[${currentRowIndex}].consumption_batch_number_sponsor`;
        const batchSponsorInNew = getNestedValue(newApiData, batchSponsorPath);
        const batchHasValue =
          batchSponsorInNew?.value !== null &&
          batchSponsorInNew?.value !== undefined &&
          String(batchSponsorInNew?.value).trim() !== '';
        if (batchSponsorInNew && batchSponsorInNew.needValidation === true && batchHasValue) {
          setNestedValue(newApiData, batchSponsorPath, { ...batchSponsorInNew, needValidation: false });
        }
      }

      const wasFieldNeedingValidation = fieldData?.needValidation === true;
      const existingData = apiData;
      const updatedDocumentJSON = {};
      Object.keys(newApiData).forEach((fieldKey) => {
        if (
          fieldKey !== "bowls" &&
          typeof newApiData[fieldKey] === "object" &&
          "value" in newApiData[fieldKey]
        ) {
          const fieldData = newApiData[fieldKey];
          // Fall back to kvpJsonOriginal's isMandatory when this field's copy doesn't carry one.
          const originalIsMandatoryForKey = originalData?.[fieldKey]?.isMandatory;
          const resolvedIsMandatoryForKey =
            fieldData.isMandatory !== undefined ? fieldData.isMandatory : originalIsMandatoryForKey;
          updatedDocumentJSON[fieldKey] = {
            value: fieldData.value,
            confidence: fieldData.confidence,
            ...(fieldData.dataType && { dataType: fieldData.dataType }),
            ...(fieldData.needValidation !== undefined && {
              needValidation: fieldData.needValidation,
            }),
            // Include isMandatory for all fields (including edited) to show mandatory star in UI
            ...(resolvedIsMandatoryForKey !== undefined && {
              isMandatory: resolvedIsMandatoryForKey,
            }),
            ...(fieldData.reasonCode !== undefined && { reasonCode: fieldData.reasonCode }),
            ...(fieldData.comment !== undefined && { comment: fieldData.comment }),
            ...(fieldData.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI }),
            // Carry errorMessage through (null when user cleared it via edit, string when backend set it)
            ...(fieldData.errorMessage !== undefined && { errorMessage: fieldData.errorMessage }),
          };
        } else if (
          (fieldKey === "bowls" || fieldKey === "pallets") &&
          Array.isArray(newApiData[fieldKey])
        ) {
          // Remove isNew flag before sending to backend (it's only for local UI state)
          // Pass original bowls data to preserve all fields for existing rows
          const originalBowlsData = originalData[fieldKey];
          updatedDocumentJSON[fieldKey] = cleanBowlsForBackend(newApiData[fieldKey], originalBowlsData);
        }
      });

      [
        "document_type",
        "folder_name",
        "site_name",
        "filename",
        "s3_location",
        "markAsReviewed",
      ].forEach((field) => {
        if (existingData[field] !== undefined) {
          updatedDocumentJSON[field] = existingData[field];
        }
      });

      // Recalculate counts from the updated document structure
      let noOfFieldsToBeReviewed = 0;
      let noOfFieldsReviewed = 0;

      // Count regular fields
      Object.keys(updatedDocumentJSON).forEach((key) => {
        if (
          key !== "bowls" &&
          key !== "pallets" &&
          key !== "noOfFieldsToBeReviewed" &&
          key !== "noOfFieldsReviewed" &&
          typeof updatedDocumentJSON[key] === "object" &&
          "value" in updatedDocumentJSON[key] &&
          updatedDocumentJSON[key].needValidation !== undefined
        ) {
          // Only count mandatory fields in noOfFieldsToBeReviewed
          if (updatedDocumentJSON[key].needValidation === true && updatedDocumentJSON[key].isMandatory === true) {
            noOfFieldsToBeReviewed += 1;
          } else if (
            updatedDocumentJSON[key].needValidation === false &&
            // updatedDocumentJSON[key].isMandatory === true &&
            (updatedDocumentJSON[key].reasonCode || updatedDocumentJSON[key].comment)
          ) {
            // Only count as reviewed if it's mandatory and has been explicitly edited (has reasonCode or comment)
            noOfFieldsReviewed += 1;
          }
        }
      });

      // Count bowl fields
      if (Array.isArray(updatedDocumentJSON.bowls)) {
        updatedDocumentJSON.bowls.forEach((bowl) => {
          if (bowl.rows && Array.isArray(bowl.rows)) {
            bowl.rows.forEach((row) => {
              Object.keys(row).forEach((key) => {
                if (
                  key !== "row_id" &&
                  typeof row[key] === "object" &&
                  "value" in row[key] &&
                  row[key].needValidation !== undefined
                ) {
                  // Only count mandatory fields in noOfFieldsToBeReviewed
                  if (row[key].needValidation === true && row[key].isMandatory === true) {
                    noOfFieldsToBeReviewed += 1;
                  } else if (
                    row[key].needValidation === false &&
                    // row[key].isMandatory === true &&
                    (row[key].reasonCode || row[key].comment)
                  ) {
                    // Only count as reviewed if it has been explicitly edited (has reasonCode or comment)
                    noOfFieldsReviewed += 1;
                  }
                }
              });
            });
          }
        });
      }

      // Count pallet fields
      if (Array.isArray(updatedDocumentJSON.pallets)) {
        updatedDocumentJSON.pallets.forEach((pallet) => {
          Object.keys(pallet).forEach((key) => {
            if (
              key !== "row_id" &&
              typeof pallet[key] === "object" &&
              "value" in pallet[key] &&
              pallet[key].needValidation !== undefined
            ) {
              // Only count mandatory fields in noOfFieldsToBeReviewed
              if (pallet[key].needValidation === true && pallet[key].isMandatory === true) {
                noOfFieldsToBeReviewed += 1;
              } else if (
                pallet[key].needValidation === false &&
                // pallet[key].isMandatory === true &&
                (pallet[key].reasonCode || pallet[key].comment)
              ) {
                // Only count as reviewed if it has been explicitly edited (has reasonCode or comment)
                noOfFieldsReviewed += 1;
              }
            }
          });
        });
      }

      

      updatedDocumentJSON.noOfFieldsToBeReviewed = noOfFieldsToBeReviewed;
      updatedDocumentJSON.noOfFieldsReviewed = noOfFieldsReviewed;

      // BCM-564: only bowl consumption/material/DoM fields need Corda validate before save
      const needsValidation = isBowlField && (
        fieldName === 'consumed_material_number' ||
        fieldName === 'consumed_material_number_sponsor' ||
        fieldName === 'consumption_batch_number' ||
        fieldName === 'consumption_batch_number_sponsor' ||
        fieldName === 'date_of_manufacture'
      );

      // BCM-654: tell Corda which specific field is being saved so it can scope
      // its validation to this field/row only and avoid returning errors about
      // other rows the user did not touch. Backward-compatible: when this field
      // is absent (legacy callers / page-level validate-on-mount) Corda should
      // keep its current "validate everything" behavior.
      //
      // Shape (for Corda team):
      //   validatingField: {
      //     bowlIndex: number|null,   // 0-based; null for top-level fields
      //     rowIndex:  number|null,   // 0-based; null for top-level fields
      //     fieldName: string,        // e.g. "consumption_batch_number_sponsor"
      //                               //      "consumed_material_number_sponsor"
      //                               //      "date_of_manufacture"
      //     value:     any            // the new value the user just submitted
      //   }
      const validatingField = {
        bowlIndex: isBowlField && currentBowlIndex >= 0 ? currentBowlIndex : null,
        rowIndex:  isBowlField && currentRowIndex  >= 0 ? currentRowIndex  : null,
        fieldName: isBowlField ? fieldName : editModalData.fieldKey,
        value:     valueToSave,
      };

      const requestPayload = {
        schrockenDeliveryScheduleID: batchDetails?.schrockenDeliveryScheduleID,
        schrockenDeliveryScheduleBatchID:
          batchDetails?.schrockenDeliveryScheduleBatchID,
        schrockenSLID: batchDetails?.schrockenSLID,
        kvpJsonEdited: updatedDocumentJSON,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        poNumber: poNumber,
        batchNumber: batchNumber,
        cmoBatchNumber: batchDetails?.cmoBatchNumber || cmoBatchNumber,
        documentType: docType,
        needsValidation: needsValidation,
        validatingField: validatingField,
      };
      const saveSource = axios.CancelToken.source();
      saveCancelRef.current = saveSource;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
        requestPayload,
        { headers: { "Content-Type": "application/json" }, cancelToken: saveSource.token }
      );

      // BCM-564: MW now validates via Corda before saving — check errorMessage first.
      // BCM-654: errorMessage can be a string OR an array of strings. Show ONLY
      // the error(s) that match the batch/material number the user just saved
      // (filtered by subject + value match). Filtering runs regardless of array
      // length, so a single error about a DIFFERENT batch/material does not
      // bleed into this popup either.
      if (response.data && response.data.errorMessage) {
        setSaving(false);
        const rawErr = response.data.errorMessage;
        let errArr = Array.isArray(rawErr)
          ? rawErr.filter(Boolean).map(String)
          : [String(rawErr)];

        const fk = editModalData.fieldKey || '';
        let expectedSubject = null;
        if (fk.includes('consumption_batch_number')) expectedSubject = 'batch';
        else if (fk.includes('consumed_material_number')) expectedSubject = 'material';
        else if (fk.includes('date_of_manufacture')) expectedSubject = 'dom';
        const valueJustSaved = editModalData.dataType === 'Date'
          ? (editModalData.saveValue || editModalData.currentValue || '')
          : (editModalData.currentValue || '');
        const v = String(valueJustSaved).trim();

        // Only narrow when we have enough context (popup field + value). If
        // either is missing we can't safely match — leave errArr untouched so
        // the user still sees what the backend returned.
        if (expectedSubject && v) {
          errArr = errArr.filter((msg) => {
            if (_getErrorSubject(msg) !== expectedSubject) return false;
            if (!_valueAppearsIn(msg, v)) return false;
            return true;
          });
        }

        setEditModalValidationError(errArr.join('\n'));
        return;
      }

      if (response.data.success && response.data.cordaResponse?.status) {
        const updatedApiData = {
          ...newApiData,
          noOfFieldsToBeReviewed: noOfFieldsToBeReviewed,
          noOfFieldsReviewed: noOfFieldsReviewed
        };

        // Remove isNew flags from local state after successful save
        // This ensures that after saving, rows/bowls are no longer considered "new"
        if (newApiData.bowls && Array.isArray(newApiData.bowls)) {
          newApiData.bowls.forEach((bowl) => {

            delete bowl.isNew;
            if (bowl.rows && Array.isArray(bowl.rows)) {
              bowl.rows.forEach((row) => {
                delete row.isNew;
              });
            }
          });
        }
        
        // Only update UI state after successful API call
        // BCM-654: store the clean edited doc (updatedApiData). Previously this wrapped
        // it as {newApiData, ...updatedApiData}, which added a stray "newApiData" key to
        // apiData — that junk then leaked into the validate/save payloads. updatedApiData
        // already contains every real field (spread of newApiData) plus the fresh counts.
        setApiData(updatedApiData);
        setEditedData(updatedApiData);
        // BCM-654: mark that an edit was saved this session so the OK-triggered validate
        // sends apiData under kvpJsonEdited (synchronous — no refresh race).
        setHasLocalEdits(true);
        
        setOpenEditModal(false);
        // BCM-654: clear stale Corda errors — re-validation runs when user clicks OK
        setCordaValidationErrors([]);
        if (props.onFieldSaveResult) {
          props.onFieldSaveResult({ message: `${editModalData.fieldLabel} is updated.` });
          setValidateAfterSave(true);
        } else {
          setSuccessDialogTitle("Updated Successfully");
          setSuccessMessage(`${editModalData.fieldLabel} is updated.`);
          setShowSuccessDialog(true);
        }
      } else {
        console.error(
          "Failed to save:",
          response.data.message || "Unknown error"
        );
        setEditModalValidationError(
          response.data.message || "Failed to update field. Please try again."
        );
      }
    } catch (error) {
      if (axios.isCancel(error)) return; // unmounted mid-request — leave state alone
      console.error(`Error saving ${docType} field:`, error);
      setEditModalValidationError(
        "An error occurred while saving. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle review complete and workflow reset with unified function
  const handleReviewAction = async (resetWorkflow = false) => {
    try {
      setMarkingReviewComplete(true);
      setMarkReviewValidationErrors([]);

      const existingData = apiData;
      const updatedDocumentJSON = {};
      
      Object.keys(existingData).forEach((fieldKey) => {
        if (
          fieldKey !== "bowls" &&
          fieldKey !== "pallets" &&
          typeof existingData[fieldKey] === "object" &&
          "value" in existingData[fieldKey]
        ) {
          const fieldData = existingData[fieldKey];
          // Fall back to kvpJsonOriginal's isMandatory when the current field data
          // doesn't carry one, so Mark Review Complete never drops the original flag.
          const originalIsMandatory = originalData?.[fieldKey]?.isMandatory;
          const resolvedIsMandatory =
            fieldData.isMandatory !== undefined ? fieldData.isMandatory : originalIsMandatory;
          updatedDocumentJSON[fieldKey] = {
            value: fieldData.value,
            confidence: fieldData.confidence,
            ...(fieldData.dataType && { dataType: fieldData.dataType }),
            ...(fieldData.needValidation !== undefined && {
              needValidation: fieldData.needValidation,
            }),
            ...(resolvedIsMandatory !== undefined && { isMandatory: resolvedIsMandatory }),
            ...(fieldData.reasonCode !== undefined && { reasonCode: fieldData.reasonCode }),
            ...(fieldData.comment !== undefined && { comment: fieldData.comment }),
            ...(fieldData.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI }),
          };
        } else if (
          fieldKey === "bowls" &&
          Array.isArray(existingData[fieldKey])
        ) {
          updatedDocumentJSON[fieldKey] = existingData[fieldKey];
        } else if (
          fieldKey === "pallets" &&
          Array.isArray(existingData[fieldKey])
        ) {
          updatedDocumentJSON[fieldKey] = existingData[fieldKey];
        }
      });

      [
        "document_type",
        "folder_name",
        "site_name",
        "filename",
        "s3_location",
        "noOfFieldsToBeReviewed",
        "noOfFieldsReviewed",
      ].forEach((field) => {
        if (existingData[field] !== undefined) {
          updatedDocumentJSON[field] = existingData[field];
        }
      });

      
      updatedDocumentJSON.markAsReviewed = true;

      const requestPayload = {
        schrockenDeliveryScheduleID: batchDetails?.schrockenDeliveryScheduleID,
        schrockenDeliveryScheduleBatchID:
          batchDetails?.schrockenDeliveryScheduleBatchID,
        schrockenSLID: batchDetails?.schrockenSLID,
        kvpJsonEdited: updatedDocumentJSON,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        poNumber: poNumber,
        batchNumber: batchNumber,
        cmoBatchNumber: batchDetails?.cmoBatchNumber || cmoBatchNumber,
        documentType: docType,
        ...(resetWorkflow && { resetWorkflow: true }),
        // Multi-shipment: documentType alone is ambiguous once more than one concurrent
        // shipment shares it — this identifies which one is being updated. Omitted entirely
        // for the single-shipment case (activeShipmentDeliveryNumber is null), so existing
        // backend behavior for every current document type is unchanged.
        ...(activeShipmentDeliveryNumber && { deliveryNumber: activeShipmentDeliveryNumber }),
      };

      const saveSource = axios.CancelToken.source();
      saveCancelRef.current = saveSource;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
        requestPayload,
        { headers: { "Content-Type": "application/json" }, cancelToken: saveSource.token }
      );

      // BCM-348: check validationErrors from backend before proceeding
      if (
        response.data.validation?.validationErrors &&
        response.data.validation.validationErrors.length > 0
      ) {
        setMarkReviewValidationErrors(response.data.validation.validationErrors);
        // Refresh data so backend-updated kvpJsonEdited (needValidation=true fields, markAsReviewed=false) loads on UI
        if (onDataRefresh) onDataRefresh();
        return;
      }

      if (response.data.success && response.data.cordaResponse?.status) {
        if (resetWorkflow) {
          setSuccessDialogTitle("Workflow reset successfully.");
          setSuccessMessage("Process flow changes have been reinitiated.");
        } else {
          setSuccessDialogTitle("Marked review as complete.");
          setSuccessMessage("Process flow change is initiated.");
        }
        
        // FLAG-GATED (Goods Receipt only): when the caller passes onReviewActionResult
        // it owns the success UI and the refetch, so the built-in success dialog and
        // the immediate onDataRefresh are skipped. Batch Execution passes no such prop
        // and is completely unaffected — it keeps the dialog + onDataRefresh below.
        if (props.onReviewActionResult) {
        // Keep the "Processing…" loading state up for 5s on success (the finally
        // block below clears markingReviewComplete/updateRerunLoading only after
        // this resolves) before surfacing the success toast/dialog — so the user
        // sees a brief loading state rather than an instant success flip.
        //await delay(15000);
        if (!isMountedRef.current) return;
        
          props.onReviewActionResult({ success: true, resetWorkflow });
        } else {
          setShowSuccessDialog(true);
          // Optionally refresh the data
          if (onDataRefresh) {
            onDataRefresh();
          }
        }
      } else {
        const action = resetWorkflow ? "reset workflow" : "mark review as complete";
        console.error(
          `Failed to ${action}:`,
          response.data.message || "Unknown error"
        );
        if (props.onReviewActionResult) {
          props.onReviewActionResult({ success: false, resetWorkflow });
        }
      }
    } catch (error) {
      if (axios.isCancel(error)) return; // unmounted mid-request — leave state alone
      const action = resetWorkflow ? "resetting workflow" : "marking review as complete";
      console.error(`Error ${action}:`, error);
      if (props.onReviewActionResult) {
        props.onReviewActionResult({ success: false, resetWorkflow, error });
      }
    } finally {
      setMarkingReviewComplete(false);
    }
  };

  // Handle close with warning check
  const handleClose = () => {
    
    if (!isFeaturesAccessLoaded) {
      onClose();
      return;
    }
    
    const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
    const reviewed = apiData.noOfFieldsReviewed || 0;
    const isMarkedAsReviewed = apiData.markAsReviewed === true;
    const isCategoryEditable = isDocumentCategoryEditable();
    const hasPartialShipping = batchDetails?.partialShippingDetails?.length > 0;

    // Scenario 1: all fields reviewed but document not yet marked as reviewed
    const shouldWarnNotMarked = canReviewAndEdit() && !showingOriginal && (toBeReviewed === 0 && reviewed > 0) && !isMarkedAsReviewed && isCategoryEditable;
    // Scenario 2: "Update & Re-run" button is visible AND enabled (reviewed > 0, marked as reviewed, no partial shipping, user can review/edit)
    const shouldWarnUpdateRerun = canReviewAndEdit() && !showingOriginal && isMarkedAsReviewed && reviewed > 0 && !hasPartialShipping && isCategoryEditable;

    const shouldWarn = shouldWarnNotMarked || shouldWarnUpdateRerun;
    
    if (shouldWarnUpdateRerun) {
      setShowUpdateRerunReminderDialog(true);
    } else if (shouldWarnNotMarked) {
      setShowExitWarning(true);
    } else {
      onClose();
    }
  };

  // Populate the ref with handleClose function so parent can call it
  useEffect(() => {
    if (handleCloseRef) {
      handleCloseRef.current = handleClose;
    }
  }, [handleCloseRef, apiData.noOfFieldsToBeReviewed, apiData.noOfFieldsReviewed, showingOriginal, featuresAccess, isFeaturesAccessLoaded]);

  // Helper function to remove isNew flag from bowls and rows (local UI flag, not sent to backend)
  // Also removes isMandatory from fields in new rows/bowls
  // Converts empty strings to null for consumption details fields
  // Merges existing rows with original data to preserve all fields
  const cleanBowlsForBackend = (bowls, originalBowls = null) => {
    if (!Array.isArray(bowls)) return bowls;
    return bowls.map((bowl, bowlIndex) => {
      const cleanedBowl = { ...bowl };
      const isBowlNew = cleanedBowl.isNew === true;
      // Remove isNew flag from bowl
      delete cleanedBowl.isNew;
      
      // Find corresponding original bowl for merging
      let originalBowl = null;
      if (originalBowls && Array.isArray(originalBowls)) {
        const bowlId = cleanedBowl.ID || cleanedBowl.bowl?.value;
        if (bowlId) {
          originalBowl = originalBowls.find(b => 
            (b.ID === bowlId) || (b.bowl?.value === bowlId)
          );
        }
      }
      
      // Clean rows if they exist
      if (cleanedBowl.rows && Array.isArray(cleanedBowl.rows)) {
        cleanedBowl.rows = cleanedBowl.rows.map((row) => {
          const cleanedRow = { ...row };
          const isRowNew = cleanedRow.isNew === true || isBowlNew;
          const rowId = cleanedRow.row_id;
          
          // Find corresponding original row for merging (for existing rows only)
          let originalRow = null;
          if (!isRowNew && rowId && originalBowl && originalBowl.rows && Array.isArray(originalBowl.rows)) {
            originalRow = originalBowl.rows.find(r => r.row_id === rowId);
          }
          
          // If this is an existing row, merge with original row to preserve all fields
          if (originalRow && !isRowNew) {
            // Start with original row data
            const mergedRow = { ...originalRow };
            
            // Override with current row data (preserving all original fields)
            Object.keys(cleanedRow).forEach((key) => {
              if (key !== "row_id" && key !== "isNew") {
                mergedRow[key] = cleanedRow[key];
              }
            });
            
            // Copy row_id from cleaned row
            if (cleanedRow.row_id) {
              mergedRow.row_id = cleanedRow.row_id;
            }
            
            // Process all fields in the merged row
            Object.keys(mergedRow).forEach((key) => {
              if (
                key !== "row_id" &&
                typeof mergedRow[key] === "object" &&
                mergedRow[key] !== null &&
                "value" in mergedRow[key]
              ) {
                // Convert empty strings to null for consumption details fields
                if (typeof mergedRow[key].value === 'string' && mergedRow[key].value.trim() === '') {
                  mergedRow[key].value = null;
                }
                
                // For merged rows (existing rows), ensure edited fields have correct flags
                // If field was edited (needValidation = false and has reasonCode/comment), preserve original isMandatory
                // But only if field is visible on UI (we only send needValidation/isMandatory for visible fields)
                // Preserve isMandatory value (true or false) from original field data
                const currentField = mergedRow[key];
                const isFieldVisibleOnUI = currentField.isVisibleOnUI !== false;
                
                if (currentField.needValidation === false && (currentField.reasonCode || currentField.comment)) {
                  // Field was edited: needValidation = false
                  // Preserve isMandatory value (true or false) if it exists
                  // Only preserve these flags if field is visible on UI
                  if (isFieldVisibleOnUI) {
                    mergedRow[key] = {
                      ...currentField,
                      needValidation: false,
                      // Preserve isMandatory value (true or false) if it exists
                      ...(currentField.isMandatory !== undefined && { isMandatory: currentField.isMandatory })
                    };
                  } else {
                    // Field was edited but not visible - remove needValidation/isMandatory but keep reasonCode/comment
                    const { isMandatory, needValidation, ...fieldWithoutFlags } = currentField;
                    mergedRow[key] = {
                      ...fieldWithoutFlags,
                      value: currentField.value,
                      confidence: currentField.confidence,
                      ...(currentField.dataType && { dataType: currentField.dataType }),
                      ...(currentField.reasonCode !== undefined && { reasonCode: currentField.reasonCode }),
                      ...(currentField.comment !== undefined && { comment: currentField.comment }),
                      ...(currentField.isVisibleOnUI !== undefined && { isVisibleOnUI: currentField.isVisibleOnUI }),
                    };
                  }
                } else if (!isFieldVisibleOnUI && (currentField.needValidation !== undefined || currentField.isMandatory !== undefined)) {
                  // Field is not visible and has needValidation/isMandatory - remove them
                  const { isMandatory, needValidation, ...fieldWithoutFlags } = currentField;
                  mergedRow[key] = {
                    ...fieldWithoutFlags,
                    value: currentField.value,
                    confidence: currentField.confidence,
                    ...(currentField.dataType && { dataType: currentField.dataType }),
                    ...(currentField.reasonCode !== undefined && { reasonCode: currentField.reasonCode }),
                    ...(currentField.comment !== undefined && { comment: currentField.comment }),
                    ...(currentField.isVisibleOnUI !== undefined && { isVisibleOnUI: currentField.isVisibleOnUI }),
                  };
                }
              }
            });
            
            return mergedRow;
          } else {
            // For new rows, process as before
            // Remove isNew flag from row
            delete cleanedRow.isNew;
            
            // Fields that should have isMandatory: true and needValidation: true for new rows
            const mandatoryFieldsForNewRows = [
              'consumption_batch_number_sponsor',
              'actual_material_quantity_consumed'
            ];
            
            // Process all fields in the row
            Object.keys(cleanedRow).forEach((key) => {
              if (
                key !== "row_id" &&
                typeof cleanedRow[key] === "object" &&
                cleanedRow[key] !== null &&
                "value" in cleanedRow[key]
              ) {
                // Convert empty strings to null for consumption details fields
                if (typeof cleanedRow[key].value === 'string' && cleanedRow[key].value.trim() === '') {
                  cleanedRow[key].value = null;
                }
                
                // If row is new, set isMandatory and needValidation based on field type and visibility
                // Only visible fields should have needValidation and isMandatory
                if (isRowNew) {
                  const isMandatoryField = mandatoryFieldsForNewRows.includes(key);
                  // Check if field is visible on UI (true if undefined or explicitly true)
                  const isFieldVisibleOnUI = cleanedRow[key].isVisibleOnUI !== false;
                  
                  if (isFieldVisibleOnUI) {
                    // For visible fields: preserve needValidation if it was explicitly set to false (field was edited)
                    // Otherwise set to true for visible fields
                    const preservedNeedValidation = cleanedRow[key].needValidation === false ? false : true;
                    
                    if (isMandatoryField) {
                      // Mandatory visible fields: set both isMandatory and needValidation
                      cleanedRow[key] = {
                        ...cleanedRow[key],
                        isMandatory: true,
                        needValidation: preservedNeedValidation
                      };
                    } else {
                      // Non-mandatory visible fields: only set needValidation, remove isMandatory
                      const { isMandatory, ...fieldWithoutMandatory } = cleanedRow[key];
                      cleanedRow[key] = {
                        ...fieldWithoutMandatory,
                        needValidation: preservedNeedValidation
                      };
                    }
                  } else {
                    // Fields not visible on UI: remove both needValidation and isMandatory
                    const { isMandatory, needValidation, ...fieldWithoutFlags } = cleanedRow[key];
                    cleanedRow[key] = {
                      ...fieldWithoutFlags
                    };
                  }
                } else {
                  // For existing rows, process edited fields
                  const currentField = cleanedRow[key];
                  const isFieldVisibleOnUI = currentField.isVisibleOnUI !== false;
                  
                  if (currentField.needValidation === false && (currentField.reasonCode || currentField.comment)) {
                    // Field was edited: needValidation = false
                    // Preserve isMandatory value (true or false) if it exists
                    // Only preserve these flags if field is visible on UI
                    if (isFieldVisibleOnUI) {
                      cleanedRow[key] = {
                        ...currentField,
                        needValidation: false,
                        // Preserve isMandatory value (true or false) if it exists
                        ...(currentField.isMandatory !== undefined && { isMandatory: currentField.isMandatory })
                      };
                    } else {
                      // Field was edited but not visible - remove needValidation/isMandatory but keep reasonCode/comment
                      const { isMandatory, needValidation, ...fieldWithoutFlags } = currentField;
                      cleanedRow[key] = {
                        ...fieldWithoutFlags,
                        value: currentField.value,
                        confidence: currentField.confidence,
                        ...(currentField.dataType && { dataType: currentField.dataType }),
                        ...(currentField.reasonCode !== undefined && { reasonCode: currentField.reasonCode }),
                      ...(currentField.comment !== undefined && { comment: currentField.comment }),
                        ...(currentField.isVisibleOnUI !== undefined && { isVisibleOnUI: currentField.isVisibleOnUI }),
                      };
                    }
                  } else if (!isFieldVisibleOnUI) {
                    // Field is not visible and not edited - remove needValidation/isMandatory
                    const { isMandatory, needValidation, ...fieldWithoutFlags } = currentField;
                    cleanedRow[key] = {
                      ...fieldWithoutFlags,
                      value: currentField.value,
                      confidence: currentField.confidence,
                      ...(currentField.dataType && { dataType: currentField.dataType }),
                      ...(currentField.reasonCode !== undefined && { reasonCode: currentField.reasonCode }),
                        ...(currentField.comment !== undefined && { comment: currentField.comment }),
                      ...(currentField.isVisibleOnUI !== undefined && { isVisibleOnUI: currentField.isVisibleOnUI }),
                    };
                  }
                  // If field is visible and not edited, keep as is (needValidation/isMandatory preserved from original)
                }
              }
            });
            
            return cleanedRow;
          }
        });
      }
      return cleanedBowl;
    });
  };

  // Function to save data to API (reusable for both edit and delete operations)
  const saveDataToAPI = async (dataToSave, successMessage = "Data saved successfully!") => {
    try {
      const existingData = dataToSave;
      const updatedDocumentJSON = {};
      Object.keys(dataToSave).forEach((fieldKey) => {
        if (
          fieldKey !== "bowls" &&
          fieldKey !== "pallets" &&
          typeof dataToSave[fieldKey] === "object" &&
          "value" in dataToSave[fieldKey]
        ) {
          const fieldData = dataToSave[fieldKey];
          // Fall back to kvpJsonOriginal's isMandatory when this field's copy doesn't carry one.
          const originalIsMandatoryForSave = originalData?.[fieldKey]?.isMandatory;
          const resolvedIsMandatoryForSave =
            fieldData.isMandatory !== undefined ? fieldData.isMandatory : originalIsMandatoryForSave;
          updatedDocumentJSON[fieldKey] = {
            value: fieldData.value,
            confidence: fieldData.confidence,
            ...(fieldData.dataType && { dataType: fieldData.dataType }),
            ...(fieldData.needValidation !== undefined && {
              needValidation: fieldData.needValidation,
            }),
            ...(resolvedIsMandatoryForSave !== undefined && {
              isMandatory: resolvedIsMandatoryForSave,
            }),
            ...(fieldData.reasonCode !== undefined && { reasonCode: fieldData.reasonCode }),
            ...(fieldData.comment !== undefined && { comment: fieldData.comment }),
            ...(fieldData.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI }),
          };
        } else if (
          fieldKey === "bowls" &&
          Array.isArray(dataToSave[fieldKey])
        ) {
          // Remove isNew flag before sending to backend (it's only for local UI state)
          // Pass original bowls data to preserve all fields for existing rows
          const originalBowlsData = originalData[fieldKey];
          updatedDocumentJSON[fieldKey] = cleanBowlsForBackend(dataToSave[fieldKey], originalBowlsData);
        } else if (
          fieldKey === "pallets" &&
          Array.isArray(dataToSave[fieldKey])
        ) {
          updatedDocumentJSON[fieldKey] = dataToSave[fieldKey];
        }
      });

      [
        "document_type",
        "folder_name",
        "site_name",
        "filename",
        "s3_location",
      ].forEach((field) => {
        if (existingData[field]) {
          updatedDocumentJSON[field] = existingData[field];
        }
      });

      // Recalculate counts from the updated document structure
      let noOfFieldsToBeReviewed = 0;
      let noOfFieldsReviewed = 0;

      // Count regular fields - only count fields with needValidation: true AND isMandatory: true
      Object.keys(updatedDocumentJSON).forEach((key) => {
        if (
          key !== "bowls" &&
          key !== "pallets" &&
          key !== "noOfFieldsToBeReviewed" &&
          key !== "noOfFieldsReviewed" &&
          typeof updatedDocumentJSON[key] === "object" &&
          "value" in updatedDocumentJSON[key] &&
          updatedDocumentJSON[key].needValidation !== undefined
        ) {
          // Only count mandatory fields in noOfFieldsToBeReviewed
          if (updatedDocumentJSON[key].needValidation === true && updatedDocumentJSON[key].isMandatory === true) {
            noOfFieldsToBeReviewed += 1;
          } else if (
            updatedDocumentJSON[key].needValidation === false &&
            // updatedDocumentJSON[key].isMandatory === true &&
            (updatedDocumentJSON[key].reasonCode || updatedDocumentJSON[key].comment)
          ) {
            // Only count as reviewed if it has been explicitly edited (has reasonCode or comment)
            noOfFieldsReviewed += 1;
          }
        }
      });

      // Count bowl fields - only count fields with needValidation: true AND isMandatory: true
      if (Array.isArray(updatedDocumentJSON.bowls)) {
        updatedDocumentJSON.bowls.forEach((bowl) => {
          if (bowl.rows && Array.isArray(bowl.rows)) {
            bowl.rows.forEach((row) => {
              Object.keys(row).forEach((key) => {
                if (
                  key !== "row_id" &&
                  typeof row[key] === "object" &&
                  "value" in row[key] &&
                  row[key].needValidation !== undefined
                ) {
                  // Only count mandatory fields in noOfFieldsToBeReviewed
                  if (row[key].needValidation === true && row[key].isMandatory === true) {
                    noOfFieldsToBeReviewed += 1;
                  } else if (
                    row[key].needValidation === false &&
                    // row[key].isMandatory === true &&
                    (row[key].reasonCode || row[key].comment)
                  ) {
                    // Only count as reviewed if it has been explicitly edited (has reasonCode or comment)
                    noOfFieldsReviewed += 1;
                  }
                }
              });
            });
          }
        });
      }

      updatedDocumentJSON.noOfFieldsToBeReviewed = noOfFieldsToBeReviewed;
      updatedDocumentJSON.noOfFieldsReviewed = noOfFieldsReviewed;

      const requestPayload = {
        schrockenDeliveryScheduleID: batchDetails?.schrockenDeliveryScheduleID,
        schrockenDeliveryScheduleBatchID:
          batchDetails?.schrockenDeliveryScheduleBatchID,
        schrockenSLID: batchDetails?.schrockenSLID,
        kvpJsonEdited: updatedDocumentJSON,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        poNumber: poNumber,
        batchNumber: batchNumber,
        cmoBatchNumber: batchDetails?.cmoBatchNumber || cmoBatchNumber,
        documentType: docType,
      };
      const saveSource = axios.CancelToken.source();
      saveCancelRef.current = saveSource;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
        requestPayload,
        { headers: { "Content-Type": "application/json" }, cancelToken: saveSource.token }
      );

      if (response.data.success && response.data.cordaResponse?.status) {
        // Update dataToSave with the calculated counts
        // const updatedDataToSave = {
        //   ...dataToSave,
        //   noOfFieldsToBeReviewed: noOfFieldsToBeReviewed,
        //   noOfFieldsReviewed: noOfFieldsReviewed
        // };
        
        // Remove isNew flags from local state after successful save
        // This ensures that after saving, rows/bowls are no longer considered "new"
        if (dataToSave.bowls && Array.isArray(dataToSave.bowls)) {
          dataToSave.bowls.forEach((bowl) => {
            delete bowl.isNew;
            if (bowl.rows && Array.isArray(bowl.rows)) {
              bowl.rows.forEach((row) => {
                delete row.isNew;
              });
            }
          });
        }
        
        setApiData(dataToSave);
        setEditedData(dataToSave);
        // BCM-654: a save through this generic helper is also an edit — mark it so the
        // OK-triggered validate sends apiData under kvpJsonEdited.
        setHasLocalEdits(true);

        if (props.onFieldSaveResult) {
          props.onFieldSaveResult({ message: successMessage });
          setValidateAfterSave(true);
        } else {
          setSuccessMessage(successMessage);
          setShowSuccessDialog(true);
        }
        return true;
      } else {
        console.error(
          "Failed to save:",
          response.data.message || "Unknown error"
        );
        throw new Error(response.data.message || "Failed to save data. Please try again.");
      }
    } catch (error) {
      if (axios.isCancel(error)) throw error; // unmounted mid-request — let the caller no-op
      console.error(`Error saving data:`, error);
      throw error;
    }
  };

  // Handler to show delete confirmation dialog for batch
  const handleDeleteBatchClick = (bowlIndex, rowIndex, batchNumber, batchLabel, isStatic = false) => {
    if (isStatic) {
      return;
    }
    
    // Safely get the row to delete
    if (!apiData.bowls || !apiData.bowls[bowlIndex] || !apiData.bowls[bowlIndex].rows || !apiData.bowls[bowlIndex].rows[rowIndex]) {
      console.error("Row not found for deletion");
      return;
    }
    
    const rowToDelete = apiData.bowls[bowlIndex].rows[rowIndex];
    const isNew = rowToDelete?.isNew === true;
    
    // If it's a new row (not saved yet), delete directly from UI without API call
    if (isNew) {
      handleDeleteBatch(bowlIndex, rowIndex, 1, false);
      return;
    }
    
    const bowl = apiData.bowls[bowlIndex];
    const consumptionDetailsLabel = bowl ? `Consumption Details ${bowl.bowl?.value || bowl.ID}` : "Consumption Details";
    
    // Helper to get material number value from a row (for delete confirmation dialog).
    // Sponsor field only — CMO field is no longer visible on UI, so the dialog must
    // show the BMS material the user is actually deleting.
    const getMaterialNumberValue = (row) => {
      return (row?.consumed_material_number_sponsor && typeof row.consumed_material_number_sponsor === 'object' && row.consumed_material_number_sponsor.value) ||
             null;
    };
    
    // Get material number from current row, or fallback to first row if not found
    let materialNumber = getMaterialNumberValue(rowToDelete);
    if (!materialNumber && bowl?.rows && bowl.rows.length > 0 && rowIndex > 0) {
      // For rows after the first one, material number is stored in the first row
      const firstRow = bowl.rows[0];
      materialNumber = getMaterialNumberValue(firstRow);
    }
    materialNumber = materialNumber || "N/A";
    
    const batchNumberValue = (rowToDelete?.consumption_batch_number_sponsor && typeof rowToDelete.consumption_batch_number_sponsor === 'object' && rowToDelete.consumption_batch_number_sponsor.value) || "N/A";
    
    setDeleteConfirmData({
      type: "batch",
      bowlIndex,
      rowIndex,
      batchNumber: 1,
      consumptionDetailsLabel,
      materialNumber,
      batchNumberValue,
      quantity: "", // Not needed for batch deletion popup
    });
    setShowDeleteConfirmDialog(true);
  };
  
  // Handler to delete consumed batch (called after confirmation)
  const handleDeleteBatch = (bowlIndex, rowIndex, batchNumber, isStatic = false) => {
    if (isStatic) {
      return null;
    }

    const newApiData = JSON.parse(JSON.stringify(apiData));
    if (!newApiData.bowls || !newApiData.bowls[bowlIndex] || !newApiData.bowls[bowlIndex].rows || !newApiData.bowls[bowlIndex].rows[rowIndex]) {
      return null;
    }

    newApiData.bowls[bowlIndex].rows.splice(rowIndex, 1);
    
    setApiData(newApiData);
    setEditedData(newApiData);
    return newApiData;
  };

  // Helper to check if bowl exists in originalData
  const isBowlFromBackend = (bowlIndex) => {
    const currentBowl = apiData.bowls?.[bowlIndex];
    if (!currentBowl) {
      return false;
    }
    
    const bowlId = currentBowl.ID || currentBowl.bowl?.value;
    if (!bowlId) {
      return false;
    }
    
    if (!originalData.bowls || !Array.isArray(originalData.bowls)) {
      return false;
    }
    
    return originalData.bowls.some(bowl => (bowl.ID === bowlId) || (bowl.bowl?.value === bowlId));
  };

  // Handler to show delete confirmation dialog for consumption section
  const handleDeleteConsumptionSectionClick = (bowlIndex, isStatic = false) => {
    if (isStatic) {
      return;
    }
    
    const bowl = apiData.bowls?.[bowlIndex];
    const isNew = bowl?.isNew === true;
    
    if (isNew) {
      handleDeleteConsumptionSection(bowlIndex, false);
      return;
    }
    
    const consumptionDetailsLabel = bowl ? `Consumption Details ${bowl.bowl?.value || bowl.ID}` : "Consumption Details";
    
    setDeleteConfirmData({
      type: "consumptionSection",
      bowlIndex,
      rowIndex: null,
      batchNumber: null,
      consumptionDetailsLabel,
    });
    setShowDeleteConfirmDialog(true);
  };
  
  // Helper function to renumber bowls sequentially
  // ID should be numeric (1, 2, 3...), bowl.value should be alphabetic (A, B, C...)
  const renumberBowls = (bowls) => {
    if (!bowls || !Array.isArray(bowls)) return bowls;
    
    return bowls.map((bowl, index) => {
      const numericId = index + 1; // 1, 2, 3, 4, etc.
      const alphabeticValue = String.fromCharCode(65 + index); // A=65, B=66, C=67, etc.
      const updatedBowl = { ...bowl };
      updatedBowl.ID = numericId;
      
      // Update bowl.value if it exists (alphabetic)
      if (updatedBowl.bowl && typeof updatedBowl.bowl === 'object') {
        updatedBowl.bowl = {
          ...updatedBowl.bowl,
          value: alphabeticValue
        };
      } else {
        updatedBowl.bowl = { value: alphabeticValue, confidence: "0", dataType: "String" };
      }
      
      return updatedBowl;
    });
  };

  // Handler to delete consumption section (bowl) - called after confirmation
  const handleDeleteConsumptionSection = (bowlIndex, isStatic = false) => {
    if (isStatic) {
      return null;
    }

    const newApiData = JSON.parse(JSON.stringify(apiData));
    if (!newApiData.bowls || !newApiData.bowls[bowlIndex]) {
      return null;
    }
    
    // Remove the bowl at the specified index
    newApiData.bowls.splice(bowlIndex, 1);
    
    // Renumber remaining bowls sequentially (A, B, C, D...)
    newApiData.bowls = renumberBowls(newApiData.bowls);
    
    setApiData(newApiData);
    setEditedData(newApiData);
    return newApiData;
  };

  // Handler to confirm deletion
  const handleConfirmDelete = async () => {
    try {
      setDeleteError("");
      setSaving(true);
      
      // Check if the item being deleted is new (not saved yet)
      let isNewItem = false;
      if (deleteConfirmData.type === "batch") {
        const rowToDelete = apiData.bowls?.[deleteConfirmData.bowlIndex]?.rows?.[deleteConfirmData.rowIndex];
        isNewItem = rowToDelete?.isNew === true;
      } else if (deleteConfirmData.type === "consumptionSection") {
        const bowlToDelete = apiData.bowls?.[deleteConfirmData.bowlIndex];
        isNewItem = bowlToDelete?.isNew === true;
      }
      
      let updatedData = null;
      if (deleteConfirmData.type === "batch") {
        updatedData = handleDeleteBatch(deleteConfirmData.bowlIndex, deleteConfirmData.rowIndex, deleteConfirmData.batchNumber, false);
      } else if (deleteConfirmData.type === "consumptionSection") {
        updatedData = handleDeleteConsumptionSection(deleteConfirmData.bowlIndex, false);
      }
      
      if (!updatedData) {
        throw new Error("Failed to perform deletion operation");
      }
      
      const deleteMessage = deleteConfirmData.type === "batch" 
        ? `Batch deleted successfully!`
        : `${deleteConfirmData.consumptionDetailsLabel} deleted successfully!`;
      
      // If it's a new item (not saved yet), just update UI without API call
      // Otherwise, save to API
      if (isNewItem) {
        // Just update the state, no API call needed
        // Close dialog and show success immediately
        setShowDeleteConfirmDialog(false);
        setDeleteConfirmData({
          type: "",
          bowlIndex: null,
          rowIndex: null,
          batchNumber: null,
          consumptionDetailsLabel: "",
          materialNumber: "",
          batchNumberValue: "",
          quantity: "",
        });
        if (props.onFieldSaveResult) {
          // localOnly: nothing was saved server-side — the caller must not refresh
          // (it would drop other unsaved new rows).
          props.onFieldSaveResult({ message: deleteMessage, localOnly: true });
          setValidateAfterSave(true);
        } else {
          setSuccessMessage(deleteMessage);
          setShowSuccessDialog(true);
        }
      } else {
        // Item was saved before, need to call API to delete it
        // Keep dialog open with loader until API call completes
        // saveDataToAPI will handle showing the success dialog
        await saveDataToAPI(updatedData, deleteMessage);
        
        // After successful API call, close the delete dialog
        // (success dialog is already shown by saveDataToAPI)
        setShowDeleteConfirmDialog(false);
        setDeleteConfirmData({
          type: "",
          bowlIndex: null,
          rowIndex: null,
          batchNumber: null,
          consumptionDetailsLabel: "",
          materialNumber: "",
          batchNumberValue: "",
          quantity: "",
        });
      }
    } catch (error) {
      console.error("Error deleting:", error);
      // Keep dialog open and show error message
      setDeleteError(
        error.message || "An error occurred while deleting. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Handler to add new consumed batch (adds a new row)
  const handleAddNewConsumedBatch = (bowlIndex, rowIndex, isStatic = false) => {
    if (isStatic) {
      return;
    }

    const newApiData = JSON.parse(JSON.stringify(apiData));
    if (!newApiData.bowls || !newApiData.bowls[bowlIndex]) {
      return;
    }
    
    if (!newApiData.bowls[bowlIndex].rows) {
      newApiData.bowls[bowlIndex].rows = [];
    }
    
    const lastRow = newApiData.bowls[bowlIndex].rows.length > 0 
      ? newApiData.bowls[bowlIndex].rows[newApiData.bowls[bowlIndex].rows.length - 1]
      : null;
    
    const nextRowId = newApiData.bowls[bowlIndex].rows.length > 0
      ? Math.max(...newApiData.bowls[bowlIndex].rows.map(r => r.row_id || 0)) + 1
      : 1;
    
    // Get reference row to copy all field structure from (use first row if available)
    const referenceRow = newApiData.bowls[bowlIndex].rows.length > 0 
      ? newApiData.bowls[bowlIndex].rows[0]
      : null;
    
    const newRow = {
      row_id: nextRowId,
      isNew: true
    };
    
    // Copy ALL fields from reference row (or lastRow if it exists) to ensure all fields are included
    const rowToCopyFrom = lastRow || referenceRow;
    
    // Fields that should be mandatory for new consumed batches
    const mandatoryFieldsForNewBatches = [
      'consumption_batch_number_sponsor',
      'consumption_batch_number',
      'actual_material_quantity_consumed'
    ];
    
    if (rowToCopyFrom) {
      // Get all field names from the row to copy from
      Object.keys(rowToCopyFrom).forEach((key) => {
        if (key !== "row_id" && key !== "isNew" && typeof rowToCopyFrom[key] === "object" && "value" in rowToCopyFrom[key]) {
          // Copy the field structure but set value to empty
          const fieldData = rowToCopyFrom[key];
          // Check if field is visible on UI (true if undefined or explicitly true)
          const isFieldVisibleOnUI = fieldData.isVisibleOnUI !== false;
          const isMandatoryField = mandatoryFieldsForNewBatches.includes(key);
          
          // Only set needValidation and isMandatory for fields visible on UI
          if (isFieldVisibleOnUI) {
            // Set needValidation: true for visible fields (so edit icon appears)
            const shouldNeedValidation = true;
            
            newRow[key] = {
              confidence: "0",
              dataType: fieldData.dataType || "String",
              needValidation: shouldNeedValidation,
              value: "",
              // Only set isMandatory for mandatory fields that are visible on UI
              ...(isMandatoryField && { isMandatory: true }),
              ...(fieldData.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI })
            };
          } else {
            // For non-visible fields, don't set needValidation or isMandatory
            newRow[key] = {
              confidence: "0",
              dataType: fieldData.dataType || "String",
              value: "",
              ...(fieldData.isVisibleOnUI !== undefined && { isVisibleOnUI: fieldData.isVisibleOnUI })
            };
          }
        }
      });
    } else {
      // Fallback: create default fields if no reference row exists
      newRow.consumption_batch_number_sponsor = {
        confidence: "0",
        dataType: "String",
        needValidation: true,
        value: "",
        isMandatory: true,
        isVisibleOnUI: true
      };
      newRow.actual_material_quantity_consumed = {
        confidence: "0",
        dataType: "Numeric",
        needValidation: true,
        value: "",
        isMandatory: true,
        isVisibleOnUI: true
      };
      newRow.consumed_material_number_sponsor = {
        confidence: "0",
        dataType: "String",
        needValidation: true, // Visible field, needs edit icon
        value: "",
        isVisibleOnUI: true
      };
    }
    
    newApiData.bowls[bowlIndex].rows.push(newRow);

    setApiData(newApiData);
    setEditedData(newApiData);
  };

  // Handler to add new consumed material (new consumption details section)
  const handleAddNewConsumedMaterial = () => {
    const newApiData = JSON.parse(JSON.stringify(apiData));
    if (!newApiData.bowls) {
      newApiData.bowls = [];
    }

    // Find max numeric ID and max alphabetic value for bowl.value
    let maxNumericId = 0;
    let maxBowlCharCode = 64;
    
    newApiData.bowls.forEach(bowl => {
      // Check numeric ID
      const numericId = typeof bowl.ID === 'number' ? bowl.ID : 
                        (typeof bowl.ID === 'string' && !isNaN(parseInt(bowl.ID)) ? parseInt(bowl.ID) : 0);
      if (numericId > maxNumericId) {
        maxNumericId = numericId;
      }
      
      // Check alphabetic bowl.value
      const bowlValue = bowl.bowl?.value;
      if (bowlValue && typeof bowlValue === 'string' && bowlValue.length === 1) {
        const charCode = bowlValue.charCodeAt(0);
        if (charCode >= 65 && charCode <= 90) {
          if (charCode > maxBowlCharCode) {
            maxBowlCharCode = charCode;
          }
        }
      }
    });

    const nextNumericId = maxNumericId + 1; // 1, 2, 3, 4, etc.
    const nextAlphabeticValue = String.fromCharCode(maxBowlCharCode + 1); // A, B, C, D, etc.

    // Collect all field names from existing rows to ensure consistency
    const allFieldNames = new Set();
    newApiData.bowls.forEach(bowl => {
      if (bowl.rows && Array.isArray(bowl.rows)) {
        bowl.rows.forEach(row => {
          Object.keys(row).forEach(key => {
            if (key !== "row_id" && key !== "isNew" && typeof row[key] === "object" && "value" in row[key]) {
              allFieldNames.add(key);
            }
          });
        });
      }
    });

    // Create new row with all fields from existing rows, but with empty values
    const newRow = {
      row_id: 1
    };

    // Get a reference row to copy field structure from (use first row from first bowl if available)
    let referenceRow = null;
    if (newApiData.bowls.length > 0 && newApiData.bowls[0].rows && newApiData.bowls[0].rows.length > 0) {
      referenceRow = newApiData.bowls[0].rows[0];
    }

    // Add all fields with empty values, preserving structure from reference row
    allFieldNames.forEach(fieldName => {
      // Fields that should be mandatory even in new rows
      const mandatoryFieldsForNewRows = [
        'consumption_batch_number_sponsor',
        'consumption_batch_number',
        'actual_material_quantity_consumed'
      ];
      
      if (referenceRow && referenceRow[fieldName] && typeof referenceRow[fieldName] === "object") {
        // Copy structure from reference row but set value to empty
        // Remove isMandatory, reasonCode, and comment for new rows - they should not show mandatory indicator or info icon
        // EXCEPT for consumption_batch_number_sponsor, consumption_batch_number, and actual_material_quantity_consumed
        // BCM-654: also strip autoPopulated — a brand-new row is a fresh manual
        // entry, NOT an inventory-derived value. autoPopulated must only ever come
        // from the backend on the rows it actually populated; we never inherit it
        // from the reference row, otherwise the new batch/material/qty would wrongly
        // show NA + no pencil.
        // BCM-1077: autoPopulatedFromBOM is stripped for the same reason and was
        // missed here. Inheriting it from the reference row made a brand-new
        // material show the "auto-populated based on the PO BOM" note and a
        // disabled pencil, for a value the user had just chosen to enter by hand.
        const {
          isMandatory, reasonCode, comment, autoPopulated, autoPopulatedFromBOM,
          ...fieldWithoutMandatory
        } = referenceRow[fieldName];
        
        // Check if field is visible on UI (true if undefined or explicitly true)
        const isFieldVisibleOnUI = referenceRow[fieldName].isVisibleOnUI !== false;
        const isMandatoryField = mandatoryFieldsForNewRows.includes(fieldName);
        
        // Only set needValidation and isMandatory for fields visible on UI
        if (isFieldVisibleOnUI) {
          // Set needValidation: true for visible fields (so edit icon appears)
          newRow[fieldName] = {
            ...fieldWithoutMandatory,
            value: "",
            confidence: "0",
            needValidation: true
          };
          // Set isMandatory for specific fields even in new rows (only if visible on UI)
          if (isMandatoryField) {
            newRow[fieldName].isMandatory = true;
          }
        } else {
          // For non-visible fields, don't set needValidation or isMandatory
          newRow[fieldName] = {
            ...fieldWithoutMandatory,
            value: "",
            confidence: "0"
          };
        }
        // Preserve isVisibleOnUI if it exists
        if (referenceRow[fieldName].isVisibleOnUI !== undefined) {
          newRow[fieldName].isVisibleOnUI = referenceRow[fieldName].isVisibleOnUI;
        }
        // Preserve dataType if it exists
        if (referenceRow[fieldName].dataType) {
          newRow[fieldName].dataType = referenceRow[fieldName].dataType;
        }
      } else {
        // Default structure if no reference row exists
        // Assume field is visible on UI if no reference row exists
        const isMandatoryField = mandatoryFieldsForNewRows.includes(fieldName);
        newRow[fieldName] = {
          confidence: "0",
          dataType: "String",
          needValidation: true, // Visible fields need edit icon
          value: "",
          isVisibleOnUI: true
        };
        // Set isMandatory for specific fields even in new rows (only for visible fields)
        if (isMandatoryField) {
          newRow[fieldName].isMandatory = true;
        }
      }
    });

    // If no existing fields found, create default fields
    if (allFieldNames.size === 0) {
      newRow.consumed_material_number_sponsor = {
        confidence: "0",
        dataType: "String",
        needValidation: false, // Not a mandatory field, no validation needed
        value: "",
        isVisibleOnUI: true
      };
      newRow.consumption_batch_number_sponsor = {
        confidence: "0",
        dataType: "String",
        needValidation: true, // Mandatory field, needs validation
        value: "",
        isMandatory: true, // Show mandatory star for consumption batch number
        isVisibleOnUI: true
      };
      newRow.actual_material_quantity_consumed = {
        confidence: "0",
        dataType: "Numeric",
        needValidation: true, // Mandatory field, needs validation
        value: "",
        isMandatory: true, // Show mandatory star for actual material quantity consumed
        isVisibleOnUI: true
      };
    }

    // Set isMandatory for consumption_batch_number_sponsor and actual_material_quantity_consumed
    // even if they were copied from reference row (for first material)
    // Only set if the field is visible on UI
    if (newRow.consumption_batch_number_sponsor && newRow.consumption_batch_number_sponsor.isVisibleOnUI !== false) {
      newRow.consumption_batch_number_sponsor.isMandatory = true;
      newRow.consumption_batch_number_sponsor.needValidation = newRow.consumption_batch_number_sponsor.needValidation !== false ? true : false;
    }
    // Also check for consumption_batch_number (without _sponsor suffix)
    if (newRow.consumption_batch_number && newRow.consumption_batch_number.isVisibleOnUI !== false) {
      newRow.consumption_batch_number.isMandatory = true;
      newRow.consumption_batch_number.needValidation = newRow.consumption_batch_number.needValidation !== false ? true : false;
    }
    if (newRow.actual_material_quantity_consumed && newRow.actual_material_quantity_consumed.isVisibleOnUI !== false) {
      newRow.actual_material_quantity_consumed.isMandatory = true;
      newRow.actual_material_quantity_consumed.needValidation = newRow.actual_material_quantity_consumed.needValidation !== false ? true : false;
    }
    // BCM-654: for a brand-new consumed material the user must type the BMS material number,
    // so mark it editable (needValidation=true) and mandatory so the pencil + red star show.
    // autoPopulated is intentionally NOT set here — it was already stripped from the
    // reference copy above, so a fresh manual entry never carries the flag (only
    // backend-populated rows do).
    if (newRow.consumed_material_number_sponsor && newRow.consumed_material_number_sponsor.isVisibleOnUI !== false) {
      newRow.consumed_material_number_sponsor.isMandatory = true;
      newRow.consumed_material_number_sponsor.needValidation = true;
    }

    const newBowl = {
      ID: nextNumericId, // Numeric ID: 1, 2, 3, 4, etc.
      isNew: true,
      bowl: { value: nextAlphabeticValue, confidence: "0", dataType: "String" }, // Alphabetic value: A, B, C, D, etc.
      rows: [newRow]
    };

    newApiData.bowls.push(newBowl);

    setApiData(newApiData);
    setEditedData(newApiData);
  };

  // BCM-654 add-on: split the flat `cordaValidationErrors` array into per-bowl /
  // per-field buckets by matching each error message against the batch/material/DoM
  // value it references. Mapped errors render under their bowl (renderBowls) and
  // inside the relevant edit popup; unmapped errors still render at the bottom as
  // the existing safety net (no error is lost if the text can't be matched).
  const _escapeRegex = (s) =>
    s.toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const _valueAppearsIn = (msg, val) => {
    if (!val || typeof msg !== 'string') return false;
    const v = val.toString().trim();
    if (!v) return false;
    // Word-boundary so "1" doesn't match "12345"
    return new RegExp(`(^|[^\\w])${_escapeRegex(v)}([^\\w]|$)`).test(msg);
  };
  const findRowForError = (errMsg) => {
    if (!apiData?.bowls || typeof errMsg !== 'string') return null;
    for (let bIdx = 0; bIdx < apiData.bowls.length; bIdx++) {
      const bowl = apiData.bowls[bIdx];
      if (!bowl?.rows) continue;
      for (let rIdx = 0; rIdx < bowl.rows.length; rIdx++) {
        const row = bowl.rows[rIdx];
        const batchVal = row?.consumption_batch_number_sponsor?.value;
        const matVal = row?.consumed_material_number_sponsor?.value;
        if (_valueAppearsIn(errMsg, batchVal) || _valueAppearsIn(errMsg, matVal)) {
          return { bowlIndex: bIdx, rowIndex: rIdx };
        }
      }
    }
    return null;
  };
  const getErrorsForBowl = (bowlIndex) => {
    if (!cordaValidationErrors?.length) return [];
    return cordaValidationErrors
      .map((msg) => ({ msg, match: findRowForError(msg) }))
      .filter((e) => e.match && e.match.bowlIndex === bowlIndex)
      .map((e) => ({ ...e, bowlLabel: apiData?.bowls?.[bowlIndex]?.bowl?.value || (bowlIndex + 1) }));
  };
  const getUnmappedErrors = () => {
    if (!cordaValidationErrors?.length) return [];
    // BCM-654: the Date of Manufacture "could not be determined" error is already
    // rendered by the dedicated date_of_manufacture banner. Exclude it from this
    // global block so it does not appear twice (field banner + global block).
    const norm = (s) => (s || '').toString().replace(/\s+/g, ' ').trim();
    const domBannerActive =
      apiData?.date_of_manufacture?.isVisibleOnUI !== false &&
      !!apiData?.date_of_manufacture?.errorMessage;
    const domErrNorm = domBannerActive
      ? norm(apiData.date_of_manufacture.errorMessage)
      : null;
    const seen = new Set();
    // BCM-654: show ALL backend errors together at the bottom, in the exact order the
    // backend sent them (no longer split per bowl). Only DoM-banner skip + dedup remain.
    return cordaValidationErrors.filter((msg) => {
      const n = norm(msg);
      if (domErrNorm && n === domErrNorm) return false; // shown by DoM banner
      if (seen.has(n)) return false;                    // dedupe identical msgs
      seen.add(n);
      return true;
    });
  };
  // BCM-654: identify the SUBJECT of an error — which field the message is
  // actually ABOUT. Most errors mention multiple fields for context (a material
  // error mentions the batch number too, e.g. "BMS Consumed Material Number 2341
  // associated with BMS Consumed Batch Number ENTCMH101 is not matching..."), so
  // a naive .includes() check picks the wrong one. We treat whichever field
  // keyword appears FIRST in the message as the subject — that's the field the
  // call-to-action ("Please edit...") targets in practice.
  // Used by the popup Save handler to pick the relevant error from an array.
  const _getErrorSubject = (msg) => {
    if (typeof msg !== 'string') return null;
    const positions = [
      { subject: 'material', idx: msg.search(/[Mm]aterial/) },
      { subject: 'batch',    idx: msg.search(/[Bb]atch [Nn]umber/) },
      { subject: 'dom',      idx: msg.search(/[Dd]ate of [Mm]anufacture/) },
    ].filter((p) => p.idx >= 0);
    if (positions.length === 0) return null;
    positions.sort((a, b) => a.idx - b.idx);
    return positions[0].subject;
  };

  const renderBowls = (bowls) => {
    if (!bowls || !Array.isArray(bowls) || bowls.length === 0) return null;

    // Fields that should be numbered (batch-related fields)
    const numberedFields = [
      "consumption_batch_number",
      "consumption_batch_number_sponsor",
      "actual_material_quantity_consumed",
    ];

    /*
     * BCM-1077: a section auto-populated from the PO BOM cannot be deleted, and
     * Consumption A never carried a delete icon at all (the bowlIndex > 0 test
     * below). So a document whose other section came from the BOM left nothing
     * deletable anywhere — the PO 45685876 dead end, where material 1480798 in
     * Consumption A could not be removed.
     *
     * When ANY section is BOM-populated, Consumption A is given an icon too, so
     * there is another way out. Computed across every bowl once, before the
     * per-bowl render below, because it is a property of the whole document
     * rather than of one section.
     */
    const anyBomAutoPopulatedBowl = bowls.some(
      (b) =>
        Array.isArray(b?.rows) &&
        b.rows.some(
          (r) =>
            r?.consumed_material_number_sponsor?.autoPopulatedFromBOM === true
        )
    );

    return bowls.map((bowl, bowlIndex) => {
      const originalBowlIndex = bowlIndex;
      const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
      const reviewed = apiData.noOfFieldsReviewed || 0;
      const isMarkedAsReviewed = apiData.markAsReviewed === true;
      const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
      const noPartialShippingDetails = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;
      
      const canShowDeleteActions = ((enableAllFieldsEdit) ||
        (markAsCompleteVisible) || (isMarkedAsReviewed && noPartialShippingDetails)) &&
      !showingOriginal && 
      !isEditingDisabled() && canReviewAndEdit();
      
      const showDeleteIconForBowl =
        (bowlIndex > 0 || anyBomAutoPopulatedBowl) && canShowDeleteActions;

      // BCM-902: consumption section that Corda auto-populated from the PO BOM must not be deletable.
      const isBomAutoPopulatedBowl = Array.isArray(bowl.rows) && bowl.rows.some(
        (r) => r?.consumed_material_number_sponsor?.autoPopulatedFromBOM === true
      );
      /*
       * BCM-1077: bowlIndex > 0 used to double as the guarantee that one
       * section always survives. Consumption A can now show the icon, so that
       * guard no longer holds implicitly and is stated outright.
       */
      const isLastRemainingBowl = bowls.length <= 1;
      const isDeleteBowlDisabled =
        isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable() ||
        isBomAutoPopulatedBowl || isLastRemainingBowl;

      return (
      <Box key={bowl.ID} mt={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Consumption Details {bowl?.bowl?.value || bowl.ID}
          </Typography>
          {showDeleteIconForBowl && (
            <Box display="flex" alignItems="center" ml={1}>
              <IconButton
                disabled={isDeleteBowlDisabled}
                className={classes.deleteIconButton}
                onClick={() => {
                  if (!isDeleteBowlDisabled) {
                    handleDeleteConsumptionSectionClick(originalBowlIndex, false);
                  }
                }}
                style={{
                  padding: 6,
                  marginTop: 2,
                  cursor: isDeleteBowlDisabled ? "not-allowed" : "pointer"
                }}
                size="small"
                title={
                  isBomAutoPopulatedBowl
                    ? "Cannot delete — auto-populated from PO BOM"
                    : isLastRemainingBowl
                    ? "Cannot delete — at least one consumption section is required"
                    : isDeleteBowlDisabled
                    ? "Delete consumption section (disabled)"
                    : "Delete consumption section"
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        {bowl.rows && Array.isArray(bowl.rows) && bowl.rows.length > 0 ? bowl.rows.map((row, rowIndex) => {
          
          // Helper to get material number value from a row.
          // Sponsor field only — CMO field (consumed_material_number) is no longer
          // visible on UI (isVisibleOnUI: false) so we never rely on it for UI decisions.
          const getMaterialNumberValue = (row) => {
            return row.consumed_material_number_sponsor?.value || null;
          };

          // Get current row's material number
          const currentMaterialNumber = getMaterialNumberValue(row);
          
          // Get previous row's material number (if not first row)
          const previousRow = rowIndex > 0 ? bowl.rows[rowIndex - 1] : null;
          const previousMaterialNumber = previousRow ? getMaterialNumberValue(previousRow) : null;
          
          // Check if material number matches previous row (skip material number fields if it matches)
          const shouldSkipMaterialNumber = rowIndex > 0 && currentMaterialNumber === previousMaterialNumber && currentMaterialNumber !== null;
          
          // Separate fields into numbered and non-numbered
          const rowFields = Object.keys(row).filter(
            (key) =>
              key !== "row_id" &&
              key !== "isNew" &&
              typeof row[key] === "object" &&
              "value" in row[key]
          );

          // Define field order for consistent display
          const materialFieldOrder = [
            "consumed_material_number",
            "consumed_material_number_sponsor",
            "consumed_material_uom",
          ];
          const batchFieldOrder = [
            "consumption_batch_number",
            "consumption_batch_number_sponsor",
            "actual_material_quantity_consumed",
          ];

          // Helper to check if a field is a batch field (base or with suffix)
          const isBatchFieldKey = (key) => {
            return numberedFields.some(baseField => {
              return key === baseField || key.startsWith(baseField + '_');
            });
          };
          
          const materialFields = rowFields
            .filter((key) => {
              // Filter out batch fields
              if (isBatchFieldKey(key)) return false;
              
              // For rows after the first: by default material is shown once per bowl
              // (rows in a bowl normally share the same material). But when a row has a
              // DISTINCT material number sponsor, show its material fields too — so a bowl
              // whose rows have different materials (e.g. a, b, c) shows each one.
              // Hide only when the row has no material (inherits the first row's material)
              // or its material matches the previous row's (same material → show once).
              if (rowIndex > 0) {
                const hasNoMaterial =
                  currentMaterialNumber === null || currentMaterialNumber === "";
                const sameAsPreviousMaterial =
                  currentMaterialNumber !== null &&
                  currentMaterialNumber === previousMaterialNumber;
                if (hasNoMaterial || sameAsPreviousMaterial) {
                  return false;
                }
                // distinct material → fall through and show this row's material fields
              }
              
              // Skip material number fields if they match previous row
              if (shouldSkipMaterialNumber && (key === "consumed_material_number" || key === "consumed_material_number_sponsor")) {
                return false;
              }
              
              // Check isVisibleOnUI property
              const path = `bowls[${originalBowlIndex}].rows[${rowIndex}].${key}`;
              const dataForCheck = showingOriginal ? originalData : apiData;
              const fieldData = getNestedValue(dataForCheck, path);
              
              // If isVisibleOnUI is explicitly false, hide the field
              // If undefined or true, show it (backward compatibility)
              return fieldData?.isVisibleOnUI !== false;
            })
            .sort((a, b) => {
              const indexA = materialFieldOrder.indexOf(a);
              const indexB = materialFieldOrder.indexOf(b);
              if (indexA === -1 && indexB === -1) return 0;
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          
          // Since one row = one batch, get batch fields directly from row
          const batchFields = rowFields.filter(key => isBatchFieldKey(key));

          return (
            <Box key={row.row_id} ml={2} mt={2}>
              {/* Material fields (displayed once, no numbering) */}
              {materialFields.map((fieldKey) => {
                const path = `bowls[${originalBowlIndex}].rows[${rowIndex}].${fieldKey}`;

                // BCM-654: The Sponsor material number shows a gray "NA" badge and a disabled
                // pencil whenever it has been auto-populated from inventory
                // (autoPopulated === true), OR when the Sponsor batch in this row is
                // low-confidence (needValidation === true) — because the material will be
                // auto-populated once the batch is reviewed; showing a confidence number
                // (e.g. 0) here would be misleading. New rows (Add New Consumed Material/
                // Batch) explicitly set autoPopulated=false (handleAddNewMaterial), so they
                // stay editable.
                // NA + no-pencil is unconditional for the auto-populated / will-auto-populate
                // states — apply-edit-all does NOT override, because the material is derived
                // from the inventory batch lookup and any manual override would be replaced
                // on the next refresh. New rows (Add New Consumed Material) explicitly have
                // autoPopulated=false and a high-confidence batch, so they fall through to
                // the normal editable rendering.
                const isMaterialNameField =
                  fieldKey === 'consumed_material_number_sponsor';
                /*
                 * The material cannot be entered until the batch is, because the
                 * inventory lookup that fills the material has not happened yet —
                 * either the batch has no value, or it still carries
                 * needValidation: true.
                 *
                 * This applies to EVERY new entry, both Add New Consumed Batch and
                 * Add New Consumed Material: in both the user enters the batch first
                 * and the material is then auto-populated from it, which is what the
                 * info icon's tooltip tells them. Both are created with value: ""
                 * and needValidation: true, and editing the batch clears both, so
                 * the material unlocks at the right moment.
                 */
                const _batchSponsor = row?.consumption_batch_number_sponsor;
                const _batchValueMissing =
                  String(_batchSponsor?.value ?? '').trim() === '';
                const _batchNotReady =
                  !!_batchSponsor &&
                  (_batchValueMissing || _batchSponsor.needValidation === true);
                const isMaterialDisabled =
                  isMaterialNameField &&
                  (
                    row?.consumed_material_number_sponsor?.autoPopulated === true ||
                    _batchNotReady
                  );
                // Locked specifically because of the batch — this is the state that gets the
                // info icon and its tooltip, rather than the bare NA badge.
                const isMaterialBlockedByBatch = isMaterialNameField && _batchNotReady;

                return (
                  <Grid
                    container
                    alignItems="center"
                    spacing={2}
                    className={classes.formField}
                    key={path}
                  >
                    <Grid item xs={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          style={{ paddingRight: 16, fontWeight: 500 }}
                        >
                          {toTitleCase(fieldKey)}
                          {isFieldMandatory(path) && (
                            <span className={classes.requiredStar}>*</span>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box style={{ width: "calc(100% - 70px)" }}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={getValueForField(path)}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    pl={1}
                                  >
                                    <Box
                                      style={{
                                        width: 1,
                                        height: 39,
                                        backgroundColor: "#ccc",
                                        marginRight: 3,
                                      }}
                                    />
                                    {isMaterialDisabled ? (
                                      // BCM-654: material is auto-populated from inventory, or is
                                      // waiting on its batch — always show gray NA so a misleading
                                      // confidence value (e.g. 0) is never displayed. The reason it
                                      // is locked is carried by the info icon in the edit-icon slot
                                      // beside the field, not in here.
                                      <Box
                                        style={{
                                          backgroundColor: "#9e9e9e",
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                        }}
                                      >
                                        NA
                                      </Box>
                                    ) : shouldShowInfoIcon(path) ? (
                                      <Box
                                        onClick={() =>
                                          fetchFieldHistory(
                                            path,
                                            toTitleCase(fieldKey)
                                          )
                                        }
                                        style={{
                                          backgroundColor: "#777",
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Info fontSize="small" />
                                      </Box>
                                    ) : (
                                      <Box
                                        style={{
                                          backgroundColor: getLevelColor(
                                            getConfidenceForField(path)
                                          ),
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                        }}
                                      >
                                        {getConfidenceDisplay(path)}
                                      </Box>
                                    )}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {/* BCM-902: PO BOM note — only when Corda flag autoPopulatedFromBOM=true.
                              Inventory case is handled by the bowl-level big note below. */}
                          {fieldKey === 'consumed_material_number_sponsor' &&
                            getNestedValue(apiData, path)?.autoPopulatedFromBOM === true && (
                            <Typography
                              variant="caption"
                              style={{
                                color: '#1976d2',
                                fontStyle: 'italic',
                                display: 'block',
                                marginTop: 2,
                              }}
                            >
                              Note - BMS Consumed Material Number field is auto-populated here based on the PO BOM
                            </Typography>
                          )}
                        </Box>
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(() => {
                            const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
                            const reviewed = apiData.noOfFieldsReviewed || 0;
                            const isMarkedAsReviewed = apiData.markAsReviewed === true;
                            const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
                            const isCategoryEditable = isDocumentCategoryEditable();

                            // The material cannot be edited until the batch is entered, and
                            // "Apply edit feature to all the fields" does not lift that — there
                            // is nothing to look the material up against yet. The edit icon is
                            // replaced by an info icon that explains the wait on hover, so the
                            // user is told why rather than just finding the pencil missing.
                            if (isMaterialBlockedByBatch) {
                              return (
                                <Box
                                  title="Please enter Consumed Material Batch to enable auto-population of Consumed Material Number"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "help",
                                  }}
                                >
                                  <Info fontSize="small" style={{ color: "#9e9e9e" }} />
                                </Box>
                              );
                            }

                            // BCM-654: hide edit pencil for material already auto-populated from
                            // inventory — unless "Apply edit feature to all the fields" is
                            // checked, in which case the pencil appears so the user can manually
                            // override even though the NA badge stays.
                            if (isMaterialDisabled && !enableAllFieldsEdit) return null;

                            // BCM-902: material carrying the PO BOM note comes straight from the
                            // BOM and must never be hand-edited. "Apply edit feature to all the
                            // fields" brings the pencil back for every other field, so here it is
                            // rendered disabled instead of clickable.
                            const isBomAutoPopulatedField =
                              fieldKey === 'consumed_material_number_sponsor' &&
                              getNestedValue(apiData, path)?.autoPopulatedFromBOM === true;
                            const isEditDisabled = !isCategoryEditable || isBomAutoPopulatedField;

                            // BCM-654: when "Apply edit feature to all the fields" is checked, the
                            // pencil is shown for every editable field — only !showingOriginal and
                            // canReviewAndEdit() still gate it. Otherwise the original per-field
                            // logic applies (needsValidation / markAsComplete + info icon / etc.).
                            // isEditingDisabled() wraps both branches so it can't be bypassed by
                            // "Apply edit feature to all the fields" (e.g. batch status 3 — post-
                            // manufacturing documents not yet received).
                            const shouldShowEdit = !isEditingDisabled() && (enableAllFieldsEdit
                              ? (!showingOriginal && canReviewAndEdit())
                              : (
                                  (shouldShowEditForValidation(path) ||
                                   (markAsCompleteVisible && shouldShowInfoIcon(path)) ||
                                   (isMarkedAsReviewed && noPartialShippingDetails && shouldShowInfoIcon(path))
                                  ) &&
                                  !showingOriginal &&
                                  canReviewAndEdit()
                                ));

                            return shouldShowEdit ? (
                              <Box
                                onClick={() => {
                                  if (!isEditDisabled) {
                                    handleOpenEditModal(
                                      path,
                                      toTitleCase(fieldKey)
                                    );
                                  }
                                }}
                                title={
                                  isBomAutoPopulatedField
                                    ? "Cannot edit — auto-populated from PO BOM"
                                    : undefined
                                }
                                style={{
                                  cursor: isEditDisabled ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: isEditDisabled ? 0.4 : 1,
                                }}
                              >
                                <Edit
                                  fontSize="small"
                                  style={{ color: "#EC6535" }}
                                />
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}

              {/* Batch fields (one row = one batch) */}
              {(() => {
                // Filter and sort batch fields first
                const dataForCheck = showingOriginal ? originalData : apiData;
                const visibleBatchFields = batchFields
                  .filter(key => {
                    // Check isVisibleOnUI property
                    const fieldPath = `bowls[${originalBowlIndex}].rows[${rowIndex}].${key}`;
                    const fieldData = getNestedValue(dataForCheck, fieldPath);
                    
                    // If isVisibleOnUI is explicitly false, hide the field
                    return fieldData?.isVisibleOnUI !== false;
                  })
                  .sort((a, b) => {
                    // Sort using batchFieldOrder if defined
                    const indexA = batchFieldOrder.indexOf(a);
                    const indexB = batchFieldOrder.indexOf(b);
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                  });
                
                return visibleBatchFields.map((fieldKey, fieldIndex) => {
                  const path = `bowls[${originalBowlIndex}].rows[${rowIndex}].${fieldKey}`;
                  // Use actual field name from response and append sequential number (based on array position, not row_id)
                  const fieldLabel = `${toTitleCase(fieldKey)} ${rowIndex + 1}`;

                  // BCM-654 (spec p.19-20): actual_material_quantity_consumed shows NA and
                  // hides the pencil ONLY when Corda auto-populated it from inventory
                  // (autoPopulated=true). If qty is already present in the document (OCR value),
                  // always show the normal confidence badge regardless of batch confidence.
                  const isActualQtyField = fieldKey === 'actual_material_quantity_consumed';
                  const isActualQtyDisabled = isActualQtyField &&
                    getNestedValue(apiData, path)?.autoPopulated === true;
                  // Show delete icon for rows except the first one (one row = one batch)
                  const showDeleteIcon = rowIndex > 0 && canShowDeleteActions;
                  
                  // Only show delete icon on the first batch field
                  const isFirstBatchField = fieldIndex === 0;
                  
                  return (
                    <Grid
                      container
                      alignItems="center"
                      spacing={2}
                      className={classes.formField}
                      key={path}
                    >
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            style={{ paddingRight: 16, fontWeight: 500 }}
                          >
                            {fieldLabel}
                            {isFieldMandatory(path) && (
                              <span className={classes.requiredStar}>*</span>
                            )}
                          </Typography>
                          {showDeleteIcon && isFirstBatchField && (
                            <IconButton
                              disabled={isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()}
                              className={classes.deleteIconButton}
                              onClick={() => {
                                // Delete entire row (one row = one batch)
                                if (!isEditingDisabled() && canReviewAndEdit() && isDocumentCategoryEditable()) {
                                  handleDeleteBatchClick(originalBowlIndex, rowIndex, 1, fieldLabel, false);
                                }
                              }}
                              style={{
                                padding: 4,
                                cursor: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "not-allowed" : "pointer"
                              }}
                              title="Delete batch"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={8}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box style={{ width: "calc(100% - 70px)" }}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              size="small"
                              value={getValueForField(path)}
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      pl={1}
                                    >
                                      <Box
                                        style={{
                                          width: 1,
                                          height: 39,
                                          backgroundColor: "#ccc",
                                          marginRight: 3,
                                        }}
                                      />
                                      {isActualQtyDisabled ? (
                                        // BCM-654: auto-populated qty — always show gray NA so a
                                        // misleading confidence value isn't displayed for a field
                                        // derived from inventory. Apply-edit-all does NOT override.
                                        <Box
                                          style={{
                                            backgroundColor: "#9e9e9e",
                                            color: "white",
                                            borderRadius: 4,
                                            padding: "2px 8px",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            minWidth: 24,
                                            textAlign: "center",
                                          }}
                                        >
                                          NA
                                        </Box>
                                      ) : shouldShowInfoIcon(path) ? (
                                        <Box
                                          onClick={() =>
                                            fetchFieldHistory(path, fieldLabel)
                                          }
                                          style={{
                                            backgroundColor: "#777",
                                            color: "white",
                                            borderRadius: 4,
                                            padding: "2px 8px",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            minWidth: 24,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Info fontSize="small" />
                                        </Box>
                                      ) : (
                                        <Box
                                          style={{
                                            backgroundColor: getLevelColor(
                                              getConfidenceForField(path)
                                            ),
                                            color: "white",
                                            borderRadius: 4,
                                            padding: "2px 8px",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            minWidth: 24,
                                            textAlign: "center",
                                          }}
                                        >
                                          {getConfidenceDisplay(path)}
                                        </Box>
                                      )}
                                    </Box>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Box>
                          <Box
                            style={{
                              width: 32,
                              height: 32,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {(() => {
                            const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
                            const reviewed = apiData.noOfFieldsReviewed || 0;
                            const isMarkedAsReviewed = apiData.markAsReviewed === true;
                            const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
                            const isCategoryEditable = isDocumentCategoryEditable();

                            // BCM-654: hide edit pencil for actual qty when auto-populated.
                            // "Apply edit feature to all the fields" overrides and shows it.
                            if (isActualQtyDisabled && !enableAllFieldsEdit) return null;

                            // BCM-654: when "Apply edit feature to all the fields" is checked, the
                            // pencil is shown for every editable field — only !showingOriginal and
                            // canReviewAndEdit() still gate it. Otherwise the original per-field
                            // logic applies (needsValidation / markAsComplete + info icon / etc.).
                            // isEditingDisabled() wraps both branches so it can't be bypassed by
                            // "Apply edit feature to all the fields" (e.g. batch status 3 — post-
                            // manufacturing documents not yet received).
                            const shouldShowEdit = !isEditingDisabled() && (enableAllFieldsEdit
                              ? (!showingOriginal && canReviewAndEdit())
                              : (
                                  (shouldShowEditForValidation(path) ||
                                   (markAsCompleteVisible && shouldShowInfoIcon(path)) ||
                                   (isMarkedAsReviewed && noPartialShippingDetails && shouldShowInfoIcon(path))
                                  ) &&
                                  !showingOriginal &&
                                  canReviewAndEdit()
                                ));

                            return shouldShowEdit ? (
                              <Box
                                onClick={() => {
                                  if (isCategoryEditable) {
                                    handleOpenEditModal(
                                      path,
                                      fieldLabel
                                    );
                                  }
                                }}
                                style={{
                                  cursor: isCategoryEditable ? "pointer" : "not-allowed",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: isCategoryEditable ? 1 : 0.5,
                                }}
                              >
                                <Edit
                                  fontSize="small"
                                  style={{ color: "#EC6535" }}
                                />
                              </Box>
                            ) : null;
                          })()}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  );
                });
              })()}
            </Box>
          );
        }) : null}

        {/* BCM-654: auto-population notes per bowl (spec pp.7, 19) — hide when viewing original */}
        {!showingOriginal && (() => {
          // BCM-654: only show the "auto-populated" note when the material was REALLY
          // auto-populated for this bowl — i.e. autoPopulated flag set AND a value is present.
          // (A low-confidence batch with an empty material must NOT show the note.)
          // BCM-902: exclude BOM-derived rows — those get their own inline BOM note above.
          const hasMaterialAutoPopulated = (bowl.rows || []).some(r =>
            r?.consumed_material_number_sponsor?.autoPopulated === true &&
            r?.consumed_material_number_sponsor?.autoPopulatedFromBOM !== true &&
            r?.consumed_material_number_sponsor?.value
          );
          const hasQtyAutoPopulated = (bowl.rows || []).some(r =>
            r?.actual_material_quantity_consumed?.autoPopulated === true &&
            (r?.actual_material_quantity_consumed?.value || r?.actual_material_quantity_consumed?.value === 0)
          );
          if (!hasMaterialAutoPopulated && !hasQtyAutoPopulated) return null;
          return (
            <Box mt={1} mb={1} ml={1}>
              {hasMaterialAutoPopulated && (
                <Typography variant="body2" style={{ color: '#555', fontStyle: 'italic', marginBottom: 4 }}>
                  Note - BMS Consumed Material Number field is auto-populated here based on the Material associated with the BMS Consumed Batch Number in BMS SAP Inventory.
                </Typography>
              )}
              {hasQtyAutoPopulated && (
                <Typography variant="body2" style={{ color: '#555', fontStyle: 'italic' }}>
                  Note - Actual Material Quantity Consumed fields are auto-populated with the On-hand Qty value for the BMS Consumption Batch Numbers from BMS SAP Inventory.
                </Typography>
              )}
            </Box>
          );
        })()}

        {/* Add New Consumed Batch button - below all rows for this consumption details section */}
        {(() => {
          const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
          const reviewed = apiData.noOfFieldsReviewed || 0;
          const isMarkedAsReviewed = apiData.markAsReviewed === true;
          const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
          const noPartialShippingDetails = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;
          
          const shouldShowAddButtons = ((enableAllFieldsEdit) ||
            (markAsCompleteVisible) || (isMarkedAsReviewed && noPartialShippingDetails)) &&
          !showingOriginal && 
          !isEditingDisabled() && canReviewAndEdit();
          
          return shouldShowAddButtons ? (
          <Box mt={2} mb={2} display="flex" alignItems="center" justifyContent="space-between">
            <Box ml={2}>
              <Button
                variant="outlined"
                disabled={isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()}
                startIcon={
                  <Box
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "whilte",
                      color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                      border: `1px solid ${(isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 8,
                    }}
                  >
                    <AddIcon style={{ fontSize: 16, color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black" }} />
                  </Box>
                }
                onClick={() => {
                  // Add new row (one row = one batch)
                  // rowIndex parameter is not used since we always add a new row at the end
                  if (!isEditingDisabled() && canReviewAndEdit() && isDocumentCategoryEditable()) {
                    handleAddNewConsumedBatch(originalBowlIndex, 0, false);
                  }
                }}
                style={{
                  textTransform: "none",
                  borderColor: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                  color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                  borderRadius: 10,
                  minWidth: 200,
                  height: 36,
                  cursor: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "not-allowed" : "pointer",
                }}
              >
                Add New Consumed Batch
              </Button>
            </Box>
            {/* Add New Consumed Material button - only show for the last bowl, positioned at right corner */}
            {bowlIndex === bowls.length - 1 && (
              <Box mr={2}>
                <Button
                  variant="outlined"
                  disabled={isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()}
                  startIcon={
                    <Box
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: "white",
                        color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                        border: `1px solid ${(isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 8,
                      }}
                    >
                      <AddIcon style={{ fontSize: 16, color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black" }} />
                    </Box>
                  }
                  onClick={() => {
                    if (!isEditingDisabled() && canReviewAndEdit() && isDocumentCategoryEditable()) {
                      handleAddNewConsumedMaterial();
                    }
                  }}
                  style={{
                    textTransform: "none",
                    borderColor: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                    color: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "#999" : "black",
                    borderRadius: 10,
                    cursor: (isEditingDisabled() || !canReviewAndEdit() || !isDocumentCategoryEditable()) ? "not-allowed" : "pointer",
                  }}
                >
                  Add New Consumed Material
                </Button>
              </Box>
            )}
          </Box>
        ) : null;
        })()}

        {/* BCM-654 add-on: per-bowl validation errors from Corda.
            Matched by batch/material value in the error text → shown under the
            specific bowl they belong to instead of being lumped at the bottom.
            Unmatched errors continue to render in the global block below. */}
        {!showingOriginal && (() => {
          // BCM-654: per-bowl errors disabled — all backend errors now render together
          // after all bowls, in backend order (see the consolidated block below).
          const bowlErrors = [];
          if (bowlErrors.length === 0) return null;
          return (
            <Box mt={2} mb={1}>
              {bowlErrors.map((e, i) => (
                <Box
                  key={`bowl-err-${bowlIndex}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginBottom: 8,
                    padding: '10px 14px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f44336',
                    borderRadius: 4,
                    color: '#b71c1c',
                  }}
                >
                  <span style={{ fontSize: 16, marginTop: 1, color: '#f44336' }}>&#9888;</span>
                  <Typography variant="body2" style={{ color: '#b71c1c', fontWeight: 500 }}>
                    {e.msg}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        })()}
      </Box>
      );
    });
  };

  const renderPallets = (pallets) => {
    if (!pallets || !Array.isArray(pallets) || pallets.length === 0) return null;

    return pallets.map((pallet, palletIndex) => {
      const palletNumber = pallet.row_id || palletIndex + 1;
      return (
        <Box key={palletIndex} mt={3}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Pallet {palletNumber}
          </Typography>
          <Box ml={2} mt={2}>
            {Object.keys(pallet)
              .filter(
                (key) =>
                  key !== "row_id" &&
                  typeof pallet[key] === "object" &&
                  "value" in pallet[key]
              )
              .map((fieldKey) => {
                const path = `pallets[${palletIndex}].${fieldKey}`;
                return (
                  <Grid
                    container
                    alignItems="center"
                    spacing={2}
                    className={classes.formField}
                    key={path}
                  >
                    <Grid item xs={4}>
                      <Box display="flex" alignItems="center">
                        <Typography
                          style={{ paddingRight: 16, fontWeight: 500 }}
                        >
                          {toTitleCase(fieldKey)}
                          {isFieldMandatory(path) && (
                            <span className={classes.requiredStar}>*</span>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box style={{ width: "calc(100% - 70px)" }}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={getValueForField(path)}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    pl={1}
                                  >
                                    <Box
                                      style={{
                                        width: 1,
                                        height: 39,
                                        backgroundColor: "#ccc",
                                        marginRight: 3,
                                      }}
                                    />
                                    {shouldShowInfoIcon(path) ? (
                                      <Box
                                        onClick={() =>
                                          fetchFieldHistory(
                                            path,
                                            toTitleCase(fieldKey)
                                          )
                                        }
                                        style={{
                                          backgroundColor: "#777",
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Info fontSize="small" />
                                      </Box>
                                    ) : (
                                      <Box
                                        style={{
                                          backgroundColor: getLevelColor(
                                            getConfidenceForField(path)
                                          ),
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                        }}
                                      >
                                        {getConfidenceDisplay(path)}
                                      </Box>
                                    )}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(() => {
                            const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
                            const reviewed = apiData.noOfFieldsReviewed || 0;
                            const isMarkedAsReviewed = apiData.markAsReviewed === true;
                            const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
                            const isCategoryEditable = isDocumentCategoryEditable();

                            // BCM-654: when "Apply edit feature to all the fields" is checked, the
                            // pencil is shown for every editable field — only !showingOriginal and
                            // canReviewAndEdit() still gate it. Otherwise the original per-field
                            // logic applies (needsValidation / markAsComplete + info icon / etc.).
                            // isEditingDisabled() wraps both branches so it can't be bypassed by
                            // "Apply edit feature to all the fields" (e.g. batch status 3 — post-
                            // manufacturing documents not yet received).
                            const shouldShowEdit = !isEditingDisabled() && (enableAllFieldsEdit
                              ? (!showingOriginal && canReviewAndEdit())
                              : (
                                  (shouldShowEditForValidation(path) ||
                                   (markAsCompleteVisible && shouldShowInfoIcon(path)) ||
                                   (isMarkedAsReviewed && noPartialShippingDetails && shouldShowInfoIcon(path))
                                  ) &&
                                  !showingOriginal &&
                                  canReviewAndEdit()
                                ));

                            return shouldShowEdit ? (
                              <Box
                                onClick={() => {
                                  if (isCategoryEditable) {
                                    handleOpenEditModal(
                                      path,
                                      toTitleCase(fieldKey)
                                    );
                                  }
                                }}
                                style={{
                                  cursor: isCategoryEditable ? "pointer" : "not-allowed",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: isCategoryEditable ? 1 : 0.5,
                                }}
                              >
                                <Edit
                                  fontSize="small"
                                  style={{ color: "#EC6535" }}
                                />
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
          </Box>
        </Box>
      );
    });
  };

   const isMarkedAsReviewed = apiData.markAsReviewed === true;
   const noPartialShippingDetails = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;

  return (
    <Box className={classes.body}>
      {/* Loading overlays */}
      {isFetchingHistory && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            style={{
              backgroundColor: "white",
              padding: 24,
              borderRadius: 8,
              textAlign: "center",
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Loading field history...</Typography>
          </Box>
        </Box>
      )}
      {isRefreshing && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            style={{
              backgroundColor: "white",
              padding: 24,
              borderRadius: 8,
              textAlign: "center",
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Refreshing data...</Typography>
          </Box>
        </Box>
      )}
      {/* Blocking backdrop for both "Mark Review as Complete" and "Update & Re-run" —
          markingReviewComplete is set for the full lifetime of handleReviewAction
          (both actions, from every entry point: the inline buttons below AND the
          "Update and Re-Run" reminder dialog), so the user is kept inactive for the
          whole request, not only the reminder-dialog path (updateRerunLoading). */}
      {(markingReviewComplete || updateRerunLoading) && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} style={{ color: "white" }} />
        </Box>
      )}

      <Grid container direction="column" spacing={2}>
        {/* BCM-898: `hideHeader` drops the Back / "Digitalized <type>" strip and its
            divider. Set only by the Goods Receipt Pending Actions tab, which embeds
            this component inline (no Dialog to go Back out of) and already names the
            document in its own tab bar. That caller puts its action buttons on that
            tab bar, so it never combines hideHeader with extraActions. */}
        {!props.hideHeader && (
          <React.Fragment>
            <Grid item container alignItems="center" spacing={2}>
              <Grid item>
                <Button
                  startIcon={<ChevronLeft fontSize="small" />}
                  style={{
                    fontWeight: "bold",
                    textTransform: "none",
                    marginLeft: "0px",
                  }}
                  onClick={handleClose}
                >
                  Back
                </Button>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  Digitalized {(docType || "").replace(/\bOf\b/g, "of")}
                </Typography>
              </Grid>
              {/* BCM-898: Sync Inventory / Sync PO / Resubmit Document, supplied by the
                  Goods Receipt dashboard. Undefined (and so absent) everywhere else.
                  extraActions is a function of this instance's docType (matching the
                  headRight={(active) => ...} pattern in GRDigitalizedDocumentReview) so
                  Resubmit Document can pre-target whichever document is currently open,
                  rather than always the first fetched document type. */}
              {props.extraActions && (
                <Grid item style={{ marginLeft: "auto" }}>
                  <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: "wrap" }}>
                    {typeof props.extraActions === "function"
                      ? props.extraActions(docType)
                      : props.extraActions}
                  </Box>
                </Grid>
              )}
            </Grid>

            <Grid item>
              <Divider
                style={{
                  margin: "0px 0px 0px 0px",
                  backgroundColor: "#707070",
                  height: 1,
                  zIndex: 4,
                }}
              />
            </Grid>
          </React.Fragment>
        )}

        {/* BCM-898: the Goods Receipt Pending Actions tab already shows this batch's
            PO/CMO/product context on the page, so it passes hideProductRibbon to skip
            this strip visually. Kept mounted (just hidden) rather than unmounted, so
            the productCode fetch/callback below still runs exactly as everywhere else. */}
        <Grid
          style={{
            marginTop: props.hideProductRibbon ? 0 : "1rem",
            marginBottom: props.hideProductRibbon ? 0 : "2rem",
            display: props.hideProductRibbon ? "none" : undefined,
          }}
        >
          <ProductDetailsLoader
            collabrixProductID={batchDetails?.collabrixProductID}
            poNumber={batchDetails?.scheduleAgreementNumber}
            materialPlantID={batchDetails?.vendorPlant}
            materialStorageLocationID={batchDetails?.storageLocation}
            batchNumber={batchDetails?.vendorBatchNumber}
            vendorMosymphonyID={batchDetails?.vendorMosymphonyID}
            vendorPlantID={batchDetails?.vendorPlant}
            sponsorStorageLocation={batchDetails?.storageLocation}
            onProductCodeFetched={setProductCode}
          />
        </Grid>

        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid
              item
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              spacing={2}
            >
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                {isIdocDocument ? (
                  <>
                    {/post-shipment/i.test(currentFileCategory || "")
                      ? "Delivery ASN data"
                      : "Production ASN with Consumption data"}
                    <br />
                    (from CMO ERP file transfer)
                  </>
                ) : (
                  docType
                )}
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (showingOriginal) {
                      setApiData(editedData);
                      setShowingOriginal(false);
                    } else {
                      setApiData(originalData);
                      setShowingOriginal(true);
                    }
                  }}
                  style={{
                    marginRight: "8px",
                    minWidth: 20,
                    textAlign: "center",
                    size: "20px",
                    color: "white",
                    backgroundColor: "grey",
                    borderColor: "grey",
                  }}
                >
                  {showingOriginal
                    ? "Show Edited Version"
                    : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">
              {(() => {
                if (!schrockenCustomFileID) return 'No Preview Available';
                if (fileType === 'pdf') return 'PDF Preview';
                if (fileType === 'excel') return 'Excel Preview';

                // No file-type resolved yet — this only happens for an IDoc-sourced document
                // (e.g. Production ASN) with no viewable file of its own, since <PDFViewer>
                // (the only thing that ever sets fileType) never renders for it. Label the
                // panel to match its category — whether the content below shows sibling PDF
                // thumbnails or is still empty — instead of leaving it stuck on "Loading Preview".
                const isOwnFilePreviewable = !currentCustomFile?.customFileName
                  || /\.(pdf|xlsx|xls)$/i.test(currentCustomFile.customFileName);

                if (!isOwnFilePreviewable) {
                  return `PDF Preview of associated ${formatCategoryLabel(currentFileCategory).toLowerCase()}`;
                }

                return 'Loading Preview';
              })()}
            </Typography>
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "100%",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {documentShipments.length > 0 && (
                <Tabs
                  value={activeShipmentIdx}
                  onChange={(e, newIdx) => {
                    setActiveShipmentIdx(newIdx);
                    // Start each shipment tab on its own edited/current view rather than
                    // inheriting whatever "Show Unedited Version" toggle state the
                    // previously active shipment was left in.
                    setShowingOriginal(false);
                    // If a sibling doc was expanded to full view on the previous shipment,
                    // collapse it — otherwise the preview would stay stuck showing the old
                    // shipment's document after switching tabs.
                    setExpandedSiblingFileID(null);
                  }}
                  variant="scrollable"
                  // No theme color (primary/secondary) on the indicator or text — both
                  // tabs stay plain bold black, distinguished only by the underline.
                  TabIndicatorProps={{ style: { backgroundColor: "#000" } }}
                  style={{ marginBottom: 16, borderBottom: "1px solid #e0e0e0" }}
                >
                  {documentShipments.map((shipment, idx) => (
                    <Tab
                      key={shipment.schrockenCustomFileID}
                      label={`Shipment ${idx + 1}`}
                      className={classes.shipmentTab}
                      style={{ fontWeight: 700, color: "#000", textTransform: "none" }}
                    />
                  ))}
                </Tabs>
              )}
              {Object.keys(apiData)
                .filter((key) => {
                  if (key === "bowls" || key === "pallets") return false;
                  if (typeof apiData[key] !== "object") return false;
                  if (!("value" in apiData[key])) return false;
                  // BCM-654: for the date_of_manufacture field, respect the
                  // isVisibleOnUI flag — hide when the backend explicitly sets
                  // isVisibleOnUI=false (applies to ALL documents, bowl-structured
                  // or otherwise). isVisibleOnUI undefined / true → show.
                  if (
                    key === 'date_of_manufacture' &&
                    apiData[key]?.isVisibleOnUI === false
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((fieldKey) => {
                  // BCM-654: DoM field shows NA badge (in place of confidence) and hides
                  // the pencil when EITHER (a) any consumption batch has low confidence
                  // (spec p.15) OR (b) backend auto-populated DoM from inventory — matching
                  // the material/qty pattern. The pencil re-appears when "apply edit to all
                  // fields" is enabled, again matching material behavior.
                  const isDoMAutoPopulated =
                    fieldKey === 'date_of_manufacture' &&
                    apiData?.date_of_manufacture?.autoPopulated === true;
                  const isDoMDisabled =
                    fieldKey === 'date_of_manufacture' &&
                    (hasAnyBatchLowConfidence || isDoMAutoPopulated);

                  return (
                  <Grid
                    container
                    alignItems="center"
                    spacing={2}
                    className={classes.formField}
                    key={fieldKey}
                  >
                    <Grid item xs={4}>
                      <Box display="flex" alignItems="center">
                        <Typography
                          style={{ paddingRight: 16, fontWeight: 500 }}
                        >
                          {toTitleCase(fieldKey)}
                          {isFieldMandatory(fieldKey) && (
                            <span className={classes.requiredStar}>*</span>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box style={{ width: "calc(100% - 70px)" }}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={getValueForField(fieldKey)}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    pl={1}
                                  >
                                    <Box
                                      style={{
                                        width: 1,
                                        height: 39,
                                        backgroundColor: "#ccc",
                                        marginRight: 3,
                                      }}
                                    />
                                    {isDoMDisabled ? (
                                      // BCM-654: DoM is either auto-populated from inventory
                                      // OR a consumption batch is low confidence — show gray NA
                                      <Box
                                        style={{
                                          backgroundColor: "#9e9e9e",
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                        }}
                                      >
                                        NA
                                      </Box>
                                    ) : shouldShowInfoIcon(fieldKey) ? (
                                      <Box
                                        onClick={() =>
                                          fetchFieldHistory(
                                            fieldKey,
                                            toTitleCase(fieldKey)
                                          )
                                        }
                                        style={{
                                          backgroundColor: "#777",
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Info fontSize="small" />
                                      </Box>
                                    ) : (
                                      <Box
                                        style={{
                                          backgroundColor: getLevelColor(
                                            getConfidenceForField(fieldKey)
                                          ),
                                          color: "white",
                                          borderRadius: 4,
                                          padding: "2px 8px",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          minWidth: 24,
                                          textAlign: "center",
                                        }}
                                      >
                                        {getConfidenceDisplay(fieldKey)}
                                      </Box>
                                    )}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(() => {
                            const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
                            const reviewed = apiData.noOfFieldsReviewed || 0;
                            const isMarkedAsReviewed = apiData.markAsReviewed === true;
                            const markAsCompleteVisible = !showingOriginal && (toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed;
                            const isCategoryEditable = isDocumentCategoryEditable();

                            // BCM-654: hide the pencil for DoM by default when it's disabled
                            // (auto-populated OR batch low confidence). Re-appears when the
                            // user enables "apply edit to all fields" — same as material/qty.
                            if (isDoMDisabled && !enableAllFieldsEdit) return null;

                            // BCM-654: when "Apply edit feature to all the fields" is checked, the
                            // pencil is shown for every editable field — only !showingOriginal and
                            // canReviewAndEdit() still gate it. Otherwise the original per-field
                            // logic applies (needsValidation / markAsComplete + info icon / etc.).
                            // isEditingDisabled() wraps both branches so it can't be bypassed by
                            // "Apply edit feature to all the fields" (e.g. batch status 3 — post-
                            // manufacturing documents not yet received).
                            const shouldShowEdit = !isEditingDisabled() && (enableAllFieldsEdit
                              ? (!showingOriginal && canReviewAndEdit())
                              : (
                                  (shouldShowEditForValidation(fieldKey) ||
                                   (markAsCompleteVisible && shouldShowInfoIcon(fieldKey)) ||
                                   (isMarkedAsReviewed && noPartialShippingDetails && shouldShowInfoIcon(fieldKey))
                                  ) &&
                                  !showingOriginal &&
                                  canReviewAndEdit()
                                ));

                            return shouldShowEdit ? (
                              <Box
                                onClick={() => {
                                  if (isCategoryEditable) {
                                    handleOpenEditModal(
                                      fieldKey,
                                      toTitleCase(fieldKey)
                                    );
                                  }
                                }}
                                style={{
                                  cursor: isCategoryEditable ? "pointer" : "not-allowed",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: isCategoryEditable ? 1 : 0.5,
                                }}
                              >
                                <Edit
                                  fontSize="small"
                                  style={{ color: "#EC6535" }}
                                />
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  );
                })}

              {/* BCM-654: DoM auto-population note (spec p.15) — hide when viewing original
                  or when the backend has hidden the DoM field via isVisibleOnUI=false */}
              {!showingOriginal &&
                apiData.date_of_manufacture?.isVisibleOnUI !== false &&
                apiData.date_of_manufacture?.autoPopulated === true && (
                <Box mb={1}>
                  <Typography variant="body2" style={{ color: '#555', fontStyle: 'italic' }}>
                    Note - Date of Manufacture field is auto-populated here based on the Material DoM from BMS SAP Inventory.
                  </Typography>
                </Box>
              )}

              {apiData.bowls && renderBowls(apiData.bowls)}

              {/* BCM-564: Batch low-confidence page-level warnings — hide when viewing original or
                  when Corda already returned its own errors (backend errors take priority) */}
              {!showingOriginal && cordaValidationErrors.length === 0 && batchConfidencePageErrors.length > 0 && (
                <Box mt={2} mb={2}>
                  {batchConfidencePageErrors.map((msg, index) => (
                    <Box
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        marginBottom: 8,
                        padding: "10px 14px",
                        backgroundColor: "#f8d7da",
                        border: "1px solid #f44336",
                        borderRadius: 4,
                        color: "#b71c1c",
                      }}
                    >
                      <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                      <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                        {msg}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* DoM backend error — shown when backend could not determine Date of Manufacture.
                  Cleared as soon as the user edits the date_of_manufacture field. Also hidden when
                  the backend has marked the DoM field as isVisibleOnUI=false. */}
              {!showingOriginal &&
                apiData?.date_of_manufacture?.isVisibleOnUI !== false &&
                apiData?.date_of_manufacture?.errorMessage && (
                <Box mt={2} mb={2}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "10px 14px",
                      backgroundColor: "#f8d7da",
                      border: "1px solid #f44336",
                      borderRadius: 4,
                      color: "#b71c1c",
                    }}
                  >
                    <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                    <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                      {apiData.date_of_manufacture.errorMessage}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Add-on (client-side only): Date of Manufacture future-date check.
                  Reads the date_of_manufacture value from apiData and shows an error when
                  it is a FUTURE date (strictly after today). Today / past dates do not
                  trigger it, and an unparseable value is ignored (no false positives).
                  Purely additive — does not change any existing validation/logic. */}
              {!showingOriginal &&
                apiData?.date_of_manufacture?.isVisibleOnUI !== false &&
                (() => {
                  const domVal = apiData?.date_of_manufacture?.value;
                  if (!domVal) return null;
                  const parsedDom = moment(
                    String(domVal),
                    ["DD-MMM-YYYY", "DD-MMM-YY", "DD.MMM.YYYY", "YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601],
                    true
                  );
                  if (!parsedDom.isValid()) return null; // can't parse → don't flag
                  const isFutureDate = parsedDom
                    .startOf("day")
                    .isAfter(moment().startOf("day"));
                  if (!isFutureDate) return null; // today or past → OK
                  return (
                    <Box mt={2} mb={2}>
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          padding: "10px 14px",
                          backgroundColor: "#f8d7da",
                          border: "1px solid #f44336",
                          borderRadius: 4,
                          color: "#b71c1c",
                        }}
                      >
                        <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                        <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                          Date of Manufacture fetched from document is not a past date. Please edit the Date of Manufacture field.
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}

              {/* BCM-564 / BCM-654 add-on: Corda validation errors. Errors that map to a
                  specific bowl now render under that bowl (renderBowls). Only UNMAPPED
                  errors fall through to this global block — preserves the safety net
                  so no error ever gets dropped if value-matching fails. */}
              {!showingOriginal && (() => {
                const unmappedErrors = getUnmappedErrors();
                if (unmappedErrors.length === 0) return null;
                return (
                  <Box mt={2} mb={2}>
                    {unmappedErrors.map((msg, index) => (
                      <Box
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          marginBottom: 8,
                          padding: "10px 14px",
                          backgroundColor: "#f8d7da",
                          border: "1px solid #f44336",
                          borderRadius: 4,
                          color: "#b71c1c",
                        }}
                      >
                        <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                        <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                          {msg}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* BCM-348: show validation errors below consumed batches — hide when viewing original */}
              {!showingOriginal && markReviewValidationErrors.length > 0 && (
                <Box mt={2} mb={2}>
                  {markReviewValidationErrors.map((err, index) => (
                    <Box
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        marginBottom: 8,
                        padding: "10px 14px",
                        backgroundColor: "#f8d7da",
                        border: "1px solid #f44336",
                        borderRadius: 4,
                        color: "#b71c1c",
                      }}
                    >
                      <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                      <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                        {err.errorMessage}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {apiData.pallets && renderPallets(apiData.pallets)}

              {/* Reacts to the corrected (summed-across-shipments) Remaining Qty above —
                  shows on whichever shipment tab is active the moment editing either
                  shipment's Dispatch Qty pushes the combined Remaining Qty below zero. */}
              {isPostShipmentAsnDoc && Number(apiData?.remaining_quantity?.value) < 0 && (
                <Box
                  display="flex"
                  style={{
                    gap: 8,
                    marginTop: 12,
                    marginBottom: 12,
                    padding: "10px 14px",
                    backgroundColor: "#f8d7da",
                    border: "1px solid #f44336",
                    borderRadius: 4,
                    color: "#b71c1c",
                  }}
                >
                  <span style={{ fontSize: 16, marginTop: 1, color: "#f44336" }}>&#9888;</span>
                  <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                    Remaining Qty has become negative. Please correct and resubmit the documents
                    such that Remaining Qty becomes zero in value (Remaining Qty = Produced Qty -
                    Total Dispatch Qty).
                  </Typography>
                </Box>
              )}

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:
                </Typography>
                <Grid item style={{ width: "28px" }} />
                {(() => {
                  const confidenceLevels = batchDetails?.confidenceLevels || {
                    lowConfidenceLimit: 80,
                    highConfidenceLimit: 90,
                  };
                  const lowConfidenceLimit =
                    confidenceLevels.lowConfidenceLimit;
                  const highConfidenceLimit =
                    confidenceLevels.highConfidenceLimit;
                  return (
                    <>
                      <Box
                        style={{
                          backgroundColor: "green",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                        }}
                      >
                        &gt; {highConfidenceLimit}
                      </Box>
                      <Box
                        style={{
                          backgroundColor: "orange",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 50,
                          marginLeft: 16,
                        }}
                      >
                        {confidenceLevels.mediumConfidenceLimitMinValue ||
                          lowConfidenceLimit}{" "}
                        -{" "}
                        {confidenceLevels.mediumConfidenceLimitMaxValue ||
                          highConfidenceLimit}
                      </Box>
                      <Box
                        style={{
                          backgroundColor: "red",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                          marginLeft: 16,
                        }}
                      >
                        &lt; {lowConfidenceLimit}
                      </Box>
                    </>
                  );
                })()}
              </Box>

              {(() => {
                const isDocumentMarkedAsReviewed = apiData.markAsReviewed === true;
                const hasNoPartialShipping = !batchDetails?.partialShippingDetails || batchDetails?.partialShippingDetails?.length === 0;
                
                return !showingOriginal && (canReviewAndEdit() || (canReviewAndEdit() && (isDocumentMarkedAsReviewed && hasNoPartialShipping))) && (
                <Box
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    borderRadius: "4px",
                    backgroundColor: "#f9f9f9",
                    marginTop: 32,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableAllFieldsEdit}
                        onChange={(e) =>
                          setEnableAllFieldsEdit(e.target.checked)
                        }
                        color="primary"
                        disabled={isEditingDisabled() || !isDocumentCategoryEditable()}
                      />
                    }
                    label="Apply edit feature to all the fields"
                  />
                  <Typography
                    variant="body2"
                    style={{
                      fontStyle: "italic",
                      color: (isEditingDisabled() || !isDocumentCategoryEditable()) ? "#999" : "#666",
                      marginTop: "4px",
                    }}
                  >
                    {/* <FormControlLabel
                      control={
                        <Checkbox
                          checked={enableAllFieldsEdit}
                          onChange={(e) =>
                            setEnableAllFieldsEdit(e.target.checked)
                          }
                          color="primary"
                          disabled={isEditingDisabled() || !isDocumentCategoryEditable()}
                        />
                      }
                      label="Apply edit feature to all the fields"
                    />
                    <Typography
                      variant="body2"
                      style={{
                        fontStyle: "italic",
                        color: (isEditingDisabled() || !isDocumentCategoryEditable()) ? "#999" : "#666",
                        marginTop: "4px",
                      }}
                    > */}
                      {(isEditingDisabled()) 
                        ? "No fields require review - all fields have acceptable confidence levels."
                        : "Note - This will enable editing of the high confidence level fields, highlighted in green colors."}
                    </Typography>
                  </Box>
                );
              })()}

              {(() => {
                const toBeReviewed = apiData.noOfFieldsToBeReviewed || 0;
                const reviewed = apiData.noOfFieldsReviewed || 0;
                const isMarkedAsReviewed = apiData.markAsReviewed === true;
                const hasPartialShippingDetails = batchDetails?.partialShippingDetails?.length > 0;
                // MOSYMCOM-2829: isIdocDocument bypasses the field-count check so
                // Production ASN always offers the button, even at 100% confidence.
                const showMarkReviewButton = !showingOriginal && (isIdocDocument || toBeReviewed > 0 || reviewed > 0) && !isMarkedAsReviewed && canReviewAndEdit();
                const isCategoryEditable = isDocumentCategoryEditable();
                return (
                  <>
                    {showMarkReviewButton && <Box
                          style={{
                            marginTop: 64,
                            paddingBottom: 16,
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={!isCategoryEditable || isEditingDisabled() || (apiData.noOfFieldsToBeReviewed && apiData.noOfFieldsToBeReviewed > 0) || markingReviewComplete || !allBillOfLadingReceived || !isEffectivelyFullyDispatched || (isPostShipmentAsnDoc && Number(apiData?.remaining_quantity?.value) < 0)}
                            onClick={() => handleReviewAction(false)}
                            style={{
                              borderRadius: 30,
                              paddingLeft: "40px",
                              paddingRight: "40px",
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            endIcon={
                              markingReviewComplete ? (
                                <CircularProgress size={20} style={{ color: "white" }} />
                              ) : null
                            }
                          >
                            {markingReviewComplete ? "Processing..." : "Mark Review as Complete"}
                          </Button>

                        </Box>}

                    { isMarkedAsReviewed && batchDetails?.partialShippingDetails?.length === 0 && canReviewAndEdit() && <Box
                          style={{
                            marginTop: 64,
                            paddingBottom: 16,
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={!(reviewed > 0) || hasPartialShippingDetails || markingReviewComplete || !isCategoryEditable || isEditingDisabled()}
                            onClick={() => handleReviewAction(true)}
                            style={{
                              borderRadius: 30,
                              paddingLeft: "20px",
                              paddingRight: "20px",
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            endIcon={
                              markingReviewComplete ? (
                                <CircularProgress size={20} style={{ color: "white" }} />
                              ) : null
                            }
                          >
                            {markingReviewComplete ? "Processing..." : "Update & Re-run"}
                          </Button>
                        </Box>}
                  </>
                );
              })()}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                // height: "calc(100vh - 200px)",
                padding: 16,
                height: "100%",
                // overflow: "hidden",
              }}
            >
              <Box
                style={{
                  // height: 500,
                  // backgroundColor: "#f5f5f5",
                  // border: "1px dashed #ccc",
                }}
              >
                {(() => {
                  if (!schrockenCustomFileID) {
                    return (
                      <Box style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography>No PDF available</Typography>
                      </Box>
                    );
                  }

                  // Default to "previewable" (the original, always-render-PDFViewer behavior) unless we can
                  // positively confirm from the filename that this file isn't a pdf/xlsx — this way, if the
                  // customFilesList lookup ever fails to find a match, existing behavior is preserved exactly.
                  const isOwnFilePreviewable = !currentCustomFile?.customFileName
                    || /\.(pdf|xlsx|xls)$/i.test(currentCustomFile.customFileName);

                  // The current document has its own viewable file (the common case) — unchanged behavior.
                  if (isOwnFilePreviewable) {
                    return (
                      <PDFViewer
                        schrockenCustomFileID={schrockenCustomFileID}
                        onFileTypeChange={handleFileTypeChange}
                      />
                    );
                  }

                  // No viewable file of its own (e.g. an IDoc-sourced XML like Production ASN) —
                  // fall back to showing the other real documents filed under the same category.
                  // Multi-shipment: customFilesList entries don't carry deliveryNumber
                  // themselves, so cross-reference digitizedExtractedData (which does) to
                  // scope the preview to only the active shipment's own sibling(s) — otherwise
                  // every shipment's Bill of Lading would show together regardless of tab.
                  // No-op (empty map, filter below always passes) when there's no active
                  // shipment context, i.e. the single-shipment case is unaffected.
                  const deliveryNumberByFileID = new Map(
                    (batchDetails?.digitizedExtractedData || [])
                      .filter((d) => d?.deliveryNumber)
                      .map((d) => [String(d.schrockenCustomFileID), d.deliveryNumber])
                  );
                  const siblingCandidates = currentFileCategory
                    ? (batchDetails?.customFilesList || []).filter(
                        (f) =>
                          f.documentCategoryType === currentFileCategory &&
                          f.schrockenCustomFileID !== schrockenCustomFileID.toString() &&
                          !f.markedForDelete &&
                          /\.(pdf|xlsx|xls)$/i.test(f.customFileName || "") &&
                          (!activeShipmentDeliveryNumber ||
                            deliveryNumberByFileID.get(f.schrockenCustomFileID) === activeShipmentDeliveryNumber)
                      )
                    : [];

                  // A sibling doc type re-uploaded more than once (e.g. Consumption Details
                  // re-sent) would otherwise show every historical version as its own
                  // thumbnail — keep only the most recently updated copy per customFileType
                  // so the preview reflects the current document only.
                  const latestSiblingByType = new Map();
                  siblingCandidates.forEach((f) => {
                    const existing = latestSiblingByType.get(f.customFileType);
                    if (!existing || new Date(f.lastUpdatedDate) > new Date(existing.lastUpdatedDate)) {
                      latestSiblingByType.set(f.customFileType, f);
                    }
                  });
                  const siblingPreviewableFiles = Array.from(latestSiblingByType.values());

                  if (expandedSiblingFileID) {
                    return (
                      <Box>
                        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <Typography variant="h6">PDF Preview</Typography>
                          <Button
                            startIcon={<ChevronLeft />}
                            onClick={() => setExpandedSiblingFileID(null)}
                            style={{ textTransform: "none", color: "#000", fontWeight: 500, padding: 0, minWidth: "auto" }}
                          >
                            Back
                          </Button>
                        </Box>
                        <PDFViewer schrockenCustomFileID={expandedSiblingFileID} onFileTypeChange={() => {}} />
                      </Box>
                    );
                  }

                  if (siblingPreviewableFiles.length > 0) {
                    return (
                      <Box>
                        <Box style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                          {siblingPreviewableFiles.map((f) => (
                            <DocumentThumbnail
                              key={f.schrockenCustomFileID}
                              schrockenCustomFileID={f.schrockenCustomFileID}
                              customFileName={f.customFileName}
                              onClick={() => setExpandedSiblingFileID(f.schrockenCustomFileID)}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  }

                  // Previously silent — now explicit, so a genuine deliveryNumber/category
                  // mismatch (e.g. a shipment with no matching sibling doc yet) is visible
                  // instead of rendering as an unexplained blank pane.
                  return (
                    <Box style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="body2" style={{ color: "#999" }}>
                        No associated document available{activeShipmentDeliveryNumber ? " for this shipment" : ""} yet.
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
              {/* Shown for as long as the batch is waiting on Post-Manufacturing Documents
                  (status 3), regardless of which preview state above is active (own PDF,
                  associated-document thumbnails, or nothing yet). Post-Shipment documents
                  never show this note. */}
              {!/post-shipment/i.test(currentFileCategory || "") && batchDetails?.SASLBatchStatusID === 3 && (
                <Typography
                  variant="body2"
                  style={{ color: "red", fontWeight: 600, fontStyle: "italic", textAlign: "center", padding: "12px 0" }}
                >
                  Note - We can proceed to next steps once the {formatCategoryLabel(currentFileCategory) || "Post-Manufacturing Documents"} are received.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* WIP Product Note - shown after update button for documents with consumption details */}
        {batchDetails?.cmoWIPProductCode && apiData?.bowls && Array.isArray(apiData.bowls) && apiData.bowls.length > 0 && (
          <Grid item xs={12}>
            <Box
              style={{
                border: "1px solid #000",
                padding: "12px 16px",
                margin: "40px 0",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                }}
              >
                Note - Product {productCode} might have WIP products. Please review the document carefully.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* History Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History
          </Typography>
          <Box style={{ maxHeight: "400px", overflowY: "auto" }}>
            <Table
              style={{
                border: "1.5px solid grey",
                borderCollapse: "collapse",
                width: "100%",
              }}
            >
              <TableHead style={{ backgroundColor: "#F5F5F5" }}>
                <TableRow>
                  <TableCell className={classes.FeildHistoryHorizantalBorder}>
                    Timestamp
                  </TableCell>
                  <TableCell className={classes.FeildHistoryHorizantalBorder}>
                    Before Value
                  </TableCell>
                  <TableCell className={classes.FeildHistoryHorizantalBorder}>
                    After Value
                  </TableCell>
                  <TableCell className={classes.FeildHistoryHorizantalBorder}>
                    Updated By
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.hasHistory ? (
                  modalData.historyData.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell
                        className={classes.FeildHistoryHorizantalBorder}
                      >
                        {record.timestamp}
                      </TableCell>
                      <TableCell
                        className={classes.FeildHistoryHorizantalBorder}
                      >
                        {record.before}
                      </TableCell>
                      <TableCell
                        className={classes.FeildHistoryHorizantalBorder}
                      >
                        {record.after}
                      </TableCell>
                      <TableCell
                        className={classes.FeildHistoryHorizantalBorder}
                      >
                        {record.updatedBy}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
              style={{
                borderRadius: 30,
                paddingLeft: "40px",
                paddingRight: "40px",
              }}
              endIcon={<ArrowForwardIos style={{ fontSize: 15 }} />}
            >
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Edit {editModalData.fieldLabel} for PO {poNumber || ""}
            </Typography>
            <Button
              onClick={() => setOpenEditModal(false)}
              style={{ minWidth: "auto" }}
            >
              ✕
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                gutterBottom
                style={{ fontWeight: "bold" }}
              >
                {editModalData.fieldLabel}
              </Typography>
              <Box display="flex" alignItems="center">
                {editModalData.dataType === "Date" ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={editModalData.currentValue || ""}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                    <Box
                      style={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => setDatePickerOpen(true)}
                    >
                      <Event style={{ color: "#EC6535" }} />
                    </Box>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <DatePicker
                        open={datePickerOpen}
                        onClose={() => setDatePickerOpen(false)}
                        value={
                          editModalData.currentValue &&
                          editModalData.currentValue !== "n/a" &&
                          editModalData.currentValue !== "NA" &&
                          editModalData.currentValue !== "" &&
                          moment(editModalData.currentValue).isValid()
                            ? new Date(editModalData.currentValue)
                            : null
                        }
                        onChange={(date) => {
                          if (date && date.isValid()) {
                            const displayDate = date.format("DD-MMM-YYYY"); // UI format
                            const saveDate = convertToISOUTC(date.toDate()); // ISO UTC
                            setEditModalData((prev) => ({
                              ...prev,
                              currentValue: displayDate, // for showing in textfield
                              saveValue: saveDate, // for saving to backend
                            }));
                            // Clear error message when user selects a date
                            if (editModalValidationError) {
                              setEditModalValidationError("");
                            }
                          }
                          setDatePickerOpen(false);
                        }}
                        format="DD-MM-YYYY"
                        autoOk={true}
                        variant="dialog"
                        style={{ display: "none" }}
                        disableFuture={
                          (editModalData.fieldLabel || "")
                            .toString()
                            .trim()
                            .toLowerCase() === "date of manufacture"
                        }
                      />
                    </MuiPickersUtilsProvider>
                  </Box>
                ) : (
                  // Your existing TextField code
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={editModalData.currentValue}
                    onInput={(e) =>
                      handleInputRestriction(e, editModalData.dataType)
                    }
                    onBlur={(e) => {
                      const { value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (
                        typeof value === "string" &&
                        value.trim() !== value
                      ) {
                        const trimmedValue = value.trim();
                        setEditModalData((prev) => ({
                          ...prev,
                          currentValue: trimmedValue,
                        }));
                      }
                    }}
                    helperText={
                      editModalData.dataType === "Numeric"
                        ? "Only numbers and decimals are allowed"
                        : editModalData.dataType === "Numeric/Whole Number"
                        ? "Only whole numbers are allowed"
                        : editModalData.dataType === "String"
                        ? "Alphanumeric, spaces, . and - only allowed"
                        : ""
                    }
                  />
                )}
                {(editModalData.confidenceLevel !== null &&
                  editModalData.confidenceLevel > 90) ||
                shouldShowInfoIcon(editModalData.fieldKey) ? null : (
                  <Box
                    onClick={() => {
                      if (
                        editModalData.currentValue ===
                        editModalData.originalValue
                      ) {
                        setEditModalData((prev) => ({
                          ...prev,
                          digitalizationCode: "DG001",
                        }));
                      }
                    }}
                    style={{
                      backgroundColor:
                        editModalData.currentValue ===
                        editModalData.originalValue
                          ? "green"
                          : "#cccccc",
                      color: "white",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      minWidth: 24,
                      textAlign: "center",
                      cursor:
                        editModalData.currentValue ===
                        editModalData.originalValue
                          ? "pointer"
                          : "not-allowed",
                      opacity:
                        editModalData.currentValue ===
                        editModalData.originalValue
                          ? 1
                          : 0.5,
                      marginLeft: 8,
                      alignSelf: "flex-start",
                      marginTop: 9,
                    }}
                  >
                    ✓
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                gutterBottom
                style={{ fontWeight: "bold" }}
              >
                Confidence Level (%)
              </Typography>
              <Typography variant="body2">
                {editModalData.confidenceLevel !== null
                  ? Math.round(editModalData.confidenceLevel)
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                gutterBottom
                style={{ fontWeight: "bold" }}
              >
                Digitalization Code / Description
              </Typography>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={editModalData.digitalizationCode}
                  onChange={(e) => {
                    setEditModalData((prev) => ({
                      ...prev,
                      digitalizationCode: e.target.value,
                    }));
                    // Clear error if digitalization code is selected OR if comment has content
                    if (e.target.value || editModalData.comment.trim()) {
                      setEditModalValidationError("");
                    }
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a digitalization code</em>
                  </MenuItem>
                  {apiDigitalizationCodes.map((code) => (
                    <MenuItem key={code.code} value={code.code}>
                      {code.code} / {code.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                gutterBottom
                style={{ fontWeight: "bold" }}
              >
                Comment
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Enter text here"
                value={editModalData.comment}
                onChange={(e) => {
                  if(e.target.value.length < 256){
                    setEditModalData((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }));
                    // Only clear error if user is typing (has content) or if digitalization code exists
                    if (e.target.value.trim() || editModalData.digitalizationCode) {
                      setEditModalValidationError("");
                    }
                  }
                }}
                onBlur={(e) => {
                  // Trim leading and trailing spaces on blur for better optimization
                  const trimmedValue = typeof e.target.value === 'string' ? e.target.value.trim() : e.target.value;
                  setEditModalData((prev) => ({
                    ...prev,
                    comment: trimmedValue,
                  }));
                  
                  // Validate on blur: if no digitalization code and no comment, show error
                  if (!editModalData.digitalizationCode && !trimmedValue) {
                    setEditModalValidationError(
                      "If digitalization code is not entered, comment field is mandatory"
                    );
                  }
                }}
                multiline
                rows={3}
                error={!!editModalValidationError}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 4, display: 'block' }}
              >
                Note - If digitalization code is not entered, comment field is mandatory
              </Typography>
            </Grid>
            {/* Error message display - more prominent */}
            {editModalValidationError && (
              <Grid item xs={12}>
                <Box
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#ffebee",
                    border: "1px solid #f44336",
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <WarningIcon style={{ color: '#f44336', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    color="error"
                    style={{ fontWeight: 500, whiteSpace: 'pre-line' }}
                  >
                    {editModalValidationError}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  style={{ borderRadius: 30 }}
                  color="primary"
                  onClick={handleSaveFieldEdit}
                  disabled={saving}
                  endIcon={
                    saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      <ArrowForwardIos style={{ fontSize: 15 }} />
                    )
                  }
                >
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* Exit Warning Dialog */}
      <Dialog
        open={showExitWarning}
        onClose={() => setShowExitWarning(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle disableTypography style={{ padding: '16px 24px 0' }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={10}>
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant="body2"
                onClick={() => setShowExitWarning(false)}
                className={classes.closeText}
              >
                Cancel <CloseIcon fontSize="large" color="error" className={classes.icon} />
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent style={{ padding: '0px 40px 30px 40px', textAlign: 'center' }}>
          <Grid
            item
            xs={12}
            style={{ display: "flex", justifyContent: "center", marginBottom: '16px' }}
          >
            <WarningIcon
              style={{
                fontSize: "5rem",
                color: "#F00",
              }}
            />
          </Grid>
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
            Are you sure you want to exit?
          </Typography>
          <Typography variant="body1">
            You have not marked the review as complete.<br/> Process flow change is not initiated.
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: '34px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowExitWarning(false);
              onClose();
            }}
            style={{ minWidth: '150px', borderRadius: 30, textTransform: 'capitalize' }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update & Re-run Reminder Dialog */}
      <Dialog
        open={showUpdateRerunReminderDialog}
        // onClose={() => {
        //   setShowUpdateRerunReminderDialog(false);
        //   onClose();
        // }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle disableTypography style={{ padding: '16px 24px 0' }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={10}>
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant="body2"
                onClick={() => {
                  setShowUpdateRerunReminderDialog(false);
                  onClose();
                }}
                className={classes.closeText}
              >
                Cancel <CloseIcon fontSize="large" color="error" className={classes.icon} />
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent style={{ padding: '0px 40px 30px 40px', textAlign: 'center' }}>
          <Grid
            item
            xs={12}
            style={{ display: "flex", justifyContent: "center", marginBottom: '16px' }}
          >
            <CheckCircleIcon style={{ fontSize: 80, color: '#2e7d32' }} />
          </Grid>
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
            Changes saved successfully
          </Typography>
          <Typography variant="body1">
            Please click on the following button to re-run the batch with the new updates
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: '34px' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={updateRerunLoading}
            onClick={async () => {
              setShowUpdateRerunReminderDialog(false);
              setUpdateRerunLoading(true);
              try {
                await handleReviewAction(true);
              } finally {
                setUpdateRerunLoading(false);
              }
            }}
            style={{ minWidth: '150px', borderRadius: 30, textTransform: 'capitalize' }}
          >
            Update and Re-Run
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent style={{ padding: '30px 40px 20px 40px', textAlign: 'center' }}>
          <CheckCircleIcon style={{ fontSize: 60, color: '#2e7d32', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
            {successDialogTitle || "Updated Successfully"}
            {/* Updated Successfully */}
          </Typography>
          <Typography variant="body1">
            {successMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: '30px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowSuccessDialog(false);
              // BCM-654: After a mark-review-complete (or workflow reset) the document is
              // already validated server-side and the page is about to close. Do NOT
              // re-validate here — re-validating re-sends the stale local apiData, whose
              // markAsReviewed is still false (apiData state is not updated on complete),
              // and Corda persists that false, flipping the just-completed status to "No".
              if (successMessage === "Process flow change is initiated." ||
                  successMessage === "Process flow changes have been reinitiated.") {
                onClose();
                return;
              }
              // BCM-564 Trigger 3: Re-validate after a field save — batchDetails is refreshed by
              // fetchDSBatchDetails before OK is clicked, so digitizedExtractedData is current.
              if (apiData?.bowls && apiData.bowls.length > 0) {
                runValidateFieldsForDigitizationUI();
              }
            }}
            style={{ minWidth: '120px', borderRadius: 30, textTransform: 'capitalize' }}
            // onClick={() => setShowSuccessDialog(false)}
            // style={{ minWidth: '120px' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={() => {
          setShowDeleteConfirmDialog(false);
          setDeleteError("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 14,
          },
        }}
      >
        <DialogTitle style={{ padding: '12px 24px 0 24px', position: 'relative' }}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setShowDeleteConfirmDialog(false);
              setDeleteError("");
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontSize: '1rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px',
              }}
            >
              Cancel
            </Typography>
            <CloseIcon
              style={{
                fontSize: '35px',
                color: '#FF0000',
                cursor: 'pointer',
              }}
              onClick={() => {
                setShowDeleteConfirmDialog(false);
                setDeleteError("");
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '0px 24px 24px 24px', textAlign: 'center' }}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '32px',
            }}
          >
            <WarningIcon 
              style={{ 
                fontSize: '5rem',
                color: 'red',
              }} 
            />
          </Box>
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600, whiteSpace: 'pre-line' }}>
            {deleteConfirmData.type === "batch" 
              ? "Are you sure you want to delete this batch?"
              : "Are you sure you want to delete the \nspecific consumption details?"}
          </Typography>
          {deleteConfirmData.type === "batch" ? (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                <strong>Consumed Material Number BMS:</strong> {deleteConfirmData.materialNumber || "N/A"}
              </Typography>
              <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                <strong>Batch Number:</strong> {deleteConfirmData.batchNumberValue || "N/A"}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" style={{ color: '#666', marginBottom: '8px' }}>
              {deleteConfirmData.consumptionDetailsLabel}
            </Typography>
          )}
          {deleteError && (
            <Box
              style={{
                marginTop: 16,
                padding: "12px 16px",
                backgroundColor: "#ffebee",
                border: "1px solid #f44336",
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <WarningIcon style={{ color: '#f44336', fontSize: 20 }} />
              <Typography
                variant="body2"
                color="error"
                style={{ fontWeight: 500 }}
              >
                {deleteError}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: '24px', paddingTop: '0' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDelete}
            disabled={saving}
            style={{ 
              minWidth: '150px',
              borderRadius: 30,
              paddingLeft: '20px',
              paddingRight: '20px',
              marginBottom: '10px',
              textTransform: 'none',
            }}
            endIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Deleting..." : "Confirm Deletion"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CommonDigitizedDocument;